import { useState, useEffect, useRef } from 'react';
import './App.css';
import { supabase } from './supabaseClient';
import { hydrateWithCommunityRatings } from './utils/hydrateWithCommunityRatings';
import * as Sentry from '@sentry/react';
import posthog from 'posthog-js';

// --- Icons ---
const SearchIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const BackIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
  </svg>
);

const HeartIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
  </svg>
);

const BookmarkIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
  </svg>
);

const ActionStarIcon = ({ filled }) => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
  </svg>
);

const UsersIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
  </svg>
);

const PlusIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
  </svg>
);

const CameraIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

function LikeButton({ count, liked, onToggle, size = 'sm' }) {
  const iconClass = size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4';
  const textClass = size === 'sm' ? 'text-[11px]' : 'text-xs';
  return (
    <button
      onClick={e => { e.stopPropagation(); onToggle(); }}
      className={`flex items-center gap-1 transition-colors ${liked ? 'text-rose-500' : 'text-gray-400 hover:text-rose-400'}`}
    >
      <svg xmlns="http://www.w3.org/2000/svg" className={iconClass} viewBox="0 0 24 24" fill={liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
      </svg>
      {count > 0 && <span className={textClass}>{count}</span>}
    </button>
  );
}

// --- Utilities ---

const BADGES = [
  { id: 'first_book',       label: 'First Book',      desc: 'Rate your first book'  },
  { id: 'bookworm',         label: 'Bookworm',         desc: 'Rate 10 books'         },
  { id: 'book_club_member', label: 'Community Member', desc: 'Join a community'      },
  { id: 'social_reader',    label: 'Social Reader',    desc: 'Post 10 discussions'   },
  { id: 'seven_day_streak', label: '7-Day Streak',     desc: 'Read 7 days in a row'  },
  { id: 'founding_reader',  label: 'Founding Reader',  desc: 'Creator partner'       },
];

async function unlockBadge(badgeId, userId) {
  if (!userId) return false;
  const { data, error } = await supabase
    .from('reading_achievements')
    .insert({ user_id: userId, badge_id: badgeId })
    .select()
    .single();
  return !error && !!data;
}

function hasSevenDayStreak(dateStrings) {
  if (dateStrings.length < 7) return false;
  const dates = [...dateStrings].sort().reverse();
  let streak = 1;
  for (let i = 1; i < dates.length; i++) {
    const d1 = new Date(dates[i - 1] + 'T00:00:00Z');
    const d2 = new Date(dates[i] + 'T00:00:00Z');
    const diff = Math.round((d1 - d2) / 86400000);
    if (diff === 1) { streak++; if (streak >= 7) return true; }
    else if (diff > 1) { streak = 1; }
  }
  return streak >= 7;
}

const BG_COLORS = [
  'bg-blue-500', 'bg-violet-500', 'bg-rose-500', 'bg-amber-500',
  'bg-emerald-500', 'bg-indigo-500', 'bg-pink-500', 'bg-teal-500',
  'bg-cyan-500', 'bg-orange-400', 'bg-purple-500', 'bg-lime-600',
];

function getBgColor(id) {
  const seed = [...String(id)].reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return BG_COLORS[seed % BG_COLORS.length];
}

// --- Content moderation ---
// Words that are hard-banned — message is blocked entirely
const BANNED_WORDS = [
  'nigger','nigga','faggot','fag','chink','spic','kike','tranny','retard','cunt',
  'rape','rapist','pedophile','pedo','molest',
];
// Words that are auto-bleeped (first + last letter kept, middle replaced with *)
const BLEEP_WORDS = [
  'fuck','fucking','fucker','fucked','shit','shitting','bitch','bitches',
  'ass','asshole','bastard','dick','cock','pussy','whore','slut','damn','piss','crap',
];

function bleep(word) {
  if (word.length <= 2) return '*'.repeat(word.length);
  return word[0] + '*'.repeat(word.length - 2) + word[word.length - 1];
}

// Returns { blocked: true, reason } or { blocked: false, text: cleaned }
function moderateText(text) {
  const lower = text.toLowerCase();
  for (const w of BANNED_WORDS) {
    const re = new RegExp(`\\b${w}s?\\b`, 'i');
    if (re.test(lower)) return { blocked: true, reason: `That language isn't allowed here.` };
  }
  let cleaned = text;
  for (const w of BLEEP_WORDS) {
    const re = new RegExp(`\\b(${w}s?)\\b`, 'gi');
    cleaned = cleaned.replace(re, match => bleep(match));
  }
  return { blocked: false, text: cleaned };
}

function stripHtml(str) {
  return (str || '').replace(/<[^>]*>/g, '').replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>').replace(/&quot;/g, '"').replace(/&#39;/g, "'").trim();
}

const BOOKS_PROXY = `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/google-books`;

const _booksCache = new Map();
async function fetchBooks(query, maxResults = 8, { minYear, maxYear, minAvgRating = 0, sortByScore = true } = {}) {
  const primaryUrl = `${BOOKS_PROXY}?q=${encodeURIComponent(query)}&maxResults=40&orderBy=relevance&printType=books`;
  const intitleTerms = query.trim().split(/\s+/).filter(w => w.length >= 3).join(' ') || query.trim();
  const titleUrl   = `${BOOKS_PROXY}?q=${encodeURIComponent('intitle:' + intitleTerms)}&maxResults=20&orderBy=relevance&printType=books`;

  const fetchUrl = async (url) => {
    if (_booksCache.has(url)) return _booksCache.get(url);
    let lastErr;
    for (let attempt = 0; attempt < 3; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, attempt * 1000));
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        _booksCache.set(url, data);
        return data;
      } catch (e) {
        lastErr = e;
      }
    }
    throw lastErr;
  };

  const [primary, titleBoost] = await Promise.all([
    fetchUrl(primaryUrl),
    fetchUrl(titleUrl).catch(() => ({ items: [] })),
  ]);
  const merged = { items: [...(primary.items || []), ...(titleBoost.items || [])] };
  return processBooks(merged, maxResults, { minYear, maxYear, minAvgRating, sortByScore });
}

function processBooks(data, maxResults, { minYear, maxYear, minAvgRating = 0, sortByScore = true } = {}) {
  // Weighted score: averageRating × ln(ratingsCount + 1)
  const score = v => (v.averageRating || 0) * Math.log((v.ratingsCount || 0) + 1);
  const items = sortByScore
    ? (data.items || []).slice().sort((a, b) => score(b.volumeInfo) - score(a.volumeInfo))
    : (data.items || []);

  const seen = new Map();
  const results = [];
  for (const item of items) {
    const info   = item.volumeInfo;
    const title  = info.title || 'Unknown Title';
    const author = (info.authors || ['Unknown']).join(', ');
    const year   = (info.publishedDate || '').slice(0, 4);
    const normTitle  = title.toLowerCase().replace(/[^a-z0-9\s]/g, '').split(/\s*[:(\u2013\u2014-]\s*/)[0].replace(/\s+/g, ' ').trim();
    // Sort name tokens so "James Clear" and "Clear, James" produce the same key
    const normAuthor = (info.authors || ['Unknown']).map(a => a.toLowerCase().replace(/[^a-z\s]/g, '').split(/\s+/).filter(Boolean).sort().join('')).sort().join('|');
    const key = `${normTitle}||${normAuthor}`;
    if (seen.has(key)) continue;
    if (minYear && parseInt(year) < minYear) continue;
    if (maxYear && parseInt(year) > maxYear) continue;
    if (minAvgRating && info.averageRating && info.averageRating < minAvgRating) continue;
    seen.set(key, true);
    results.push({
      id:          item.id,
      title,
      author,
      year,
      cover:       (info.imageLinks?.extraLarge || info.imageLinks?.large || info.imageLinks?.medium || info.imageLinks?.thumbnail)?.replace('http://', 'https://')?.replace('zoom=1', 'zoom=3') || null,
      description: stripHtml(info.description),
      bg:          getBgColor(item.id),
      rating:      info.averageRating ? Math.round(info.averageRating * 20) / 10 : null,
      ratingSource: 'google',
    });
    if (results.length === maxResults) break;
  }
  return results;
}

async function fetchBookById(volumeId) {
  const url = `${BOOKS_PROXY}?id=${encodeURIComponent(volumeId)}`;
  const res = await fetch(url);
  if (!res.ok) throw new Error('Not found');
  const item = await res.json();
  const info = item.volumeInfo;
  return {
    id:          item.id,
    title:       info.title || 'Unknown Title',
    author:      (info.authors || ['Unknown']).join(', '),
    year:        (info.publishedDate || '').slice(0, 4),
    cover:       (info.imageLinks?.extraLarge || info.imageLinks?.large || info.imageLinks?.medium || info.imageLinks?.thumbnail)?.replace('http://', 'https://')?.replace('zoom=1', 'zoom=3') || null,
    description: stripHtml(info.description),
    bg:          getBgColor(item.id),
    rating:      info.averageRating ? Math.round(info.averageRating * 20) / 10 : null,
    ratingSource: 'google',
  };
}

async function fetchPopularFromApp() {
  try {
    const { data } = await supabase
      .from('reviews')
      .select('book_id, book_title, book_author, book_cover, rating')
      .not('rating', 'is', null)
      .gte('rating', 1);
    if (!data || data.length === 0) return [];
    const stats = {};
    data.forEach(r => {
      if (!stats[r.book_id]) stats[r.book_id] = { book_id: r.book_id, book_title: r.book_title, book_author: r.book_author, book_cover: r.book_cover, ratings: [] };
      stats[r.book_id].ratings.push(r.rating);
    });
    // Deduplicate books that share the same title+author but have different Google Books IDs
    // (different editions return different IDs; merge their ratings so one entry appears)
    const titleKey = b => `${(b.book_title || '').toLowerCase().trim()}|${(b.book_author || '').toLowerCase().trim()}`;
    const merged = {};
    Object.values(stats).forEach(b => {
      const key = titleKey(b);
      if (!merged[key]) {
        merged[key] = { ...b };
      } else {
        // Keep the entry with a cover if possible, merge all ratings
        if (!merged[key].book_cover && b.book_cover) merged[key].book_cover = b.book_cover;
        merged[key].ratings = merged[key].ratings.concat(b.ratings);
      }
    });
    // Weighted score: avg × ln(count + 1) — rewards both quality and volume
    // Min avg 6/10, at least 1 review to appear
    return Object.values(merged)
      .filter(b => b.ratings.length >= 1)
      .map(b => {
        const avg = b.ratings.reduce((s, r) => s + r, 0) / b.ratings.length;
        const score = avg * Math.log(b.ratings.length + 1);
        return { ...b, avg, score, count: b.ratings.length };
      })
      .filter(b => b.avg >= 6.0)
      .sort((a, b) => b.score - a.score)
      .slice(0, 20)
      .map(b => ({
        id: b.book_id,
        title: b.book_title,
        author: b.book_author || '',
        cover: b.book_cover || null,
        bg: getBgColor(b.book_id),
        year: '', description: '',
        rating: Math.round(b.avg * 10) / 10,
        ratingSource: 'community',
        ratingCount: b.count,
      }));
  } catch {
    return [];
  }
}

// Convert a featured_books Supabase row to the book shape used by the app
function mapFeaturedBook(b) {
  return {
    id:          b.id,
    title:       b.title,
    author:      b.author      || '',
    cover:       b.cover       || null,
    description: b.description || '',
    year:        b.year        || '',
    rating:      b.display_rating ?? null,
    bg:          getBgColor(b.id),
  };
}

// Infer a genre search query from a book's description text
// eslint-disable-next-line no-unused-vars
function inferGenreQuery(description) {
  const d = (description || '').toLowerCase();
  if (/mystery|thriller|detective|crime/.test(d))                    return GENRE_QUERIES.g3;
  if (/science fiction|sci-fi|space opera|alien/.test(d))            return GENRE_QUERIES.g2;
  if (/fantasy|magic|dragon|wizard|witch/.test(d))                   return GENRE_QUERIES.g2;
  if (/self-help|habits|mindset|productivity|business/.test(d))      return GENRE_QUERIES.g4;
  if (/biography|memoir|autobiography/.test(d))                      return GENRE_QUERIES.g5;
  if (/history|historical|nonfiction|non-fiction/.test(d))           return GENRE_QUERIES.g6;
  return GENRE_QUERIES.g1;
}

// --- Fallback dummy data (used instantly; silently replaced by API data when available) ---
// All books use the same shape as fetchBooks returns.

const fb = (id, title, author, year, bg) => ({ id, title, author, year, bg, cover: null, description: '' });

const FALLBACK_MOST_LIKED = [
  fb('fml1', 'The Women',            'Kristin Hannah',   '2024', 'bg-red-400'),
  fb('fml2', 'All Fours',            'Miranda July',     '2024', 'bg-fuchsia-500'),
  fb('fml3', 'James',                'Percival Everett', '2024', 'bg-lime-600'),
  fb('fml4', 'Intermezzo',           'Sally Rooney',     '2024', 'bg-purple-500'),
  fb('fml5', 'The God of the Woods', 'Lauren Fox',       '2024', 'bg-green-700'),
  fb('fml6', 'Orbital',              'Samantha Harvey',  '2023', 'bg-sky-500'),
];

const FALLBACK_GENRE_BOOKS = {
  g1: { mostLiked: [fb('g1a','Normal People','Sally Rooney','2018','bg-blue-500'), fb('g1b','A Little Life','Hanya Yanagihara','2015','bg-blue-500'), fb('g1c','Pachinko','Min Jin Lee','2017','bg-blue-500'), fb('g1d','The Goldfinch','Donna Tartt','2013','bg-blue-500'), fb('g1e','The Book Thief','Markus Zusak','2005','bg-blue-500')] },
  g2: { mostLiked: [fb('g2a','Dune','Frank Herbert','1965','bg-cyan-500'), fb('g2b','Project Hail Mary','Andy Weir','2021','bg-cyan-500'), fb('g2c','The Name of the Wind','Patrick Rothfuss','2007','bg-cyan-500'), fb('g2d','The Way of Kings','Brandon Sanderson','2010','bg-cyan-500')] },
  g3: { mostLiked: [fb('g3a','Gone Girl','Gillian Flynn','2012','bg-stone-600'), fb('g3b','Big Little Lies','Liane Moriarty','2014','bg-stone-600'), fb('g3c','In the Woods','Tana French','2007','bg-stone-600'), fb('g3d','The Secret History','Donna Tartt','1992','bg-stone-600')] },
  g4: { mostLiked: [fb('g4a','Atomic Habits','James Clear','2018','bg-emerald-500'), fb('g4b','Deep Work','Cal Newport','2016','bg-emerald-500'), fb('g4c','The Psychology of Money','Morgan Housel','2020','bg-emerald-500'), fb('g4d','Thinking, Fast and Slow','Daniel Kahneman','2011','bg-emerald-500')] },
  g5: { mostLiked: [fb('g5a','Steve Jobs','Walter Isaacson','2011','bg-amber-500'), fb('g5b','Educated','Tara Westover','2018','bg-amber-500'), fb('g5c','Open','Andre Agassi','2009','bg-amber-500'), fb('g5d','Long Walk to Freedom','Nelson Mandela','1994','bg-amber-500')] },
  g6: { mostLiked: [fb('g6a','Sapiens','Yuval Noah Harari','2011','bg-slate-500'), fb('g6b','Killers of the Flower Moon','David Grann','2017','bg-slate-500'), fb('g6c','The Devil in the White City','Erik Larson','2003','bg-slate-500'), fb('g6d','SPQR','Mary Beard','2015','bg-slate-500')] },
};

// --- Static data (not replaced by API) ---

const GENRES = [
  { id: 'g1', name: '📚 Fiction',               bg: 'bg-purple-600'  },
  { id: 'g2', name: '🔬 Sci-Fi & Fantasy',      bg: 'bg-teal-700'    },
  { id: 'g3', name: '🔍 Mystery & Thriller',    bg: 'bg-red-900'     },
  { id: 'g4', name: '💪 Self-Help & Business',  bg: 'bg-amber-500'   },
  { id: 'g5', name: '📖 Biography & Memoir',    bg: 'bg-orange-800'  },
  { id: 'g6', name: '🎓 History & Non-Fiction', bg: 'bg-slate-700'   },
];

// Queries tuned to surface well-rated English-language books per genre
const GENRE_QUERIES = {
  g1: 'subject:fiction bestseller literary novel award',
  g2: 'subject:"science fiction" OR subject:fantasy bestseller award',
  g3: 'subject:mystery OR subject:thriller bestseller suspense',
  g4: 'subject:"self-help" OR subject:"business" productivity habits success',
  g5: 'subject:biography OR subject:memoir bestseller autobiography',
  g6: 'subject:history OR subject:"nonfiction" narrative bestseller award',
};



// --- FakeCover ---

function FakeCover({ book, className = '' }) {
  return (
    <div className={`${book.bg || 'bg-gray-700'} w-full h-full flex flex-col justify-between ${className}`}>
      {/* Decorative top band */}
      <div className="bg-black/20 px-3 py-2 flex flex-col gap-1">
        <div className="h-0.5 bg-white/40 rounded-full" />
        <div className="h-0.5 bg-white/25 rounded-full w-2/3" />
      </div>
      {/* Title block */}
      <div className="flex-1 flex items-center justify-center px-3">
        <p className="text-white text-xs font-bold text-center leading-snug line-clamp-5 drop-shadow-md">
          {book.title}
        </p>
      </div>
      {/* Author footer */}
      <div className="bg-black/20 px-3 py-2">
        <p className="text-white/80 text-[9px] font-medium text-center truncate">{book.author}</p>
      </div>
    </div>
  );
}

// --- BookCard ---

function BookCard({ book, onSelect }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="flex-shrink-0 w-32 cursor-pointer group active:scale-95 transition-transform" onClick={() => onSelect?.(book)}>
      <div className="relative w-32 h-48 rounded-xl overflow-hidden mb-2 shadow-[0_8px_24px_rgba(0,0,0,0.6)] group-hover:shadow-[0_12px_32px_rgba(0,0,0,0.8)] transition-shadow">
        {book.cover && !imgError
          ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" onError={() => setImgError(true)} />
          : <FakeCover book={book} />
        }
        {book.rating && (
          <div className="absolute bottom-2 right-2 flex flex-col items-end gap-0.5">
            <div className="flex items-center gap-0.5 bg-black/60 backdrop-blur-sm rounded-md px-1.5 py-0.5">
              <span className="text-yellow-400 text-[10px] leading-none">★</span>
              <span className="text-white text-[10px] font-semibold leading-none">{book.rating}</span>
            </div>
            <span className="text-white/70 text-[8px] font-medium leading-none bg-black/50 rounded px-1 py-0.5">
              {book.ratingSource === 'community' && book.ratingCount >= 3
                ? `${book.ratingCount} ratings`
                : book.ratingSource === 'community' && book.ratingCount > 0
                ? `${book.ratingCount} review${book.ratingCount > 1 ? 's' : ''}`
                : 'Google'}
            </span>
          </div>
        )}
      </div>
      <p className="text-xs font-semibold text-gray-100 line-clamp-1">{book.title}</p>
      <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{book.author}</p>
    </div>
  );
}

function BookCardSkeleton() {
  return (
    <div className="flex-shrink-0 w-32 animate-pulse">
      <div className="w-32 h-48 rounded-xl bg-gray-800 mb-2 shadow-[0_8px_24px_rgba(0,0,0,0.5)]" />
      <div className="h-3 bg-gray-800 rounded-full w-24 mb-1.5" />
      <div className="h-3 bg-gray-700 rounded-full w-16" />
    </div>
  );
}

function HeroBookCard({ book, onSelect }) {
  const [imgError, setImgError] = useState(false);
  return (
    <div className="mx-5 mb-8 cursor-pointer active:scale-[0.98] transition-transform" onClick={() => onSelect?.(book)}>
      <div className="relative rounded-2xl overflow-hidden h-56 shadow-[0_16px_48px_rgba(0,0,0,0.8)]">
        {book.cover && !imgError
          ? <>
              <img src={book.cover} alt="" className="absolute inset-0 w-full h-full object-cover scale-110 blur-lg opacity-60" aria-hidden="true" onError={() => setImgError(true)} />
              <img src={book.cover} alt={book.title} className="relative w-full h-full object-contain" onError={() => setImgError(true)} />
            </>
          : <FakeCover book={book} className="absolute inset-0" />
        }
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute top-3 left-3">
          <span className="bg-white/20 backdrop-blur-sm text-white text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md">Editor's Pick</span>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <p className="text-white text-lg font-bold leading-tight line-clamp-2 drop-shadow">{book.title}</p>
          <p className="text-white/70 text-sm mt-0.5">{book.author}</p>
          {book.rating && (
            <div className="flex items-center gap-1 mt-2">
              <span className="text-yellow-400 text-sm">★</span>
              <span className="text-white text-sm font-semibold">{book.rating}<span className="text-white/60 font-normal">/10</span></span>
              <span className="text-white/50 text-xs">
                {book.ratingSource === 'community' && book.ratingCount > 0
                  ? `· ${book.ratingCount} ${book.ratingCount === 1 ? 'rating' : 'ratings'}`
                  : '· Google Books'}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


function CommunityCardSkeleton() {
  return (
    <div className="bg-gray-900 rounded-2xl p-4 animate-pulse">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="flex-1">
          <div className="h-4 bg-gray-700 rounded-full w-1/2 mb-2" />
          <div className="h-3 bg-gray-800 rounded-full w-3/4" />
        </div>
        <div className="w-10 h-10 rounded-xl bg-gray-700 flex-shrink-0" />
      </div>
      <div className="flex items-center justify-between">
        <div className="h-3 bg-gray-800 rounded-full w-20" />
        <div className="h-7 w-16 bg-gray-700 rounded-lg" />
      </div>
    </div>
  );
}

function HorizontalSection({ title, books, onBookSelect, loading }) {
  return (
    <section className="mb-8">
      <h2 className="text-base font-bold text-white mb-3 px-5">{title}</h2>
      <div className="flex gap-4 overflow-x-auto px-5 pb-2 no-scrollbar">
        {loading
          ? Array.from({ length: 6 }).map((_, i) => <BookCardSkeleton key={i} />)
          : books.map(book => <BookCard key={book.id} book={book} onSelect={onBookSelect} />)}
      </div>
    </section>
  );
}

// --- Star Rating ---

function StarRating({ rating, onChange }) {
  const [hovered, setHovered] = useState(null);
  const display = hovered !== null ? hovered : rating;

  return (
    <div className="flex gap-1" onMouseLeave={() => setHovered(null)}>
      {Array.from({ length: 10 }, (_, i) => i + 1).map(star => {
        const isFull = display >= star;
        const isHalf = !isFull && display >= star - 0.5;
        return (
          <div key={star} className="relative w-6 h-6">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#4b5563" strokeWidth={1.5} className="absolute inset-0 w-6 h-6">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            {(isFull || isHalf) && (
              <div className="absolute inset-0 overflow-hidden" style={{ width: isFull ? '100%' : '50%' }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth={0.5} className="w-6 h-6">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
            )}
            <div className="absolute left-0 top-0 w-1/2 h-full cursor-pointer z-10" onMouseEnter={() => setHovered(star - 0.5)} onClick={() => onChange(rating === star - 0.5 ? 0 : star - 0.5)} />
            <div className="absolute right-0 top-0 w-1/2 h-full cursor-pointer z-10" onMouseEnter={() => setHovered(star)} onClick={() => onChange(rating === star ? 0 : star)} />
          </div>
        );
      })}
    </div>
  );
}

function CommunityStarsDisplay({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 10 }, (_, i) => i + 1).map(star => {
        const fillPct = Math.min(100, Math.max(0, (rating - (star - 1)) * 100));
        return (
          <div key={star} className="relative w-5 h-5">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#374151" strokeWidth={1.5} className="absolute inset-0 w-5 h-5">
              <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
            </svg>
            {fillPct > 0 && (
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${fillPct}%` }}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth={0.5} className="w-5 h-5">
                  <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" />
                </svg>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ExpandableDescription({ text }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-2">
      <p className={`text-xs text-gray-400 leading-relaxed ${expanded ? '' : 'line-clamp-4'}`}>{text}</p>
      {!expanded && (
        <button
          onClick={() => setExpanded(true)}
          className="text-xs text-gray-400 hover:text-gray-300 mt-0.5"
        >
          more
        </button>
      )}
    </div>
  );
}

// --- Book Detail Overlay ---

function BookDetailOverlay({ book, data, onUpdate, onClose, userId, userProfile, hasReadingGoal, onUserSelect }) {
  const { rating = 0, loved = false, booklisted = false, review = '', hasSpoiler = false, finishedThisYear } = data;
  const [realStats, setRealStats] = useState(null); // { rating, count } from real reviews
  const [showReviews, setShowReviews] = useState(false);
  const [revealedSpoilers, setRevealedSpoilers] = useState(new Set());
  const [showFinishedModal, setShowFinishedModal] = useState(false);
  const [pendingRating, setPendingRating] = useState(null);
  const [draftReview, setDraftReview] = useState(review);
  const [savingReview, setSavingReview] = useState(false);
  const [reviewToast, setReviewToast] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [realReviews, setRealReviews] = useState([]);
  const [reviewSort, setReviewSort] = useState('newest');
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [fullBook, setFullBook] = useState(book);

  // If description is missing (e.g. book came from community data), fetch it from API
  useEffect(() => {
    if (book.description) { setFullBook(book); return; }
    fetchBookById(book.id)
      .then(fetched => setFullBook({ ...book, ...fetched, cover: book.cover || fetched.cover }))
      .catch(() => setFullBook(book));
  }, [book.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use fullBook everywhere description/year/cover might be missing
  const bookDisplay = fullBook;

  // Fetch real community stats on mount — match by title+author (same as home page) so
  // counts are consistent regardless of which book_id edition was used to save each review.
  useEffect(() => {
    supabase
      .from('reviews')
      .select('rating, review_text')
      .eq('book_title', book.title)
      .eq('book_author', book.author || '')
      .then(({ data: rows }) => {
        if (!rows) return;
        const rated    = rows.filter(r => r.rating != null);
        const reviewed = rows.filter(r => r.review_text != null && r.review_text.trim() !== '');
        const avg = rated.length > 0 ? rated.reduce((sum, r) => sum + r.rating, 0) / rated.length : null;
        setRealStats({
          rating:      avg != null ? Math.round(avg * 10) / 10 : null,
          ratingCount: rated.length,
          reviewCount: reviewed.length,
        });
      });
  }, [book.id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!showReviews) return;
    setLoadingReviews(true);
    supabase
      .from('reviews')
      .select('*, profiles(id, username, display_name, avatar_bg, avatar_url)')
      .eq('book_title', book.title)
      .eq('book_author', book.author || '')
      .not('review_text', 'is', null)
      .neq('review_text', '')
      .order('created_at', { ascending: false })
      .then(async ({ data: rows }) => {
        if (!rows || rows.length === 0) { setRealReviews([]); setLoadingReviews(false); return; }
        const ids = rows.map(r => r.id);
        const [{ data: likesData }, { data: userLikesData }] = await Promise.all([
          supabase.from('review_likes').select('review_id').in('review_id', ids),
          userId
            ? supabase.from('review_likes').select('review_id').eq('user_id', userId).in('review_id', ids)
            : Promise.resolve({ data: [] }),
        ]);
        const countMap = {};
        (likesData || []).forEach(l => { countMap[l.review_id] = (countMap[l.review_id] || 0) + 1; });
        const userLikedSet = new Set((userLikesData || []).map(l => l.review_id));
        setRealReviews(rows.map(r => ({ ...r, like_count: countMap[r.id] || 0, liked_by_user: userLikedSet.has(r.id) })));
        setLoadingReviews(false);
      });
  }, [showReviews, book.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitReview = async () => {
    if (!draftReview.trim()) return;
    if (!userId) return;
    const modResult = moderateText(draftReview.trim());
    if (modResult.blocked) {
      setReviewToast(modResult.reason);
      setTimeout(() => setReviewToast(null), 3000);
      return;
    }
    setSavingReview(true);
    try {
      const { error } = await supabase.from('reviews').upsert({
        user_id: userId,
        book_id: book.id,
        book_title: book.title,
        book_author: book.author || null,
        book_cover: book.cover || null,
        rating: rating ? Math.round(rating) : null,
        review_text: modResult.text,
        has_spoiler: hasSpoiler,
        finished_this_year: finishedThisYear ?? true,
        loved,
        booklisted,
      }, { onConflict: 'user_id,book_id' });
      if (error) throw error;
      onUpdate({ review: draftReview.trim() });
      setIsEditing(false);
      setReviewToast('Review saved!');
      setTimeout(() => setReviewToast(null), 3000);
    } catch (err) {
      setReviewToast(err?.message || 'Failed to save. Try again.');
      setTimeout(() => setReviewToast(null), 3000);
    } finally {
      setSavingReview(false);
    }
  };

  const handleRating = (r) => {
    if (rating === 0 && r > 0 && userId && hasReadingGoal) {
      setPendingRating(r);
      setShowFinishedModal(true);
    } else {
      onUpdate({ rating: r });
    }
  };

  const deleteReview = async (reviewId) => {
    if (reviewId) {
      await supabase.from('reviews').delete().eq('id', reviewId).eq('user_id', userId);
      setRealReviews(prev => prev.filter(r => r.id !== reviewId));
    } else {
      await supabase.from('reviews').delete().eq('user_id', userId).eq('book_id', book.id);
      setRealReviews(prev => prev.filter(r => r.user_id !== userId));
    }
    setRealStats(prev => prev ? { ...prev, reviewCount: Math.max(0, prev.reviewCount - 1) } : null);
    onUpdate({ review: '' });
  };

  const toggleSpoilerReveal = (id) => {
    setRevealedSpoilers(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleReviewLike = async (reviewId) => {
    if (!userId) { return; }
    const review = realReviews.find(r => r.id === reviewId);
    if (!review) return;
    const wasLiked = review.liked_by_user;
    // Optimistic update
    setRealReviews(prev => prev.map(r =>
      r.id === reviewId
        ? { ...r, liked_by_user: !wasLiked, like_count: r.like_count + (wasLiked ? -1 : 1) }
        : r
    ));
    const { error } = wasLiked
      ? await supabase.from('review_likes').delete().eq('review_id', reviewId).eq('user_id', userId)
      : await supabase.from('review_likes').insert({ review_id: reviewId, user_id: userId });
    if (error) {
      // Revert on failure
      setRealReviews(prev => prev.map(r =>
        r.id === reviewId
          ? { ...r, liked_by_user: wasLiked, like_count: r.like_count + (wasLiked ? 1 : -1) }
          : r
      ));
    }
  };

  return (
    <div className="fixed inset-0 bg-[#0f0f10] z-50 flex flex-col overflow-y-auto">
      <div className="flex items-center px-5 pt-10 pb-4 flex-shrink-0">
        <button onClick={onClose} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Back
        </button>
      </div>

      <div className="px-5 flex gap-4 items-start flex-shrink-0">
        {book.cover
          ? <img src={book.cover} alt={book.title} className="w-28 h-40 rounded-xl object-contain shadow-[0_8px_32px_rgba(0,0,0,0.7)] flex-shrink-0 bg-gray-900" />
          : <div className={`${book.bg} w-28 h-40 rounded-xl flex items-end p-2 shadow-[0_8px_32px_rgba(0,0,0,0.7)] flex-shrink-0`}>
              <p className="text-white text-xs font-bold drop-shadow leading-snug line-clamp-3">{book.title}</p>
            </div>
        }
        <div className="flex-1 min-w-0 pt-1">
          <h1 className="text-xl font-bold text-white leading-tight">{bookDisplay.title}</h1>
          <p className="text-sm text-gray-400 mt-0.5">{bookDisplay.author}{bookDisplay.year ? ` · ${bookDisplay.year}` : ''}</p>
          {bookDisplay.description && <ExpandableDescription text={bookDisplay.description} />}
        </div>
      </div>

      <div className="px-5 mt-5 flex gap-3 flex-shrink-0">
        <button onClick={() => onUpdate({ rating: 0 })} className={`flex-1 flex flex-col items-center gap-1.5 py-3.5 rounded-xl text-xs font-semibold transition-colors ${rating > 0 ? 'bg-amber-400 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-600'}`}>
          <ActionStarIcon filled={rating > 0} />
          Rate
        </button>
        <button onClick={() => onUpdate({ loved: !loved })} className={`flex-1 flex flex-col items-center gap-1.5 py-3.5 rounded-xl text-xs font-semibold transition-colors ${loved ? 'bg-rose-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-600'}`}>
          <HeartIcon filled={loved} />
          Love
        </button>
        <button onClick={() => onUpdate({ booklisted: !booklisted })} className={`flex-1 flex flex-col items-center gap-1.5 py-3.5 rounded-xl text-xs font-semibold transition-colors ${booklisted ? 'bg-indigo-500 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-600'}`}>
          <BookmarkIcon filled={booklisted} />
          Bookmark
        </button>
      </div>

      <div className="px-5 mt-7 flex-shrink-0">
        <p className="text-sm font-semibold text-gray-200 mb-3">Community Rating</p>
        {realStats?.rating != null ? (
          <>
            <CommunityStarsDisplay rating={realStats.rating} />
            <p className="text-sm text-gray-400 mt-2">
              {realStats.rating} / 10
              <span className="text-gray-400 text-xs ml-2">{realStats.ratingCount.toLocaleString()} {realStats.ratingCount === 1 ? 'rating' : 'ratings'}</span>
            </p>
          </>
        ) : realStats !== null ? (
          <p className="text-sm text-gray-400">Be the first to rate this book!</p>
        ) : null}
        {book.rating != null && book.ratingSource === 'google' && (
          <p className="text-xs text-gray-400 mt-2">Google Books: {book.rating}/10</p>
        )}
      </div>

      <div className="mx-5 mt-7 border-t border-gray-800 flex-shrink-0" />

      <div className="px-5 mt-7 flex-shrink-0">
        <p className="text-sm font-semibold text-gray-200 mb-3">Your Rating</p>
        <StarRating rating={rating} onChange={handleRating} />
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-gray-400">{rating > 0 ? `${rating} / 10` : 'Tap a star to rate'}</p>
          {rating > 0 && hasReadingGoal && (
            <button
              onClick={() => setShowFinishedModal(true)}
              className="text-xs text-gray-400 hover:text-gray-300 transition-colors"
            >
              {finishedThisYear === false ? `Not finished in ${new Date().getFullYear()}` : `Finished in ${new Date().getFullYear()}`} · change
            </button>
          )}
        </div>
      </div>

      {showFinishedModal && hasReadingGoal && (
        <FinishedThisYearModal
          onConfirm={(finished) => {
            const updates = { finishedThisYear: finished };
            if (pendingRating !== null) updates.rating = pendingRating;
            onUpdate(updates);
            setShowFinishedModal(false);
            setPendingRating(null);
          }}
        />
      )}

      {reviewToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-[60] pointer-events-none">
          <div className={`text-white text-sm font-semibold text-center py-3 px-5 rounded-2xl shadow-xl ${reviewToast.startsWith('Failed') ? 'bg-red-500' : 'bg-gray-700'}`}>
            {reviewToast}
          </div>
        </div>
      )}

      <div className="px-5 mt-7 flex-shrink-0">
        <p className="text-sm font-semibold text-gray-200 mb-3">Your Review</p>
        {review.trim() ? (
          <div className="bg-gray-900 rounded-xl px-4 py-4 opacity-60">
            {hasSpoiler && (
              <span className="inline-block text-[10px] text-amber-400 font-semibold bg-amber-900/30 px-1.5 py-0.5 rounded-full mb-2">spoiler</span>
            )}
            <p className="text-sm text-gray-300 leading-relaxed line-clamp-4">{review}</p>
            <div className="flex items-center gap-4 mt-3">
              <button
                onClick={() => { setDraftReview(review); setIsEditing(true); }}
                className="text-xs font-semibold text-gray-400 hover:text-white underline transition-colors"
              >
                Edit review
              </button>
              <button
                onClick={() => deleteReview()}
                className="text-xs font-semibold text-red-400 hover:text-red-600 underline transition-colors"
              >
                Delete review
              </button>
            </div>
          </div>
        ) : (
          <>
            <textarea
              value={draftReview}
              onChange={e => setDraftReview(e.target.value)}
              placeholder="What did you think of this book?"
              rows={5}
              maxLength={5000}
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 resize-none transition-colors"
            />
            <div className="flex items-center justify-between mt-3">
              <label className="flex items-center gap-2.5 cursor-pointer select-none">
                <div
                  onClick={() => onUpdate({ hasSpoiler: !hasSpoiler })}
                  className={`w-10 h-5 rounded-full flex-shrink-0 relative transition-colors ${hasSpoiler ? 'bg-amber-500' : 'bg-gray-700'}`}
                >
                  <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${hasSpoiler ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </div>
                <span className="text-sm text-gray-300">Contains spoilers</span>
              </label>
              <button
                onClick={submitReview}
                disabled={savingReview || !draftReview.trim()}
                className="bg-white text-gray-900 text-sm font-semibold px-5 py-2 rounded-xl hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                {savingReview ? 'Saving…' : 'Submit'}
              </button>
            </div>
          </>
        )}
      </div>

      {isEditing && (
        <div className="fixed inset-0 bg-[#0f0f10] z-[60] flex flex-col">
          <div className="flex items-center justify-between px-5 pt-10 pb-4 border-b border-gray-800 flex-shrink-0">
            <button
              onClick={() => { setDraftReview(review); setIsEditing(false); }}
              className="text-sm font-medium text-gray-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <p className="text-base font-bold text-white">Edit Review</p>
            <div className="w-12" />
          </div>
          <div className="flex-1 px-5 pt-6 overflow-y-auto">
            <textarea
              value={draftReview}
              onChange={e => setDraftReview(e.target.value)}
              placeholder="What did you think of this book?"
              rows={8}
              maxLength={5000}
              autoFocus
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 resize-none transition-colors"
            />
            <label className="flex items-center gap-2.5 mt-4 cursor-pointer select-none">
              <div
                onClick={() => onUpdate({ hasSpoiler: !hasSpoiler })}
                className={`w-10 h-5 rounded-full flex-shrink-0 relative transition-colors ${hasSpoiler ? 'bg-amber-500' : 'bg-gray-700'}`}
              >
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${hasSpoiler ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </div>
              <span className="text-sm text-gray-300">Contains spoilers</span>
              {hasSpoiler && <span className="text-xs font-semibold text-amber-400 bg-amber-900/30 px-2 py-0.5 rounded-full">Spoiler</span>}
            </label>
          </div>
          <div className="px-5 pb-10 pt-4 flex gap-3 flex-shrink-0 border-t border-gray-800">
            <button
              onClick={() => { setDraftReview(review); setIsEditing(false); }}
              className="flex-1 py-3.5 rounded-xl bg-gray-800 text-gray-200 text-sm font-semibold hover:bg-gray-600 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={submitReview}
              disabled={savingReview || !draftReview.trim()}
              className="flex-1 py-3.5 rounded-xl bg-white text-gray-900 text-sm font-semibold hover:bg-gray-100 disabled:opacity-40 transition-colors"
            >
              {savingReview ? 'Saving…' : 'Submit'}
            </button>
          </div>
        </div>
      )}

      {realStats && (
        <div className="px-5 mt-7 pb-12 flex-shrink-0">
          <button
            onClick={() => setShowReviews(true)}
            className="w-full flex items-center justify-between px-4 py-3.5 rounded-xl bg-gray-800 hover:bg-gray-600 transition-colors"
          >
            <span className="text-sm font-semibold text-gray-100">Reviews</span>
            <span className="text-xs text-gray-400">
              {realStats.rating != null && <span className="mr-1">★ {realStats.rating}</span>}
              {realStats.reviewCount.toLocaleString()} {realStats.reviewCount === 1 ? 'review' : 'reviews'} &rsaquo;
            </span>
          </button>
        </div>
      )}

      {showReviews && (
        <div className="fixed inset-0 bg-[#0f0f10] z-[60] flex flex-col">
          <div className="flex items-center justify-between px-5 pt-10 pb-4 border-b border-gray-800 flex-shrink-0">
            <button onClick={() => setShowReviews(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
              <BackIcon />
              Back
            </button>
            <p className="text-base font-bold text-white">Reviews</p>
            <div className="w-12" />
          </div>
          <p className="px-5 pt-3 pb-1 text-xs text-gray-400 flex-shrink-0">
            {realStats?.reviewCount.toLocaleString()} {realStats?.reviewCount === 1 ? 'review' : 'reviews'}
            {realStats?.rating != null && <span> · ★ {realStats.rating}/10 average</span>}
          </p>
          <div className="flex gap-2 px-5 pb-2 flex-shrink-0">
            <button
              onClick={() => setReviewSort('newest')}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${reviewSort === 'newest' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-200'}`}
            >
              Newest
            </button>
            <button
              onClick={() => setReviewSort('liked')}
              className={`text-xs font-semibold px-3 py-1.5 rounded-full transition-colors ${reviewSort === 'liked' ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-400 hover:text-gray-200'}`}
            >
              Most Liked
            </button>
          </div>
          <div className="overflow-y-auto flex-1 px-5 pt-3 pb-10">
            {loadingReviews ? (
              <p className="text-sm text-gray-400 text-center py-10">Loading reviews…</p>
            ) : realReviews.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10">No written reviews yet</p>
            ) : (
              <div className="flex flex-col gap-0">
                {[...realReviews]
                  .sort((a, b) => reviewSort === 'liked'
                    ? (b.like_count ?? 0) - (a.like_count ?? 0)
                    : new Date(b.created_at) - new Date(a.created_at)
                  )
                  .map(r => {
                    const revealed = revealedSpoilers.has(r.id);
                    const displayName = r.user_id === userId ? 'You' : (r.profiles?.display_name || r.profiles?.username || 'User');
                    const reviewProfile = r.user_id === userId ? userProfile : r.profiles;
                    return (
                      <div key={r.id} className="py-5 border-b border-gray-800 last:border-0">
                        <div className="flex items-center gap-2 mb-2">
                          {r.user_id !== userId && r.profiles && onUserSelect ? (
                            <button className="flex items-center gap-2" onClick={() => onUserSelect(r.profiles)}>
                              <Avatar profile={reviewProfile} size="sm" />
                              <span className="text-sm font-semibold text-gray-100">{displayName}</span>
                            </button>
                          ) : (
                            <>
                              <Avatar profile={reviewProfile} size="sm" />
                              <span className="text-sm font-semibold text-gray-100">{displayName}</span>
                            </>
                          )}
                          {r.rating && <span className="text-xs text-amber-500 ml-auto">★ {r.rating}/10</span>}
                          {r.user_id === userId && (
                            <button
                              onClick={() => deleteReview(r.id)}
                              className="ml-2 text-gray-300 hover:text-red-400 transition-colors text-xs"
                              title="Delete review"
                            >✕</button>
                          )}
                        </div>
                        {r.has_spoiler && !revealed ? (
                          <button
                            onClick={() => toggleSpoilerReveal(r.id)}
                            className="w-full py-3 rounded-xl bg-amber-900/30 border border-amber-700/50 text-amber-400 text-xs font-semibold flex items-center justify-center gap-1.5"
                          >
                            <span>⚠</span>
                            <span>Contains spoilers, tap to reveal</span>
                          </button>
                        ) : (
                          <>
                            {r.has_spoiler && (
                              <span className="inline-block text-[10px] text-amber-400 font-semibold bg-amber-900/30 px-1.5 py-0.5 rounded-full mb-2">spoiler</span>
                            )}
                            {r.review_text?.trim() && (
                              <p className="text-sm text-gray-300 leading-relaxed">{r.review_text}</p>
                            )}
                          </>
                        )}
                        <div className="flex justify-end mt-2">
                          <LikeButton
                            count={r.like_count || 0}
                            liked={r.liked_by_user || false}
                            onToggle={() => toggleReviewLike(r.id)}
                            size="sm"
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// --- Home Screen ---

function HomeScreen({ onBookSelect, bookData, userId }) {
  const [selectedGenre, setSelectedGenre] = useState(null);

  if (selectedGenre) {
    return <GenreDetailScreen genre={selectedGenre} onBack={() => setSelectedGenre(null)} onBookSelect={onBookSelect} />;
  }

  return <CommunityScreen onSelectGenre={setSelectedGenre} onBookSelect={onBookSelect} bookData={bookData} userId={userId} />;
}

// --- Bookmarks Screen ---

function BookmarksScreen({ bookData, onBack, onBookSelect }) {
  const bookmarked = Object.entries(bookData).filter(([, d]) => d.booklisted && d._meta);

  return (
    <div className="pt-4">
      <div className="flex items-center px-5 pt-6 pb-2">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Profile
        </button>
      </div>
      <h1 className="px-5 text-xl font-bold text-white mt-2 mb-6">Bookmarks</h1>

      {bookmarked.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-16">No bookmarks yet</p>
      ) : (
        <div className="grid grid-cols-3 gap-4 px-5 pb-10">
          {bookmarked.map(([id, d]) => (
            <div key={id} className="cursor-pointer group" onClick={() => onBookSelect?.({ id, ...d._meta })}>
              <div className="w-full aspect-[2/3] rounded-xl overflow-hidden mb-1.5 shadow-sm group-hover:shadow-md transition-shadow">
                {d._meta.cover
                  ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                  : <div className={`${d._meta.bg} w-full h-full flex items-end p-2`}>
                      <p className="text-white text-xs font-semibold line-clamp-2 leading-tight drop-shadow">{d._meta.title}</p>
                    </div>
                }
              </div>
              <p className="text-xs font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
              <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{d._meta.author}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Loved Screen ---

function LovedScreen({ bookData, onBack, onBookSelect }) {
  const loved = Object.entries(bookData).filter(([, d]) => d.loved && d._meta);

  return (
    <div className="pt-4">
      <div className="flex items-center px-5 pt-6 pb-2">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Profile
        </button>
      </div>
      <h1 className="px-5 text-xl font-bold text-white mt-2 mb-6">Loved Books</h1>

      {loved.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-16">No loved books yet</p>
      ) : (
        <div className="grid grid-cols-3 gap-4 px-5 pb-10">
          {loved.map(([id, d]) => (
            <div key={id} className="cursor-pointer group" onClick={() => onBookSelect?.({ id, ...d._meta })}>
              <div className="w-full aspect-[2/3] rounded-xl overflow-hidden mb-1.5 shadow-sm group-hover:shadow-md transition-shadow">
                {d._meta.cover
                  ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                  : <div className={`${d._meta.bg} w-full h-full flex items-end p-2`}>
                      <p className="text-white text-xs font-semibold line-clamp-2 leading-tight drop-shadow">{d._meta.title}</p>
                    </div>
                }
              </div>
              <p className="text-xs font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
              <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{d._meta.author}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Books Read Screen ---

function BooksReadScreen({ bookData, onBack, onBookSelect }) {
  const read = Object.entries(bookData)
    .filter(([, d]) => (d.rating > 0 || d.loved || d.review?.trim()) && d._meta)
    .reverse();

  return (
    <div className="pt-4">
      <div className="flex items-center px-5 pt-6 pb-2">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Profile
        </button>
      </div>
      <h1 className="px-5 text-xl font-bold text-white mt-2 mb-6">Books Read</h1>

      {read.length === 0 ? (
        <p className="text-center text-gray-400 text-sm py-16">No books read yet</p>
      ) : (
        <div className="grid grid-cols-3 gap-4 px-5 pb-10">
          {read.map(([id, d]) => (
            <div key={id} className="cursor-pointer group" onClick={() => onBookSelect?.({ id, ...d._meta })}>
              <div className="w-full aspect-[2/3] rounded-xl overflow-hidden mb-1.5 shadow-sm group-hover:shadow-md transition-shadow">
                {d._meta.cover
                  ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                  : <FakeCover book={{ ...d._meta, bg: d._meta.bg || getBgColor(id) }} className="w-full h-full" />
                }
              </div>
              <p className="text-xs font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
              <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{d._meta.author}</p>
              {d.rating > 0 && <p className="text-xs text-amber-500 font-medium mt-0.5">★ {d.rating}/10</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Friends List Screen ---

function FriendsListScreen({ userId, onBack, onUserSelect }) {
  const [friends,       setFriends]       = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [confirmRemove, setConfirmRemove] = useState(null); // profile to confirm removal

  useEffect(() => {
    if (!userId) return;
    supabase
      .from('friendships')
      .select('requester_id, addressee_id')
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`)
      .eq('status', 'accepted')
      .then(async ({ data }) => {
        const rows = data || [];
        const friendIds = rows.map(f => f.requester_id === userId ? f.addressee_id : f.requester_id);
        if (friendIds.length === 0) { setLoading(false); return; }
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, username, display_name, avatar_bg, avatar_url')
          .in('id', friendIds);
        setFriends(profiles || []);
        setLoading(false);
      });
  }, [userId]);

  const handleRemoveFriend = async (friendId) => {
    await supabase
      .from('friendships')
      .delete()
      .or(`and(requester_id.eq.${userId},addressee_id.eq.${friendId}),and(requester_id.eq.${friendId},addressee_id.eq.${userId})`);
    setFriends(prev => prev.filter(f => f.id !== friendId));
    setConfirmRemove(null);
  };

  return (
    <div className="pt-4">
      {confirmRemove && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setConfirmRemove(null)}>
          <div className="bg-[#161616] w-full max-w-lg rounded-t-3xl p-6 pb-10" onClick={e => e.stopPropagation()}>
            <h2 className="text-base font-bold text-white mb-1">Remove Friend?</h2>
            <p className="text-sm text-gray-400 mb-6">
              Remove <span className="font-semibold text-gray-100">{confirmRemove.display_name || confirmRemove.username}</span> from your friends. You can send them a new request later.
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleRemoveFriend(confirmRemove.id)}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3.5 rounded-xl transition-colors"
              >
                Remove Friend
              </button>
              <button
                onClick={() => setConfirmRemove(null)}
                className="w-full bg-gray-800 hover:bg-gray-600 text-gray-200 font-semibold py-3.5 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center px-5 pt-6 pb-2">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Back
        </button>
      </div>
      <h2 className="text-base font-bold text-white px-5 mb-4">Friends</h2>
      {loading ? (
        <div className="px-5 flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3 animate-pulse">
              <div className="w-9 h-9 rounded-full bg-gray-700 flex-shrink-0" />
              <div className="flex-1">
                <div className="h-3 bg-gray-700 rounded-full w-32 mb-1.5" />
                <div className="h-3 bg-gray-800 rounded-full w-20" />
              </div>
            </div>
          ))}
        </div>
      ) : friends.length === 0 ? (
        <div className="px-5">
          <div className="bg-gray-900 rounded-2xl p-6 text-center">
            <p className="text-sm font-semibold text-gray-200 mb-1">No friends yet</p>
            <p className="text-xs text-gray-400">Find friends in the Social tab</p>
          </div>
        </div>
      ) : (
        <div className="px-5 flex flex-col gap-1">
          {friends.map(profile => (
            <div key={profile.id} className="flex items-center gap-3 py-3">
              <button
                className="flex items-center gap-3 text-left flex-1 min-w-0 hover:bg-gray-900 rounded-xl px-2 -mx-2 py-1 transition-colors"
                onClick={() => onUserSelect?.(profile)}
              >
                <Avatar profile={profile} size="md" />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-100 truncate">{profile.display_name || profile.username}</p>
                  <p className="text-xs text-gray-400">@{profile.username}</p>
                </div>
              </button>
              <button
                onClick={() => setConfirmRemove(profile)}
                className="flex-shrink-0 text-xs text-gray-400 hover:text-red-500 font-medium transition-colors px-2 py-1"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// --- Profile Screen ---

function CircularProgress({ value, max }) {
  const pct = max > 0 ? Math.min(value / max, 1) : 0;
  const r = 38;
  const circumference = 2 * Math.PI * r;
  return (
    <svg width="96" height="96" viewBox="0 0 96 96" className="flex-shrink-0">
      <circle cx="48" cy="48" r={r} fill="none" stroke="#1f2937" strokeWidth="8" />
      <circle
        cx="48" cy="48" r={r} fill="none"
        stroke="#e5e7eb" strokeWidth="8"
        strokeDasharray={`${pct * circumference} ${circumference}`}
        strokeLinecap="round"
        transform="rotate(-90 48 48)"
      />
      <text x="48" y="44" textAnchor="middle" fontSize="16" fontWeight="bold" fill="#f3f4f6">{Math.round(pct * 100)}%</text>
      <text x="48" y="58" textAnchor="middle" fontSize="9" fill="#6b7280">complete</text>
    </svg>
  );
}

function FinishedThisYearModal({ onConfirm }) {
  const [finished, setFinished] = useState(true);
  const year = new Date().getFullYear();

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-[70]" />
      <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-[#161616] rounded-t-2xl z-[70] shadow-2xl">
        <div className="px-5 pt-6 pb-10 flex flex-col gap-5">
          <p className="text-base font-bold text-white">Did you finish this book in {year}?</p>
          <p className="text-sm text-gray-400 -mt-2">This helps track your reading goal accurately.</p>
          <div className="flex gap-3">
            <button
              onClick={() => setFinished(true)}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${finished ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-600'}`}
            >
              Yes
            </button>
            <button
              onClick={() => setFinished(false)}
              className={`flex-1 py-3 rounded-xl text-sm font-semibold transition-colors ${!finished ? 'bg-white text-gray-900' : 'bg-gray-800 text-gray-400 hover:bg-gray-600'}`}
            >
              No
            </button>
          </div>
          <button
            onClick={() => onConfirm(finished)}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </>
  );
}

function ReadingGoalModal({ currentGoal, onClose, onSaved, userId }) {
  const [value,  setValue]  = useState(String(currentGoal ?? ''));
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState(null);
  const year = new Date().getFullYear();

  const handleSave = async () => {
    const n = parseInt(value);
    if (!n || n < 1 || !userId) return;
    if (n > 366) { setError('Reading goal cannot exceed 366 books.'); return; }
    setSaving(true);
    setError(null);
    try {
      const { error } = await supabase.from('reading_goals').upsert(
        { user_id: userId, year, goal_count: n },
        { onConflict: 'user_id,year' }
      );
      if (error) throw error;
      onSaved(n);
    } catch {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-[#161616] rounded-t-2xl z-50 shadow-2xl">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-800">
          <h2 className="text-base font-bold text-white">{year} Reading Goal</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <CloseIcon />
          </button>
        </div>
        <div className="px-5 py-6 flex flex-col gap-4 pb-10">
          <p className="text-sm text-gray-400">How many books do you want to read in {year}?</p>
          <input
            type="number"
            min={1}
            value={value}
            onChange={e => setValue(e.target.value)}
            placeholder="e.g. 50"
            className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-2xl font-bold text-gray-100 text-center placeholder-gray-300 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
          />
          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          <button
            onClick={handleSave}
            disabled={saving || !value || parseInt(value) < 1}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : 'Set Goal'}
          </button>
        </div>
      </div>
    </>
  );
}

function FeedbackSection({ userId }) {
  const [open,       setOpen]       = useState(false);
  const [message,    setMessage]    = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [error,      setError]      = useState(null);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setSubmitting(true);
    setError(null);
    const { error: err } = await supabase.from('feedback').insert({ user_id: userId, message: message.trim() });
    setSubmitting(false);
    if (err) { setError('Something went wrong. Please try again.'); return; }
    setSubmitted(true);
    setMessage('');
    setTimeout(() => { setSubmitted(false); setOpen(false); }, 2500);
  };

  return (
    <div className="mx-5 mb-12 mt-4 bg-gray-900 rounded-2xl px-5 pt-7 pb-7 flex flex-col gap-6">
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">About ReadShelf</p>
        <p className="text-sm text-gray-400 leading-relaxed">ReadShelf started with a simple idea: book lovers deserve better. A place to share what you're reading, discover what others love, and actually connect with people who get it.</p>
        <p className="text-sm text-gray-400 leading-relaxed mt-3">We're building this from the ground up, shaped by the community every step of the way. Every rating, review, and recommendation makes ReadShelf better for everyone.</p>
        <p className="text-sm text-gray-400 leading-relaxed mt-3">This is your space. We're just here to build it.</p>
        <p className="text-sm text-gray-400 leading-relaxed mt-3 italic">We'd love to hear your thoughts below!</p>
      </div>
      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Contact Us</p>
        <a href="mailto:readshelfapp@proton.me" className="text-sm text-gray-200 hover:text-white transition-colors">readshelfapp@proton.me</a>
      </div>
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="w-full text-center bg-white text-gray-900 hover:bg-gray-100 font-semibold text-sm py-3.5 rounded-xl transition-colors"
        >
          Send Feedback
        </button>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-sm font-semibold text-white">Send Feedback</p>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder="What's on your mind? Bug reports, feature ideas, anything..."
            rows={4}
            className="w-full rounded-xl border border-gray-700 bg-gray-800 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none resize-none focus:border-gray-500 transition-colors"
          />
          {error     && <p className="text-xs text-red-500">{error}</p>}
          {submitted && <p className="text-xs text-emerald-600 font-medium">Thanks for the feedback!</p>}
          <div className="flex gap-2">
            <button
              onClick={() => { setOpen(false); setMessage(''); setError(null); }}
              className="flex-1 py-3 rounded-xl bg-gray-800 hover:bg-gray-700 text-sm font-semibold text-gray-300 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !message.trim()}
              className="flex-1 py-3 rounded-xl bg-white text-gray-900 hover:bg-gray-100 text-sm font-semibold transition-colors disabled:opacity-40"
            >
              {submitting ? 'Sending...' : 'Submit'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminPanel({ onClose, userId }) {
  const [tab,       setTab]       = useState('reviews');
  const [data,      setData]      = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [toast,     setToast]     = useState(null);
  const [confirm,   setConfirm]   = useState(null); // { label, onConfirm }

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(null), 3000); };

  const load = async (t) => {
    setLoading(true);
    setData([]);
    if (t === 'reviews') {
      const { data: rows } = await supabase.from('reviews').select('*, profiles(username, display_name)').not('review_text', 'is', null).neq('review_text', '').order('created_at', { ascending: false }).limit(100);
      setData(rows || []);
    } else if (t === 'discussions') {
      const { data: rows } = await supabase.from('club_discussions').select('*, profiles(username, display_name), book_clubs(name)').order('created_at', { ascending: false }).limit(100);
      setData(rows || []);
    } else if (t === 'communities') {
      const { data: rows } = await supabase.from('book_clubs').select('*, profiles(username, display_name)').order('created_at', { ascending: false });
      setData(rows || []);
    } else if (t === 'users') {
      const { data: rows } = await supabase.from('profiles').select('*').order('created_at', { ascending: false });
      const { data: achievements } = await supabase.from('reading_achievements').select('user_id, badge_id').eq('badge_id', 'founding_reader');
      const foundingIds = new Set((achievements || []).map(a => a.user_id));
      setData((rows || []).map(u => ({ ...u, _isFoundingReader: foundingIds.has(u.id) })));
    }
    setLoading(false);
  };

  useEffect(() => { load(tab); }, [tab]);

  const deleteReview = async (id) => {
    await supabase.from('reviews').delete().eq('id', id);
    setData(prev => prev.filter(r => r.id !== id));
    showToast('Review deleted');
  };

  const deleteDiscussion = async (id) => {
    await supabase.from('club_discussions').delete().eq('id', id);
    setData(prev => prev.filter(r => r.id !== id));
    showToast('Discussion deleted');
  };

  const deleteCommunity = async (id) => {
    await supabase.from('book_clubs').delete().eq('id', id);
    setData(prev => prev.filter(r => r.id !== id));
    showToast('Community deleted');
  };

  const toggleFeatured = async (community) => {
    const newVal = !community.is_featured;
    const { error } = await supabase.from('book_clubs').update({ is_featured: newVal }).eq('id', community.id);
    if (error) { showToast('Failed to update'); return; }
    setData(prev => prev.map(c => c.id === community.id ? { ...c, is_featured: newVal } : c));
    showToast(newVal ? `"${community.name}" featured` : `"${community.name}" unfeatured`);
  };

  const toggleBan = async (user) => {
    const newVal = !user.is_banned;
    await supabase.from('profiles').update({ is_banned: newVal }).eq('id', user.id);
    setData(prev => prev.map(u => u.id === user.id ? { ...u, is_banned: newVal } : u));
    showToast(newVal ? `${user.username} banned` : `${user.username} unbanned`);
  };

  const awardFoundingReader = async (user) => {
    const { error } = await supabase.from('reading_achievements').insert({ user_id: user.id, badge_id: 'founding_reader' });
    if (error && error.code !== '23505') { showToast('Failed to award badge'); return; }
    setData(prev => prev.map(u => u.id === user.id ? { ...u, _isFoundingReader: true } : u));
    showToast(`Founding Reader awarded to @${user.username}`);
  };

  const revokeFoundingReader = async (user) => {
    const { error } = await supabase.from('reading_achievements').delete().eq('user_id', user.id).eq('badge_id', 'founding_reader');
    if (error) { showToast('Failed to revoke badge'); return; }
    setData(prev => prev.map(u => u.id === user.id ? { ...u, _isFoundingReader: false } : u));
    showToast(`Founding Reader revoked from @${user.username}`);
  };

  const ask = (label, onConfirm) => setConfirm({ label, onConfirm });

  const TABS = ['reviews', 'discussions', 'communities', 'users'];

  return (
    <div className="fixed inset-0 bg-[#0f0f10] z-50 flex flex-col">
      {toast && (
        <div className="fixed top-5 left-0 right-0 max-w-lg mx-auto px-5 z-[60] pointer-events-none">
          <div className="bg-gray-700 text-white text-sm font-medium text-center py-3 px-5 rounded-2xl shadow-xl">{toast}</div>
        </div>
      )}
      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50" onClick={() => setConfirm(null)}>
          <div className="bg-gray-900 rounded-2xl mx-6 p-6 shadow-xl" onClick={e => e.stopPropagation()}>
            <p className="text-sm font-semibold text-white mb-4">{confirm.label}</p>
            <div className="flex gap-3">
              <button onClick={() => setConfirm(null)} className="flex-1 py-2.5 rounded-xl bg-gray-800 text-sm font-semibold text-gray-300">Cancel</button>
              <button onClick={() => { confirm.onConfirm(); setConfirm(null); }} className="flex-1 py-2.5 rounded-xl bg-red-500 text-sm font-semibold text-white">Delete</button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-5 pt-12 pb-3 border-b border-gray-800">
        <h1 className="text-base font-bold text-white">Admin Panel</h1>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-200"><CloseIcon /></button>
      </div>

      <div className="flex border-b border-gray-800 overflow-x-auto no-scrollbar">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`flex-shrink-0 px-4 py-3 text-xs font-semibold capitalize border-b-2 transition-colors ${tab === t ? 'border-gray-300 text-white' : 'border-transparent text-gray-400'}`}>
            {t}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="w-5 h-5 border-2 border-gray-600 border-t-gray-900 rounded-full animate-spin" /></div>
        ) : data.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-16">Nothing here</p>
        ) : (
          <div className="divide-y divide-gray-800">
            {tab === 'reviews' && data.map(r => (
              <div key={r.id} className="px-5 py-4 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-400">@{r.profiles?.username} · {r.book_title}</p>
                  <p className="text-sm text-gray-100 mt-1 leading-snug line-clamp-3">{r.review_text}</p>
                </div>
                <button onClick={() => ask('Delete this review?', () => deleteReview(r.id))} className="text-gray-300 hover:text-red-400 flex-shrink-0 text-xs mt-0.5">✕</button>
              </div>
            ))}
            {tab === 'discussions' && data.map(r => (
              <div key={r.id} className="px-5 py-4 flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-400">@{r.profiles?.username} · {r.book_clubs?.name}</p>
                  <p className="text-sm text-gray-100 mt-1 leading-snug line-clamp-3">{r.message}</p>
                </div>
                <button onClick={() => ask('Delete this message?', () => deleteDiscussion(r.id))} className="text-gray-300 hover:text-red-400 flex-shrink-0 text-xs mt-0.5">✕</button>
              </div>
            ))}
            {tab === 'communities' && data.map(r => (
              <div key={r.id} className="px-5 py-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white line-clamp-1">{r.name}</p>
                  <p className="text-xs text-gray-400 mt-0.5">by @{r.profiles?.username}</p>
                  {r.is_featured && <p className="text-xs text-amber-400 font-semibold mt-0.5">Featured</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <button
                    onClick={() => toggleFeatured(r)}
                    className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${r.is_featured ? 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50' : 'bg-gray-800 text-gray-300 hover:bg-gray-600'}`}
                  >
                    {r.is_featured ? 'Unfeature' : 'Feature'}
                  </button>
                  <button onClick={() => ask(`Delete "${r.name}"?`, () => deleteCommunity(r.id))} className="text-gray-300 hover:text-red-400 flex-shrink-0 text-xs">✕</button>
                </div>
              </div>
            ))}
            {tab === 'users' && data.map(u => (
              <div key={u.id} className="px-5 py-4 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white">{u.display_name || u.username}</p>
                  <p className="text-xs text-gray-400">@{u.username}</p>
                  {u.is_banned && <p className="text-xs text-red-500 font-semibold mt-0.5">Banned</p>}
                  {u._isFoundingReader && <p className="text-xs text-amber-400 font-semibold mt-0.5">Founding Reader</p>}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {u._isFoundingReader ? (
                    <button
                      onClick={() => revokeFoundingReader(u)}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors bg-red-900/30 text-red-400 hover:bg-red-900/50"
                    >
                      Revoke
                    </button>
                  ) : (
                    <button
                      onClick={() => awardFoundingReader(u)}
                      disabled={u.id === userId}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${u.id === userId ? 'bg-gray-800 text-gray-500 cursor-default' : 'bg-amber-900/30 text-amber-400 hover:bg-amber-900/50'}`}
                    >
                      Award
                    </button>
                  )}
                  {u.id !== userId && (
                    <button
                      onClick={() => toggleBan(u)}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors ${u.is_banned ? 'bg-gray-800 text-gray-300 hover:bg-gray-600' : 'bg-red-900/30 text-red-400 hover:bg-red-900/50'}`}
                    >
                      {u.is_banned ? 'Unban' : 'Ban'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function PrivacyPolicyScreen({ onBack }) {
  return (
    <div className="fixed inset-0 bg-[#0f0f10] z-50 flex flex-col overflow-y-auto">
      <div className="max-w-lg mx-auto px-5 pt-12 pb-16">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors mb-8">
          <BackIcon /> Profile
        </button>
        <h1 className="text-2xl font-bold text-white mb-6">Privacy Policy</h1>

        <div className="flex flex-col gap-6 text-sm text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-base font-semibold text-white mb-2">What we collect</h2>
            <p>When you create an account, we collect your email address and the display name you choose. As you use Readshelf, we store the books you rate and review, your reading activity, and your connections with other users (friends, communities).</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">How we use it</h2>
            <p>Your data is used to provide the Readshelf experience, powering your profile, community rankings, reading goals, and social features. We do not sell your data or share it with third parties for advertising purposes.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Analytics &amp; crash reporting</h2>
            <p>We use PostHog to understand how the app is used (e.g. which features are popular, navigation patterns). We use Sentry to capture error and crash reports so we can fix bugs quickly. These tools may collect your device type, OS version, approximate location, and anonymised usage events. They do not receive your email address, display name, or reading data. You can review their privacy policies at posthog.com/privacy and sentry.io/privacy.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Where it's stored</h2>
            <p>Your account and reading data is stored securely using Supabase (PostgreSQL), hosted in the US-West region. We use industry-standard security practices including row-level security policies. Analytics data is processed by PostHog (EU cloud) and crash data by Sentry (US).</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Your rights</h2>
            <p>You can request deletion of your account and all associated data at any time by using the feedback form in your profile. We will action deletion requests within 30 days.</p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">Early access</h2>
            <p>Readshelf is in early access. This policy may be updated as the product evolves. Continued use of the app after changes constitutes acceptance of the updated policy.</p>
          </section>

          <p className="text-xs text-gray-500 mt-4">Last updated: April 2026 · Added analytics and crash reporting disclosure</p>
        </div>
      </div>
    </div>
  );
}

function ProfileScreen({ bookData, onBookSelect, userId, userProfile, onProfileUpdate, onUserSelect, isAdmin, onGoalBookRemove, onGoalCountChange }) {
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [bookmarksOpen,    setBookmarksOpen]    = useState(false);
  const [lovedOpen,        setLovedOpen]        = useState(false);
  const [friendsOpen,      setFriendsOpen]      = useState(false);
  const [goal,             setGoal]             = useState(null);
  const [showGoalModal,    setShowGoalModal]    = useState(false);
  const [showDeleteGoalConfirm, setShowDeleteGoalConfirm] = useState(false);
  const [goalBooksOpen,        setGoalBooksOpen]        = useState(false);
  const [booksReadOpen,        setBooksReadOpen]        = useState(false);
  const [unlockedBadges,   setUnlockedBadges]   = useState(new Set());
  const [badgeToast,       setBadgeToast]       = useState(null);
  const [showAllActivity,     setShowAllActivity]     = useState(false);
  const [loadingActivityId,   setLoadingActivityId]   = useState(null);
  const [uploadingAvatar,  setUploadingAvatar]  = useState(false);
  const [avatarError,      setAvatarError]      = useState(null);
  const [showAdminPanel,   setShowAdminPanel]   = useState(false);
  const [friendCount,      setFriendCount]      = useState(null);
  const [settingsOpen,     setSettingsOpen]     = useState(false);
  const [isPrivate,        setIsPrivate]        = useState(userProfile?.is_private ?? false);
  const [privacyLoading,   setPrivacyLoading]   = useState(false);
  const [confirmPrivacyOpen, setConfirmPrivacyOpen] = useState(false);
  const avatarInputRef = useRef(null);

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !userId) return;
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!ALLOWED_TYPES.includes(file.type)) { setAvatarError('Please select a JPEG, PNG, WebP, or GIF image.'); return; }
    if (file.size > 5 * 1024 * 1024) { setAvatarError('Image must be under 5MB.'); return; }
    setUploadingAvatar(true);
    setAvatarError(null);
    try {
      const ext  = file.name.split('.').pop();
      const path = `${userId}/avatar.${ext}`;
      const { error: uploadErr } = await supabase.storage.from('avatars').upload(path, file, { upsert: true });
      if (uploadErr) throw uploadErr;
      const { data: { publicUrl } } = supabase.storage.from('avatars').getPublicUrl(path);
      const { error: updateErr } = await supabase.from('profiles').update({ avatar_url: publicUrl }).eq('id', userId);
      if (updateErr) throw updateErr;
      onProfileUpdate({ avatar_url: publicUrl });
    } catch (err) {
      console.error('Avatar upload error:', err);
      setAvatarError(err?.message || 'Upload failed. Please try again.');
      setTimeout(() => setAvatarError(null), 5000);
    } finally {
      setUploadingAvatar(false);
      e.target.value = '';
    }
  };

  const handleTogglePrivacy = async () => {
    if (privacyLoading) return;
    setPrivacyLoading(true);
    const next = !isPrivate;
    setIsPrivate(next);
    const { error } = await supabase.from('profiles').update({ is_private: next }).eq('id', userId);
    if (error) {
      setIsPrivate(!next);
    } else {
      onProfileUpdate({ is_private: next });
    }
    setPrivacyLoading(false);
  };

  useEffect(() => {
    if (!userId) return;
    const year = new Date().getFullYear();
    supabase.from('friendships').select('id', { count: 'exact', head: true })
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`)
      .eq('status', 'accepted')
      .then(({ count }) => setFriendCount(count || 0));

    Promise.all([
      supabase.from('reading_goals').select('*').eq('user_id', userId).eq('year', year).maybeSingle(),
      supabase.from('reading_achievements').select('badge_id').eq('user_id', userId),
      supabase.from('reading_activity').select('activity_date').eq('user_id', userId),
    ]).then(async ([goalRes, achievementsRes, activityRes]) => {
      if (goalRes.data) setGoal(goalRes.data);
      const unlocked = new Set((achievementsRes.data || []).map(r => r.badge_id));
      const dates = (activityRes.data || []).map(r => r.activity_date);
      if (!unlocked.has('seven_day_streak') && hasSevenDayStreak(dates)) {
        const isNew = await unlockBadge('seven_day_streak', userId).catch(() => false);
        if (isNew) {
          unlocked.add('seven_day_streak');
          setBadgeToast('7-Day Streak');
          setTimeout(() => setBadgeToast(null), 3000);
        }
      }
      setUnlockedBadges(new Set(unlocked));
    });
  }, [userId]);

  useEffect(() => {
    setIsPrivate(userProfile?.is_private ?? false);
  }, [userProfile?.is_private]); // eslint-disable-line react-hooks/exhaustive-deps

  if (showPrivacyPolicy) {
    return <PrivacyPolicyScreen onBack={() => setShowPrivacyPolicy(false)} />;
  }
  if (settingsOpen) {
    return (
      <div className="bg-[#0f0f10] min-h-screen pt-10 pb-12">
        {confirmPrivacyOpen && (
          <div className="fixed inset-0 bg-black/60 z-[200] flex items-end justify-center p-5">
            <div className="bg-gray-900 rounded-2xl w-full max-w-sm p-6 flex flex-col gap-3">
              <h2 className="text-base font-bold text-white text-center">
                {isPrivate ? 'Switch to Public?' : 'Switch to Private?'}
              </h2>
              <p className="text-sm text-gray-400 text-center leading-relaxed">
                {isPrivate
                  ? 'Your shelf and reviews will be visible to everyone.'
                  : 'Only your friends will be able to see your shelf and reviews.'}
              </p>
              <div className="flex gap-3 mt-1">
                <button onClick={() => setConfirmPrivacyOpen(false)} className="flex-1 py-3 text-sm font-semibold text-gray-300 bg-gray-800 rounded-xl hover:bg-gray-700 transition-colors">Cancel</button>
                <button onClick={() => { setConfirmPrivacyOpen(false); handleTogglePrivacy(); }} className="flex-1 py-3 text-sm font-semibold text-white bg-gray-700 rounded-xl hover:bg-gray-600 transition-colors">Confirm</button>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center justify-between px-5 mb-8">
          <button onClick={() => setSettingsOpen(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
          <h1 className="text-base font-bold text-white">Settings</h1>
          <div className="w-12" />
        </div>

        <div className="px-5 flex flex-col gap-4">
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">Account</p>
            <div className="bg-gray-900 rounded-2xl divide-y divide-gray-800">
              <div className="flex items-center justify-between px-4 py-4">
                <div>
                  <p className="text-sm font-semibold text-gray-100">Private Account</p>
                  <p className="text-xs text-gray-400 mt-0.5">Only friends see your shelf &amp; reviews</p>
                </div>
                <button
                  onClick={() => setConfirmPrivacyOpen(true)}
                  disabled={privacyLoading}
                  className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${isPrivate ? 'bg-white' : 'bg-gray-700'}`}
                >
                  <span className={`absolute top-0.5 w-5 h-5 rounded-full transition-transform ${isPrivate ? 'translate-x-[22px] bg-gray-900' : 'translate-x-0.5 bg-gray-500'}`} />
                </button>
              </div>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 px-1">About</p>
            <div className="bg-gray-900 rounded-2xl divide-y divide-gray-800">
              <button
                onClick={() => setShowPrivacyPolicy(true)}
                className="w-full flex items-center justify-between px-4 py-4 text-left"
              >
                <p className="text-sm font-semibold text-gray-100">Privacy Policy</p>
                <span className="text-gray-500 text-sm">›</span>
              </button>
            </div>
          </div>

          <div className="bg-gray-900 rounded-2xl">
            <button
              onClick={() => supabase.auth.signOut()}
              className="w-full px-4 py-4 text-left"
            >
              <p className="text-sm font-semibold text-red-400">Sign Out</p>
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (booksReadOpen) {
    return <BooksReadScreen bookData={bookData} onBack={() => setBooksReadOpen(false)} onBookSelect={onBookSelect} />;
  }
  if (bookmarksOpen) {
    return <BookmarksScreen bookData={bookData} onBack={() => setBookmarksOpen(false)} onBookSelect={onBookSelect} />;
  }
  if (lovedOpen) {
    return <LovedScreen bookData={bookData} onBack={() => setLovedOpen(false)} onBookSelect={onBookSelect} />;
  }
  if (friendsOpen) {
    return <FriendsListScreen userId={userId} onBack={() => setFriendsOpen(false)} onUserSelect={onUserSelect} />;
  }

  if (goalBooksOpen) {
    const year = new Date().getFullYear();
    const handleRemoveFromGoal = async (bookId) => {
      await supabase.from('reviews').update({ finished_this_year: false }).eq('user_id', userId).eq('book_id', bookId);
      onGoalBookRemove?.(bookId);
    };
    const currentGoalBooks = Object.entries(bookData).filter(([, d]) => d.finishedThisYear === true && d.rating > 0 && d._meta);
    return (
      <div className="pt-4 pb-12">
        <div className="flex items-center px-5 pt-6 pb-2">
          <button onClick={() => setGoalBooksOpen(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
        </div>
        <div className="px-5 mb-5">
          <h2 className="text-base font-bold text-white">{year} Reading Goal</h2>
          <p className="text-xs text-gray-400 mt-0.5">{currentGoalBooks.length} of {goal?.goal_count ?? 0} books read</p>
        </div>
        {currentGoalBooks.length === 0 ? (
          <div className="px-5">
            <div className="bg-gray-900 rounded-2xl p-6 text-center">
              <p className="text-sm font-semibold text-gray-200 mb-1">No books yet</p>
              <p className="text-xs text-gray-400">Books you finish and rate this year will appear here</p>
            </div>
          </div>
        ) : (
          <div className="px-5 flex flex-col gap-0">
            {currentGoalBooks.map(([id, d]) => (
              <div key={id} className="flex items-center gap-3 py-3.5 border-b border-gray-800 last:border-0">
                <div
                  className="flex-shrink-0 w-12 h-[68px] rounded-lg overflow-hidden shadow-sm cursor-pointer"
                  onClick={() => onBookSelect({ id, ...d._meta })}
                >
                  {d._meta.cover
                    ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                    : <div className={`${d._meta.bg || getBgColor(id)} w-full h-full`} />
                  }
                </div>
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => onBookSelect({ id, ...d._meta })}>
                  <p className="text-sm font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{d._meta.author}</p>
                  {d.rating > 0 && <p className="text-xs text-amber-500 font-medium mt-1">★ {d.rating}/10</p>}
                </div>
                <button
                  onClick={() => handleRemoveFromGoal(id)}
                  className="flex-shrink-0 p-2 text-gray-300 hover:text-red-400 transition-colors"
                  title="Remove from this year"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>
                </button>
              </div>
            ))}
          </div>
        )}
        <p className="text-xs text-gray-400 text-center mt-6 px-5">Removing a book only uncounts it from your {year} goal. It stays in your library.</p>
      </div>
    );
  }

  const entries        = Object.values(bookData);
  const booksRead      = entries.filter(d => d.rating > 0 || d.loved || d.review?.trim()).length;
  const loved          = entries.filter(d => d.loved).length;
  const bookmarks      = entries.filter(d => d.booklisted).length;
  const goalBooks      = Object.entries(bookData).filter(([, d]) => d.finishedThisYear === true && d.rating > 0 && d._meta);
  const goalProgress   = goalBooks.length;
  const favouriteReads = Object.entries(bookData).filter(([, d]) => d.rating > 0 && d._meta).sort(([, a], [, b]) => b.rating - a.rating);
  const activity       = Object.entries(bookData).filter(([, d]) => (d.rating > 0 || d.loved || d.review?.trim()) && d._meta).reverse();

  const goalCount = goal?.goal_count ?? 0;
  const now = new Date();
  const monthsElapsed = now.getMonth() + 1;
  const expectedByNow = goalCount > 0 ? Math.ceil((goalCount / 12) * monthsElapsed) : 0;
  const isOnTrack = goalProgress >= expectedByNow;
  const behindBy = expectedByNow - goalProgress;

  return (
    <div className="pt-10">
      {showGoalModal && (
        <ReadingGoalModal
          currentGoal={goal?.goal_count}
          onClose={() => setShowGoalModal(false)}
          onSaved={n => { setGoal(prev => ({ ...(prev || {}), goal_count: n })); onGoalCountChange?.(n); setShowGoalModal(false); }}
          userId={userId}
        />
      )}

      {showDeleteGoalConfirm && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40" onClick={() => setShowDeleteGoalConfirm(false)}>
          <div className="bg-[#161616] w-full max-w-lg rounded-t-3xl p-6 pb-10" onClick={e => e.stopPropagation()}>
            <h2 className="text-base font-bold text-white mb-1">Delete Reading Goal?</h2>
            <p className="text-sm text-gray-400 mb-6">Your {new Date().getFullYear()} reading goal will be removed. This can't be undone.</p>
            <div className="flex flex-col gap-3">
              <button
                onClick={async () => {
                  await supabase.from('reading_goals').delete().eq('user_id', userId).eq('year', new Date().getFullYear());
                  setGoal(null);
                  onGoalCountChange?.(0);
                  setShowDeleteGoalConfirm(false);
                }}
                className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3.5 rounded-xl transition-colors"
              >
                Delete Goal
              </button>
              <button
                onClick={() => setShowDeleteGoalConfirm(false)}
                className="w-full bg-gray-800 hover:bg-gray-600 text-gray-200 font-semibold py-3.5 rounded-xl transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {badgeToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-amber-500 text-white text-sm font-bold text-center py-3 px-5 rounded-2xl shadow-xl">
            Badge unlocked: {badgeToast}
          </div>
        </div>
      )}

      <div className="flex justify-end px-5 mb-2">
        <button
          onClick={() => setSettingsOpen(true)}
          className="p-2 -mr-2 text-gray-400 hover:text-white transition-colors"
          aria-label="Settings"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
          </svg>
        </button>
      </div>

      <div className="px-5 flex flex-col items-center mb-10">
        <div className="relative mb-4">
          <Avatar profile={userProfile} size="lg" className="shadow-md" />
          <button
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            className="absolute bottom-0 right-0 w-8 h-8 bg-white text-gray-900 hover:bg-gray-100 rounded-full flex items-center justify-center shadow-md transition-colors disabled:opacity-50"
          >
            {uploadingAvatar
              ? <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              : <CameraIcon />
            }
          </button>
          <input ref={avatarInputRef} type="file" accept="image/*" className="hidden" onChange={handleAvatarUpload} />
        </div>
        {avatarError && <p className="text-xs text-red-500 mb-2 text-center">{avatarError}</p>}
        <h1 className="text-xl font-bold text-white">{userProfile?.display_name || userProfile?.username || 'Loading...'}</h1>
        <p className="text-sm text-gray-400 mt-1">@{userProfile?.username || ''}</p>
        {isAdmin && (
          <button
            onClick={() => setShowAdminPanel(true)}
            className="mt-2 text-xs font-semibold text-gray-900 bg-white hover:bg-gray-100 px-4 py-1.5 rounded-lg transition-colors"
          >
            Admin Panel
          </button>
        )}
      </div>
      {showAdminPanel && <AdminPanel onClose={() => setShowAdminPanel(false)} userId={userId} />}

      <TopBooks userId={userId} isOwnProfile={true} onBookSelect={onBookSelect} />

      <div className="px-5 flex gap-2 justify-center mb-6">
        <div className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors" onClick={() => setBooksReadOpen(true)}>
          <span className="text-2xl font-bold text-white">{booksRead}</span>
          <span className="text-xs text-gray-400 mt-1.5">Books Read</span>
        </div>
        <div className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors" onClick={() => setLovedOpen(true)}>
          <span className="text-2xl font-bold text-rose-500">{loved}</span>
          <span className="text-xs text-gray-400 mt-1.5">Loved</span>
        </div>
        <div className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors" onClick={() => setBookmarksOpen(true)}>
          <span className="text-2xl font-bold text-indigo-500">{bookmarks}</span>
          <span className="text-xs text-gray-400 mt-1.5">Bookmarks</span>
        </div>
        <div className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors" onClick={() => setFriendsOpen(true)}>
          <span className="text-2xl font-bold text-emerald-600">{friendCount ?? '-'}</span>
          <span className="text-xs text-gray-400 mt-1.5">Friends</span>
        </div>
      </div>

      <div className="px-5 mb-8">
        {goalCount > 0 ? (
          <div className={`rounded-2xl p-5 flex items-center gap-5 ${goalProgress >= goalCount ? 'bg-emerald-900/30 border border-emerald-800' : 'bg-gray-900'}`}>
            <button onClick={() => setGoalBooksOpen(true)} className="flex-shrink-0">
              <CircularProgress value={goalProgress} max={goalCount} />
            </button>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <button className="text-left flex-1 min-w-0" onClick={() => setGoalBooksOpen(true)}>
                  <p className="text-base font-bold text-white">{now.getFullYear()} Reading Goal</p>
                  <p className="text-sm text-gray-400 mt-0.5">{goalProgress} / {goalCount} books</p>
                  {goalProgress >= goalCount ? (
                    <p className="text-xs font-semibold mt-2 text-emerald-600">Goal complete! Well done!</p>
                  ) : (
                    <p className={`text-xs font-semibold mt-2 ${isOnTrack ? 'text-emerald-600' : 'text-amber-600'}`}>
                      {isOnTrack ? "You're on track!" : `Read ${behindBy} more this month`}
                    </p>
                  )}
                </button>
                <button
                  onClick={() => setShowDeleteGoalConfirm(true)}
                  className="text-gray-300 hover:text-red-400 transition-colors mt-0.5 flex-shrink-0"
                  title="Delete goal"
                >
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M12 4L4 12M4 4l8 8" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round"/></svg>
                </button>
              </div>
              <button
                onClick={() => setShowGoalModal(true)}
                className="mt-3 text-xs text-gray-400 hover:text-gray-300 underline transition-colors"
              >
                Edit goal
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowGoalModal(true)}
            className="w-full bg-gray-900 hover:bg-gray-800 border border-dashed border-gray-600 rounded-2xl py-5 flex flex-col items-center gap-2 transition-colors"
          >
            <p className="text-sm font-semibold text-gray-200">Set a Reading Goal</p>
            <p className="text-xs text-gray-400">Track how many books you want to read this year</p>
          </button>
        )}
      </div>

      {favouriteReads.length > 0 && (
        <section className="mb-8">
          <h2 className="text-base font-bold text-white mb-3 px-5">Favorite Reads</h2>
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 no-scrollbar">
            {favouriteReads.map(([id, d]) => (
              <div key={id} className="flex-shrink-0 w-32 cursor-pointer group" onClick={() => onBookSelect({ id, ...d._meta })}>
                <div className="w-32 h-48 rounded-xl overflow-hidden mb-2 shadow-sm group-hover:shadow-md transition-shadow">
                  {d._meta.cover
                    ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                    : <div className={`${d._meta.bg} w-full h-full flex items-end p-2.5`}>
                        <p className="text-white text-xs font-semibold line-clamp-3 leading-tight drop-shadow">{d._meta.title}</p>
                      </div>
                  }
                </div>
                <p className="text-xs font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
                <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{d._meta.author}</p>
                <p className="text-xs text-amber-500 mt-0.5">★ {d.rating}/10</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {activity.length > 0 && (
        <section className="mb-10">
          <h2 className="text-base font-bold text-white mb-4 px-5">Recent Activity</h2>
          <div className="px-5 flex flex-col gap-5">
            {(showAllActivity ? activity.slice(0, 10) : activity.slice(0, 5)).map(([id, d]) => (
              <div key={id} className="flex gap-3 cursor-pointer" onClick={async () => {
                if (loadingActivityId) return;
                setLoadingActivityId(id);
                try {
                  const full = await fetchBookById(id);
                  onBookSelect({ ...full, cover: d._meta.cover || full.cover });
                } catch {
                  onBookSelect({ id, ...d._meta });
                } finally {
                  setLoadingActivityId(null);
                }
              }}>
                <div className="flex-shrink-0 w-16 h-24 rounded-xl overflow-hidden shadow-sm relative">
                  {d._meta.cover
                    ? <img src={d._meta.cover} alt={d._meta.title} className="w-full h-full object-cover" />
                    : <div className={`${d._meta.bg} w-full h-full`} />
                  }
                  {loadingActivityId === id && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-xs font-semibold text-gray-100 line-clamp-1">{d._meta.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{d._meta.author}</p>
                  <div className="mt-2">
                    {d.review?.trim() ? (
                      <>
                        <p className="text-xs text-gray-200">{d.rating > 0 ? `You rated ${d.rating}/10` : 'You reviewed this'}</p>
                        <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-3">{d.review}</p>
                      </>
                    ) : d.rating > 0 ? (
                      <p className="text-xs text-gray-200">You rated <span className="text-amber-500 font-semibold">{d.rating}/10</span></p>
                    ) : (
                      <p className="text-xs text-gray-200">You <span className="text-rose-500">loved</span> this</p>
                    )}
                    <p className="text-xs text-gray-400 mt-1">@{userProfile?.username || ''}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {activity.length > 5 && (
            <button
              onClick={() => setShowAllActivity(v => !v)}
              className="mx-5 mt-5 w-[calc(100%-2.5rem)] py-3 rounded-xl bg-gray-800 hover:bg-gray-600 text-sm font-semibold text-gray-300 transition-colors"
            >
              {showAllActivity ? 'Show less' : `Show more (${Math.min(activity.length, 10) - 5} more)`}
            </button>
          )}
        </section>
      )}

      {unlockedBadges.size > 0 && (
        <section className="mb-10 px-5">
          <h2 className="text-base font-bold text-white mb-3">Achievements</h2>
          <div className="grid grid-cols-2 gap-3">
            {BADGES.filter(badge => unlockedBadges.has(badge.id)).map(badge => (
              <div key={badge.id} className={`rounded-2xl p-4 ${getBgColor(badge.id)}`}>
                <p className="text-sm font-bold text-white">{badge.label}</p>
                <p className="text-xs mt-1 leading-snug text-white/80">{badge.desc}</p>
                <p className="text-[10px] text-white/70 font-semibold mt-2 uppercase tracking-wide">Unlocked</p>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

// --- Genre Tile ---

function GenreTile({ genre, onSelect }) {
  return (
    <div className={`${genre.bg} rounded-xl h-20 flex items-center justify-center px-3 cursor-pointer active:scale-95 transition-transform shadow-sm`} onClick={() => onSelect(genre)}>
      <p className="text-white text-sm font-bold text-center drop-shadow leading-snug">{genre.name}</p>
    </div>
  );
}

// --- Genre Detail Screen ---

function GenreDetailScreen({ genre, onBack, onBookSelect }) {
  const fallback = FALLBACK_GENRE_BOOKS[genre.id] || { mostLiked: [] };
  const [mostLiked, setMostLiked] = useState(fallback.mostLiked);

  useEffect(() => {
    async function load() {
      try {
        const { data } = await supabase.from('featured_books').select('*').eq('genre', genre.id).order('sort_order');
        const liked = (data || []).map(mapFeaturedBook);
        const hydrated = await hydrateWithCommunityRatings(liked);

        // Re-rank by community score once ≥3 books have community ratings
        const communityRated = hydrated.filter(b => b.ratingSource === 'community');
        if (communityRated.length >= 3) {
          const reranked = hydrated
            .map(b => ({ ...b, _score: b.ratingSource === 'community' ? b.rating * Math.log(b.ratingCount + 1) : 0 }))
            .sort((a, b) => b._score - a._score);
          setMostLiked(reranked);
          return;
        }
        setMostLiked(hydrated.length >= 2 ? hydrated : fallback.mostLiked);
      } catch {} // silently keep fallback
    }
    load();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genre.id]);

  const heroBook  = mostLiked[0] ?? null;
  const restLiked = mostLiked.slice(1);

  return (
    <div className="pt-3">
      <div className="flex items-center gap-2 px-5 mb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Home
        </button>
        <span className="text-gray-300">/</span>
        <span className="text-sm font-semibold text-white">{genre.name}</span>
      </div>

      {heroBook && <HeroBookCard book={heroBook} onSelect={onBookSelect} />}

      <HorizontalSection title="Most Liked This Month" books={restLiked} onBookSelect={onBookSelect} />
    </div>
  );
}

// --- Community Screen ---

function CommunityScreen({ onSelectGenre, onBookSelect, bookData, userId }) {
  const [mostLiked, setMostLiked] = useState([]);
  const [loading,   setLoading]   = useState(true);

  useEffect(() => {
    Promise.all([
      supabase.from('featured_books').select('*').order('sort_order'),
      fetchPopularFromApp(),
    ])
      .then(async ([{ data: featured }, appPopular]) => {
        const featuredBooks = (featured || []).map(mapFeaturedBook);

        if (appPopular.length >= 1) {
          setMostLiked(appPopular);
        } else {
          const base = featuredBooks.length > 0 ? featuredBooks : FALLBACK_MOST_LIKED;
          const hydrated = await hydrateWithCommunityRatings(base);
          setMostLiked(hydrated);
        }
      })
      .catch(() => {
        setMostLiked(FALLBACK_MOST_LIKED);
      })
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const heroBook     = mostLiked[0] ?? null;
  const restLiked    = mostLiked.slice(1);

  return (
    <div className="pt-4">
      {/* Early access banner */}
      <div className="mx-5 mb-5 bg-gray-900 rounded-2xl px-5 py-4">
        <p className="text-xs text-gray-300 leading-relaxed">
          ReadShelf is in early access, we&apos;re building this community together. iOS &amp; Android will come as we grow.
        </p>
      </div>

      {/* Hero — top pick from Most Liked */}
      {!loading && heroBook && <HeroBookCard book={heroBook} onSelect={onBookSelect} />}
      {loading && <div className="mx-5 mb-8 h-56 rounded-2xl bg-gray-800 animate-pulse" />}

      {/* Most Liked — rest of the list after the hero */}
      {(loading || restLiked.length > 0) && (
        <HorizontalSection title="Most Liked This Month" books={restLiked} onBookSelect={onBookSelect} loading={loading} />
      )}

      <section className="mb-8">
        <h2 className="text-base font-bold text-white mb-3 px-5">Browse by Genre</h2>
        <div className="grid grid-cols-2 gap-3 px-5">
          {GENRES.map(genre => <GenreTile key={genre.id} genre={genre} onSelect={onSelectGenre} />)}
        </div>
      </section>

      <FeedbackSection userId={userId} />
    </div>
  );
}

// --- Activity Detail Sheet ---

function ActivityDetailSheet({ item, onClose, onBookSelect, userId }) {
  const [loadingBook, setLoadingBook] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [liked, setLiked] = useState(false);

  useEffect(() => {
    if (!item?.id) return;
    supabase.from('review_likes').select('user_id').eq('review_id', item.id)
      .then(({ data }) => {
        setLikeCount((data || []).length);
        setLiked((data || []).some(l => l.user_id === userId));
      });
  }, [item?.id, userId]);

  const handleBookClick = async () => {
    if (!item.book.id || !onBookSelect) return;
    setLoadingBook(true);
    try {
      const full = await fetchBookById(item.book.id);
      onClose();
      onBookSelect({ ...full, cover: item.book.cover || full.cover });
    } catch {
      onClose();
      onBookSelect({ id: item.book.id, title: item.book.title, author: item.book.author, cover: item.book.cover || null, bg: item.book.bg, year: '', description: '' });
    } finally {
      setLoadingBook(false);
    }
  };

  const toggleLike = async () => {
    if (!userId) return;
    const wasLiked = liked;
    setLiked(!wasLiked);
    setLikeCount(c => c + (wasLiked ? -1 : 1));
    const { error } = wasLiked
      ? await supabase.from('review_likes').delete().eq('review_id', item.id).eq('user_id', userId)
      : await supabase.from('review_likes').insert({ review_id: item.id, user_id: userId });
    if (error) {
      setLiked(wasLiked);
      setLikeCount(c => c + (wasLiked ? 1 : -1));
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-[#161616] rounded-t-2xl z-50 px-5 pt-4 pb-10 shadow-2xl">
        <div className="flex justify-end mb-4">
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <CloseIcon />
          </button>
        </div>
        <div className="flex gap-4 items-start">
          <button
            className="flex-shrink-0 w-28 h-40 rounded-xl overflow-hidden shadow-md relative active:scale-95 transition-transform"
            onClick={handleBookClick}
            disabled={loadingBook}
          >
            {item.book.cover
              ? <img src={item.book.cover} alt={item.book.title} className="w-full h-full object-cover" />
              : <div className={`${item.book.bg} w-full h-full flex items-end p-2.5`}>
                  <p className="text-white text-xs font-semibold line-clamp-3 leading-tight drop-shadow">{item.book.title}</p>
                </div>
            }
            {loadingBook && (
              <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </button>
          <div className="flex-1 min-w-0">
            <button className="text-left mb-0.5" onClick={handleBookClick} disabled={loadingBook}>
              <p className="text-sm font-bold text-white leading-snug line-clamp-2">{item.book.title}</p>
              <p className="text-xs text-gray-400 mt-0.5">{item.book.author}</p>
            </button>
            <div className="mt-3">
              {item.type === 'loved' ? (
                <p className="text-sm text-gray-200"><span className="font-semibold">{item.name}</span>{' '}<span className="text-rose-500">loved</span> this</p>
              ) : (
                <>
                  <p className="text-sm text-gray-200"><span className="font-semibold">{item.name}</span> rated {item.rating}/10</p>
                  {item.review && <p className="text-xs text-gray-400 mt-2 leading-relaxed">{item.review}</p>}
                  {item.review && (
                    <div className="mt-3">
                      <LikeButton count={likeCount} liked={liked} onToggle={toggleLike} size="md" />
                    </div>
                  )}
                </>
              )}
              <p className="text-xs text-gray-400 mt-2">@{item.username}</p>
            </div>
            <button
              onClick={handleBookClick}
              disabled={loadingBook}
              className="mt-4 text-xs font-semibold text-white bg-gray-800 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
            >
              {loadingBook ? 'Loading…' : 'View Book →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

// --- Friends Screen ---

function FriendsScreen({ onBookSelect, userId, onAuthRequired, onUserSelect, onRequestHandled }) {
  const [friendActivity,    setFriendActivity]    = useState([]);
  const [friendRequests,    setFriendRequests]    = useState([]);
  const [friends,           setFriends]           = useState([]);
  const [userSearch,        setUserSearch]        = useState('');
  const [searchResults,     setSearchResults]     = useState([]);
  const [searchLoading,     setSearchLoading]     = useState(false);
  const [pendingIds,        setPendingIds]        = useState(new Set());
  const [friendRec,         setFriendRec]         = useState([]);
  const [suggestedAccounts,    setSuggestedAccounts]    = useState([]);
  const [selectedActivity,     setSelectedActivity]     = useState(null);
  const [friendActivityExpanded, setFriendActivityExpanded] = useState(false);
  const [loadingRecId,           setLoadingRecId]           = useState(null);
  const [friendDataLoaded,       setFriendDataLoaded]       = useState(false);
  const searchTimer = useRef(null);

  const fetchFriendData = async () => {
    if (!userId) return;

    const { data: friendships } = await supabase
      .from('friendships')
      .select('*')
      .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);

    const all = friendships || [];
    const accepted = all.filter(f => f.status === 'accepted');
    const incomingPending = all.filter(f => f.status === 'pending' && f.addressee_id === userId);
    const outgoingPending = all.filter(f => f.status === 'pending' && f.requester_id === userId);

    setFriends(accepted);
    setPendingIds(new Set(outgoingPending.map(f => f.addressee_id)));

    // Enrich incoming requests with profiles
    if (incomingPending.length > 0) {
      const requesterIds = incomingPending.map(f => f.requester_id);
      const { data: profiles } = await supabase.from('profiles').select('*').in('id', requesterIds);
      const profileMap = {};
      (profiles || []).forEach(p => { profileMap[p.id] = p; });
      setFriendRequests(incomingPending.map(f => ({ ...f, profile: profileMap[f.requester_id] })));
    } else {
      setFriendRequests([]);
    }

    const friendIds = accepted.map(f => f.requester_id === userId ? f.addressee_id : f.requester_id);

    // Build set of people we already know about (self, friends, pending either direction)
    const alreadyKnownIds = new Set([
      userId,
      ...friendIds,
      ...outgoingPending.map(f => f.addressee_id),
      ...incomingPending.map(f => f.requester_id),
    ]);

    // Suggested accounts: friends-of-friends + popular
    const fofCountMap = new Map(); // profileId -> mutual friend count
    if (friendIds.length > 0) {
      const { data: fofRows } = await supabase
        .from('friendships')
        .select('requester_id, addressee_id')
        .eq('status', 'accepted')
        .or(`requester_id.in.(${friendIds.join(',')}),addressee_id.in.(${friendIds.join(',')})`);
      (fofRows || []).forEach(f => {
        const other = friendIds.includes(f.requester_id) ? f.addressee_id : f.requester_id;
        if (!alreadyKnownIds.has(other)) fofCountMap.set(other, (fofCountMap.get(other) || 0) + 1);
      });
    }

    // Popularity: count accepted friendships per user
    const { data: allFriendshipRows } = await supabase
      .from('friendships')
      .select('requester_id, addressee_id')
      .eq('status', 'accepted')
      .limit(300);
    const popularityMap = new Map();
    (allFriendshipRows || []).forEach(f => {
      popularityMap.set(f.requester_id, (popularityMap.get(f.requester_id) || 0) + 1);
      popularityMap.set(f.addressee_id, (popularityMap.get(f.addressee_id) || 0) + 1);
    });

    // Combine: FoF candidates first, then popular strangers
    const candidateIds = new Set(fofCountMap.keys());
    [...popularityMap.entries()]
      .filter(([id]) => !alreadyKnownIds.has(id) && !candidateIds.has(id))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .forEach(([id]) => candidateIds.add(id));

    // Fallback: fill with recently joined users when pool is thin
    if (candidateIds.size < 3) {
      const needed = 3 - candidateIds.size;
      const excludeList = [...new Set([...alreadyKnownIds, ...candidateIds])]; // always non-empty: userId is in alreadyKnownIds
      const { data: recentUsers } = await supabase
        .from('profiles')
        .select('id')
        .not('id', 'in', `(${excludeList.join(',')})`)
        .order('created_at', { ascending: false })
        .limit(needed);
      (recentUsers || []).forEach(p => candidateIds.add(p.id));
    }

    if (candidateIds.size > 0) {
      const topIds = [...candidateIds].slice(0, 5);
      const { data: sugProfiles } = await supabase.from('profiles').select('*').in('id', topIds);
      const scored = (sugProfiles || [])
        .map(p => ({ ...p, _mutuals: fofCountMap.get(p.id) || 0, _popular: popularityMap.get(p.id) || 0 }))
        .sort((a, b) => (b._mutuals * 3 + b._popular) - (a._mutuals * 3 + a._popular));
      setSuggestedAccounts(scored.slice(0, 3));
    } else {
      setSuggestedAccounts([]);
    }

    if (friendIds.length === 0) { setFriendDataLoaded(true); return; }

    // Fetch reviews from friends — only meaningful activity (rated, loved, or wrote a review)
    const { data: reviews } = await supabase
      .from('reviews')
      .select('*, profiles(id, username, display_name, avatar_bg, avatar_url), review_likes(count)')
      .in('user_id', friendIds)
      .or('rating.gt.0,loved.eq.true,review_text.not.is.null')
      .order('created_at', { ascending: false });

    setFriendActivity(reviews || []);

    // Recommendations: highly rated books from friends
    const highRated = (reviews || []).filter(r => r.rating >= 8);
    if (highRated.length > 0) {
      setFriendRec(highRated.map(r => ({
        id: r.book_id,
        title: r.book_title,
        author: r.book_author || '',
        cover: r.book_cover || null,
        bg: getBgColor(r.book_id),
        profile: r.profiles || null,
        username: r.profiles?.username || 'friend',
        rating: r.rating,
        year: '',
        description: '',
      })));
    }
    setFriendDataLoaded(true);
  };

  useEffect(() => { fetchFriendData(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // User search with debounce
  useEffect(() => {
    if (!userSearch.trim()) { setSearchResults([]); return; }
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(async () => {
      setSearchLoading(true);
      const { data } = await supabase
        .from('profiles')
        .select('*')
        .ilike('username', `%${userSearch}%`)
        .neq('id', userId || '')
        .limit(10);
      setSearchResults(data || []);
      setSearchLoading(false);
    }, 400);
    return () => clearTimeout(searchTimer.current);
  }, [userSearch, userId]);

  const handleAddFriend = async (profileId) => {
    if (!userId) { onAuthRequired?.(); return; }
    const { error } = await supabase.from('friendships').insert({
      requester_id: userId,
      addressee_id: profileId,
      status: 'pending',
    });
    if (!error) setPendingIds(prev => new Set([...prev, profileId]));
  };

  const handleUnrequest = async (profileId) => {
    await supabase.from('friendships')
      .delete()
      .eq('requester_id', userId)
      .eq('addressee_id', profileId)
      .eq('status', 'pending');
    setPendingIds(prev => { const next = new Set(prev); next.delete(profileId); return next; });
  };

  const handleAcceptFriend = async (friendshipId) => {
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
    setFriendRequests(prev => prev.filter(f => f.id !== friendshipId));
    onRequestHandled?.();
    fetchFriendData();
  };

  const friendUserIds = new Set(
    friends.map(f => f.requester_id === userId ? f.addressee_id : f.requester_id)
  );

  // Shared row renderer for any activity item (friends feed + community feed)
  const renderActivityItem = (item, key) => {
    const profile = item.profiles;
    const activityItem = {
      id: item.id,
      book: { id: item.book_id, title: item.book_title, author: item.book_author || '', bg: getBgColor(item.book_id), cover: item.book_cover || null },
      name: profile?.display_name || profile?.username || 'Reader',
      username: profile?.username || '',
      type: 'reviewed',
      rating: item.rating,
      review: item.review_text,
    };
    return (
      <div key={key} className="flex gap-3 cursor-pointer" onClick={() => setSelectedActivity(activityItem)}>
        <div className="flex-shrink-0 w-16 h-24 rounded-xl overflow-hidden shadow-sm">
          {item.book_cover
            ? <img src={item.book_cover} alt={item.book_title} className="w-full h-full object-cover" />
            : <div className={`${getBgColor(item.book_id)} w-full h-full`} />
          }
        </div>
        <div className="flex-1 min-w-0 pt-0.5">
          <button
            onClick={e => { e.stopPropagation(); profile && onUserSelect?.(profile); }}
            className="flex items-center gap-1.5 mb-1.5"
          >
            <Avatar profile={profile} size="sm" />
            <span className="text-xs font-semibold text-gray-100">{profile?.display_name || profile?.username || 'Reader'}</span>
            <span className="text-xs text-gray-400">@{profile?.username || ''}</span>
          </button>
          <p className="text-xs font-semibold text-gray-100 line-clamp-1">{item.book_title}</p>
          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{item.book_author}</p>
          <div className="mt-2">
            {item.rating ? (
              <>
                <p className="text-xs text-gray-200">rated {item.rating}/10</p>
                {item.review_text && <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">{item.review_text}</p>}
                {parseInt(item.review_likes?.[0]?.count ?? 0) > 0 && (
                  <p className="text-[11px] text-gray-500 mt-1">♥ {parseInt(item.review_likes?.[0]?.count ?? 0)}</p>
                )}
              </>
            ) : item.loved ? (
              <p className="text-xs text-rose-500 font-medium">♥ loved this</p>
            ) : (
              <p className="text-xs text-gray-200">reviewed this</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="pt-4">
      {selectedActivity && (
        <ActivityDetailSheet item={selectedActivity} onClose={() => setSelectedActivity(null)} onBookSelect={onBookSelect} userId={userId} />
      )}

      {/* Friend Requests — pinned at top */}
      {friendRequests.length > 0 && (
        <section className="mb-6 px-5">
          <h2 className="text-base font-bold text-white mb-3">
            Friend Requests
            <span className="ml-2 text-xs font-semibold text-white bg-red-500 rounded-full px-2 py-0.5">{friendRequests.length}</span>
          </h2>
          <div className="flex flex-col gap-3">
            {friendRequests.map(req => (
              <div key={req.id} className="flex items-center justify-between">
                <button className="flex items-center gap-3 text-left" onClick={() => req.profile && onUserSelect?.(req.profile)}>
                  <Avatar profile={req.profile} size="md" />
                  <div>
                    <p className="text-sm font-semibold text-gray-100">{req.profile?.display_name || req.profile?.username || 'Unknown'}</p>
                    <p className="text-xs text-gray-400">@{req.profile?.username || req.requester_id.slice(0, 8)}</p>
                  </div>
                </button>
                <button
                  onClick={() => handleAcceptFriend(req.id)}
                  className="text-xs font-semibold bg-white text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Accept
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Find Friends */}
      <section className="mb-8 px-5">
        <h2 className="text-base font-bold text-white mb-3">Find Friends</h2>
        <input
          type="text"
          value={userSearch}
          onChange={e => setUserSearch(e.target.value)}
          placeholder="Search by username..."
          className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
        />
        {searchLoading && <p className="text-xs text-gray-400 text-center mt-2">Searching...</p>}
        {searchResults.length > 0 && (
          <div className="mt-3 flex flex-col gap-3">
            {searchResults.map(profile => {
              const isFriend = friendUserIds.has(profile.id);
              const isPending = pendingIds.has(profile.id);
              return (
                <div key={profile.id} className="flex items-center justify-between">
                  <button className="flex items-center gap-3 text-left" onClick={() => onUserSelect?.(profile)}>
                    <Avatar profile={profile} size="md" />
                    <div>
                      <p className="text-sm font-semibold text-gray-100">{profile.display_name || profile.username}</p>
                      <p className="text-xs text-gray-400">@{profile.username}</p>
                    </div>
                  </button>
                  {isFriend ? (
                    <span className="text-xs font-semibold text-emerald-400 bg-emerald-900/30 px-3 py-1.5 rounded-lg">Friends ✓</span>
                  ) : isPending ? (
                    <button onClick={() => handleUnrequest(profile.id)} className="text-xs font-semibold text-gray-400 bg-gray-800 px-3 py-1.5 rounded-lg hover:bg-red-900/30 hover:text-red-400 transition-colors">Requested</button>
                  ) : (
                    <button onClick={() => handleAddFriend(profile.id)} className="text-xs font-semibold bg-white text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">Add Friend</button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Suggested Accounts */}
      {!userSearch.trim() && suggestedAccounts.length > 0 && (
        <section className="mb-8 px-5">
          <h2 className="text-base font-bold text-white mb-3">Suggested for You</h2>
          <div className="flex flex-col gap-3">
            {suggestedAccounts.map(profile => {
              const isFriend = friendUserIds.has(profile.id);
              const isPending = pendingIds.has(profile.id);
              const mutuals = profile._mutuals || 0;
              return (
                <div key={profile.id} className="flex items-center justify-between">
                  <button className="flex items-center gap-3 text-left" onClick={() => onUserSelect?.(profile)}>
                    <Avatar profile={profile} size="md" />
                    <div>
                      <p className="text-sm font-semibold text-gray-100">{profile.display_name || profile.username}</p>
                      <p className="text-xs text-gray-400">{mutuals > 0 ? `${mutuals} mutual friend${mutuals > 1 ? 's' : ''}` : `@${profile.username}`}</p>
                    </div>
                  </button>
                  {isFriend ? (
                    <span className="text-xs font-semibold text-emerald-400 bg-emerald-900/30 px-3 py-1.5 rounded-lg">Friends ✓</span>
                  ) : isPending ? (
                    <button onClick={() => handleUnrequest(profile.id)} className="text-xs font-semibold text-gray-400 bg-gray-800 px-3 py-1.5 rounded-lg hover:bg-red-900/30 hover:text-red-400 transition-colors">Requested</button>
                  ) : (
                    <button onClick={() => handleAddFriend(profile.id)} className="text-xs font-semibold bg-white text-gray-900 px-3 py-1.5 rounded-lg hover:bg-gray-100 transition-colors">Add Friend</button>
                  )}
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* Books Recommended by Friends */}
      {friendRec.length > 0 && (
        <section className="mb-10">
          <h2 className="text-base font-bold text-white mb-3 px-5">Recommended by Friends</h2>
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 no-scrollbar">
            {friendRec.map(book => (
              <div key={book.id} className="flex-shrink-0 w-32 cursor-pointer group" onClick={async () => {
                if (loadingRecId) return;
                setLoadingRecId(book.id);
                try {
                  const full = await fetchBookById(book.id);
                  onBookSelect({ ...full, cover: book.cover || full.cover });
                } catch {
                  onBookSelect(book);
                } finally {
                  setLoadingRecId(null);
                }
              }}>
                <div className="w-32 h-48 rounded-xl overflow-hidden mb-2 shadow-sm group-hover:shadow-md transition-shadow relative">
                  {book.cover
                    ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
                    : <div className={`${book.bg} w-full h-full flex items-end p-2.5`}>
                        <p className="text-white text-xs font-semibold line-clamp-3 leading-tight drop-shadow">{book.title}</p>
                      </div>
                  }
                  {loadingRecId === book.id && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <p className="text-xs font-semibold text-gray-100 line-clamp-1">{book.title}</p>
                <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{book.author}</p>
                {book.rating > 0 && <p className="text-xs text-amber-500 mt-0.5">★ {book.rating}/10</p>}
                {book.profile && (
                  <button
                    onClick={e => { e.stopPropagation(); onUserSelect?.(book.profile); }}
                    className="flex items-center gap-1 mt-1.5"
                  >
                    <Avatar profile={book.profile} size="sm" />
                    <span className="text-xs text-gray-400">@{book.profile.username}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Friends' Activity — ghost when 0 friends, real feed otherwise */}
      {friendDataLoaded && friends.length === 0 ? (
        <section className="mb-10 px-5">
          <h2 className="text-base font-bold text-white mb-4">Friends' Activity</h2>
          <div className="relative overflow-hidden" style={{ height: 180 }}>
            <div className="flex flex-col gap-5 pointer-events-none select-none" style={{ filter: 'blur(4px)', opacity: 0.25 }}>
              {/* Ghost row 1 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-16 h-24 rounded-xl bg-gray-800" />
                <div className="flex-1 pt-0.5 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-7 h-7 rounded-full bg-gray-700 flex-shrink-0" />
                    <div className="h-3 w-20 bg-gray-700 rounded-full" />
                    <div className="h-3 w-14 bg-gray-800 rounded-full" />
                  </div>
                  <div className="h-3 w-28 bg-gray-700 rounded-full" />
                  <div className="h-3 w-20 bg-gray-800 rounded-full" />
                  <div className="h-3 w-16 bg-gray-700 rounded-full" />
                </div>
              </div>
              {/* Ghost row 2 */}
              <div className="flex gap-3">
                <div className="flex-shrink-0 w-16 h-24 rounded-xl bg-gray-800" />
                <div className="flex-1 pt-0.5 flex flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <div className="w-7 h-7 rounded-full bg-gray-700 flex-shrink-0" />
                    <div className="h-3 w-16 bg-gray-700 rounded-full" />
                    <div className="h-3 w-10 bg-gray-800 rounded-full" />
                  </div>
                  <div className="h-3 w-24 bg-gray-700 rounded-full" />
                  <div className="h-3 w-16 bg-gray-800 rounded-full" />
                </div>
              </div>
            </div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#0f0f10] flex items-end justify-center pb-3">
              <span className="text-xs font-semibold text-gray-300 bg-gray-800 border border-gray-700 rounded-full px-4 py-2">
                Add a friend to unlock your feed
              </span>
            </div>
          </div>
        </section>
      ) : friendActivity.length > 0 && (
        <section className="mb-10">
          <h2 className="text-base font-bold text-white mb-4 px-5">Friends' Activity</h2>
          <div className="px-5 flex flex-col gap-5">
            {friendActivity.slice(0, friendActivityExpanded ? 10 : 5).map(item => renderActivityItem(item, item.id))}
          </div>
          {friendActivity.length > 5 && (
            <div className="px-5 mt-4">
              <button
                onClick={() => setFriendActivityExpanded(v => !v)}
                className="w-full text-xs font-semibold text-gray-400 hover:text-white py-2 transition-colors"
              >
                {friendActivityExpanded ? 'Show less' : `Show more (${Math.min(friendActivity.length, 10) - 5} more)`}
              </button>
            </div>
          )}
        </section>
      )}

    </div>
  );
}

// --- Search Overlay ---

function SearchOverlay({ onClose, onBookSelect }) {
  const [query,   setQuery]   = useState('');
  const [books,   setBooks]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);
  const timer    = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useEffect(() => {
    if (query.trim().length < 2) { setBooks([]); setError(null); return; }

    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const results = await fetchBooks(query.trim(), 8, { sortByScore: true });
        setBooks(results);
      } catch {
        setError('Something went wrong. Please try again.');
        setBooks([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => clearTimeout(timer.current);
  }, [query]);

  return (
    <div className="fixed inset-0 bg-[#0f0f10] z-50 flex flex-col">
      <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-800">
        <button onClick={onClose} className="text-gray-400 hover:text-gray-200 flex-shrink-0"><CloseIcon /></button>
        <div className="flex-1 flex items-center gap-2 bg-gray-900 border border-gray-800 rounded-xl px-3 py-2.5">
          <span className="text-gray-400 flex-shrink-0"><SearchIcon /></span>
          <input
            ref={inputRef}
            type="text"
            placeholder="Search for books..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="flex-1 bg-transparent text-base text-white placeholder-gray-400 outline-none"
          />
          {query && <button onClick={() => setQuery('')} className="text-xs text-gray-400 hover:text-gray-300 flex-shrink-0">Clear</button>}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        {query.trim().length < 2  && <p className="text-center text-gray-400 text-sm py-16">Keep typing to search for books</p>}
        {loading                  && <p className="text-center text-gray-400 text-sm py-16">Searching...</p>}
        {error                    && <p className="text-center text-red-400 text-sm py-16">{error}</p>}
        {!loading && query.trim().length >= 2 && books.length === 0 && !error && (
          <p className="text-center text-gray-400 text-sm py-16">No results for "{query}"</p>
        )}
        {!loading && books.length > 0 && (
          <div className="flex flex-col divide-y divide-gray-800">
            {books.map(book => (
              <div key={book.id} className="flex items-center gap-4 px-5 py-3 cursor-pointer hover:bg-gray-900 active:bg-gray-800 transition-colors" onClick={() => onBookSelect?.(book)}>
                <div className="w-12 flex-shrink-0 rounded-md overflow-hidden bg-gray-800 flex items-center justify-center" style={{ height: '72px' }}>
                  {book.cover
                    ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
                    : <div className={`w-full h-full ${book.bg} flex items-center justify-center`}><span className="text-white text-xs font-bold px-1 text-center leading-tight">{book.title.slice(0, 2)}</span></div>
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-white line-clamp-2 leading-snug">{book.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{book.author}</p>
                  {book.year && <p className="text-xs text-gray-300 mt-0.5">{book.year}</p>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function TopBooks({ userId, isOwnProfile, onBookSelect }) {
  const [books,         setBooks]         = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [showPicker,    setShowPicker]    = useState(false);
  const [removingId,    setRemovingId]    = useState(null);
  const [loadingBookId, setLoadingBookId] = useState(null);
  const [editing,       setEditing]       = useState(false);

  useEffect(() => {
    if (!userId) return;
    supabase.from('top_books').select('*').eq('user_id', userId).order('added_at').limit(5)
      .then(({ data }) => { setBooks(data || []); setLoading(false); });
  }, [userId]);

  const handleAdd = async (book) => {
    if (books.length >= 5) return;
    if (books.some(b => b.book_id === book.id)) return;
    const { data, error } = await supabase.from('top_books').insert({
      user_id:   userId,
      book_id:   book.id,
      book_data: { title: book.title, author: book.author, cover: book.cover },
    }).select().single();
    if (error) throw error;
    setBooks(prev => [...prev, data]);
  };

  const handleRemove = async (topBookId) => {
    setRemovingId(topBookId);
    try {
      const { error } = await supabase.from('top_books').delete().eq('id', topBookId);
      if (error) throw error;
      setBooks(prev => prev.filter(b => b.id !== topBookId));
    } finally {
      setRemovingId(null);
    }
  };

  const handleBookClick = async (b) => {
    setLoadingBookId(b.id);
    try {
      const full = await fetchBookById(b.book_id);
      onBookSelect(full);
    } catch {
      onBookSelect({ id: b.book_id, title: b.book_data.title, author: b.book_data.author || 'Unknown', cover: b.book_data.cover || null, bg: getBgColor(b.book_id), description: '', year: '' });
    } finally {
      setLoadingBookId(null);
    }
  };

  if (loading) return null;
  if (!isOwnProfile && books.length === 0) return null;

  return (
    <div className="mb-6">
      {showPicker && (
        <BookPickerModal title="Add to Top Books" confirmLabel="Add to Top Books" onClose={() => setShowPicker(false)} onSelect={handleAdd} />
      )}
      <div className="flex items-center justify-between mb-3 px-5">
        <h2 className="text-base font-bold text-white">Top Books</h2>
        {isOwnProfile && books.length > 0 && (
          <button onClick={() => setEditing(v => !v)} className="text-xs font-semibold text-gray-400 hover:text-white transition-colors">
            {editing ? 'Done' : 'Edit'}
          </button>
        )}
      </div>
      <div className="flex gap-1.5 px-5">
        {books.map(b => (
          <div key={b.id} className="relative flex-1 min-w-0">
            <button
              className="w-full aspect-[2/3] rounded-xl overflow-hidden shadow-sm block relative"
              disabled={loadingBookId === b.id}
              onClick={() => handleBookClick(b)}
            >
              {b.book_data?.cover
                ? <img src={b.book_data.cover} alt={b.book_data.title} className="w-full h-full object-cover" />
                : <div className={`${getBgColor(b.book_id)} w-full h-full flex items-end p-1.5`}>
                    <p className="text-white text-[9px] font-semibold line-clamp-3 leading-tight drop-shadow">{b.book_data?.title}</p>
                  </div>
              }
              {loadingBookId === b.id && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
            {isOwnProfile && editing && (
              <button
                onClick={() => handleRemove(b.id)}
                disabled={removingId === b.id}
                className="absolute -top-1 -right-1 w-5 h-5 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50 z-10"
              >
                <span className="text-[10px] font-bold leading-none">✕</span>
              </button>
            )}
          </div>
        ))}
        {isOwnProfile && books.length < 5 && Array.from({ length: 5 - books.length }).map((_, i) => (
          <button
            key={`empty-${i}`}
            onClick={() => setShowPicker(true)}
            className="flex-1 min-w-0 aspect-[2/3] rounded-xl border-2 border-dashed border-gray-700 flex items-center justify-center hover:border-gray-500 hover:bg-gray-800 transition-colors text-gray-500"
          >
            <PlusIcon />
          </button>
        ))}
      </div>
    </div>
  );
}

function UserProfileScreen({ profile, onBack, onBookSelect, userId, onUserSelect }) {
  const [friendStatus,  setFriendStatus]  = useState(null); // null=loading, 'none'|'pending_out'|'pending_in'|'friends'
  const [friendshipId,  setFriendshipId]  = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [stats,         setStats]         = useState(null); // { booksRead, loved }
  const [friendCount,   setFriendCount]   = useState(null);
  const [favouriteReads, setFavouriteReads] = useState([]);
  const [recentActivity, setRecentActivity] = useState([]);
  const [allBooksRead,  setAllBooksRead]  = useState([]);
  const [allLovedBooks, setAllLovedBooks] = useState([]);
  const [viewFriends,        setViewFriends]        = useState(false);
  const [viewBooksRead,      setViewBooksRead]      = useState(false);
  const [viewLovedBooks,     setViewLovedBooks]     = useState(false);
  const [recentActivityExpanded,  setRecentActivityExpanded]  = useState(false);
  const [loadingActivityId,       setLoadingActivityId]       = useState(null);

  // Fetch friendship status with the logged-in user
  useEffect(() => {
    if (!userId || !profile?.id || profile.id === userId) { setFriendStatus('none'); return; }
    supabase
      .from('friendships')
      .select('id, status, requester_id, addressee_id')
      .or(`and(requester_id.eq.${userId},addressee_id.eq.${profile.id}),and(requester_id.eq.${profile.id},addressee_id.eq.${userId})`)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) { setFriendStatus('none'); return; }
        setFriendshipId(data.id);
        if (data.status === 'accepted') setFriendStatus('friends');
        else if (data.requester_id === userId) setFriendStatus('pending_out');
        else setFriendStatus('pending_in');
      });
  }, [userId, profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch this user's reading data + friend count
  useEffect(() => {
    if (!profile?.id) return;
    supabase.from('reviews').select('*').eq('user_id', profile.id).order('created_at', { ascending: false })
      .then(({ data }) => {
        const all = data || [];
        const booksReadList = all.filter(r => (r.rating > 0 || r.loved || r.review_text?.trim()) && r.book_title);
        const lovedList     = all.filter(r => r.loved && r.book_title);
        setStats({ booksRead: booksReadList.length, loved: lovedList.length });
        setAllBooksRead(booksReadList);
        setAllLovedBooks(lovedList);
        setFavouriteReads(all.filter(r => r.rating > 0 && r.book_title).sort((a, b) => b.rating - a.rating).slice(0, 10));
        setRecentActivity(booksReadList.slice(0, 10));
      });
    supabase.from('friendships')
      .select('id', { count: 'exact', head: true })
      .or(`requester_id.eq.${profile.id},addressee_id.eq.${profile.id}`)
      .eq('status', 'accepted')
      .then(({ count }) => setFriendCount(count || 0));
  }, [profile?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddFriend = async () => {
    if (!userId) return;
    setActionLoading(true);
    const { data, error } = await supabase.from('friendships').insert({
      requester_id: userId, addressee_id: profile.id, status: 'pending',
    }).select().single();
    if (!error) { setFriendStatus('pending_out'); setFriendshipId(data?.id); }
    setActionLoading(false);
  };

  const handleAccept = async () => {
    if (!friendshipId) return;
    setActionLoading(true);
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
    setFriendStatus('friends');
    setActionLoading(false);
  };

  const handleUnrequest = async () => {
    if (!friendshipId) return;
    setActionLoading(true);
    await supabase.from('friendships').delete().eq('id', friendshipId);
    setFriendStatus('none');
    setFriendshipId(null);
    setActionLoading(false);
  };

  // Show their friends list inside the same overlay
  if (viewFriends) {
    return (
      <div className="fixed inset-0 bg-[#0f0f10] z-[80] overflow-y-auto">
        <FriendsListScreen
          userId={profile.id}
          onBack={() => setViewFriends(false)}
          onUserSelect={onUserSelect}
        />
      </div>
    );
  }

  if (viewBooksRead) {
    return (
      <div className="fixed inset-0 bg-[#0f0f10] z-[80] overflow-y-auto pb-12">
        <div className="flex items-center px-5 pt-10 pb-2">
          <button onClick={() => setViewBooksRead(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
        </div>
        <h2 className="text-base font-bold text-white px-5 mb-4">Books Read</h2>
        {allBooksRead.length === 0 ? (
          <div className="px-5">
            <div className="bg-gray-900 rounded-2xl p-6 text-center">
              <p className="text-sm font-semibold text-gray-200 mb-1">No books yet</p>
              <p className="text-xs text-gray-400">This reader hasn't logged any books</p>
            </div>
          </div>
        ) : (
          <div className="px-5 flex flex-col gap-4">
            {allBooksRead.map(r => (
              <div
                key={r.id}
                className="flex gap-3 cursor-pointer"
                onClick={() => onBookSelect({ id: r.book_id, title: r.book_title, author: r.book_author || '', cover: r.book_cover || null, bg: getBgColor(r.book_id), year: '', description: '' })}
              >
                <div className="flex-shrink-0 w-14 h-20 rounded-xl overflow-hidden shadow-sm">
                  {r.book_cover
                    ? <img src={r.book_cover} alt={r.book_title} className="w-full h-full object-cover" />
                    : <div className={`${getBgColor(r.book_id)} w-full h-full`} />
                  }
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-gray-100 line-clamp-1">{r.book_title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{r.book_author}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    {r.rating > 0 && <span className="text-xs text-amber-500 font-medium">★ {r.rating}/10</span>}
                    {r.loved && <span className="text-xs text-rose-500 font-medium">♥ Loved</span>}
                  </div>
                  {r.review_text && (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">{r.review_text}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (viewLovedBooks) {
    return (
      <div className="fixed inset-0 bg-[#0f0f10] z-[80] overflow-y-auto pb-12">
        <div className="flex items-center px-5 pt-10 pb-2">
          <button onClick={() => setViewLovedBooks(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
        </div>
        <h2 className="text-base font-bold text-white px-5 mb-4">Loved Books</h2>
        {allLovedBooks.length === 0 ? (
          <div className="px-5">
            <div className="bg-gray-900 rounded-2xl p-6 text-center">
              <p className="text-sm font-semibold text-gray-200 mb-1">No loved books yet</p>
              <p className="text-xs text-gray-400">This reader hasn't loved any books</p>
            </div>
          </div>
        ) : (
          <div className="px-5 flex flex-col gap-4">
            {allLovedBooks.map(r => (
              <div
                key={r.id}
                className="flex gap-3 cursor-pointer"
                onClick={() => onBookSelect({ id: r.book_id, title: r.book_title, author: r.book_author || '', cover: r.book_cover || null, bg: getBgColor(r.book_id), year: '', description: '' })}
              >
                <div className="flex-shrink-0 w-14 h-20 rounded-xl overflow-hidden shadow-sm">
                  {r.book_cover
                    ? <img src={r.book_cover} alt={r.book_title} className="w-full h-full object-cover" />
                    : <div className={`${getBgColor(r.book_id)} w-full h-full`} />
                  }
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-gray-100 line-clamp-1">{r.book_title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{r.book_author}</p>
                  {r.rating > 0 && <p className="text-xs text-amber-500 font-medium mt-1.5">★ {r.rating}/10</p>}
                  {r.review_text && (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">{r.review_text}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const isLocked = profile?.is_private && friendStatus !== null && friendStatus !== 'friends' && profile?.id !== userId;

  if (isLocked) {
    return (
      <div className="fixed inset-0 bg-[#0f0f10] z-[80] flex flex-col overflow-y-auto pb-12">
        <div className="flex items-center px-5 pt-10 pb-4 flex-shrink-0">
          <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
        </div>
        <div className="px-5 flex flex-col items-center mb-8 flex-shrink-0">
          <Avatar profile={profile} size="lg" className="shadow-md mb-4" />
          <h1 className="text-xl font-bold text-white">{profile?.display_name || profile?.username}</h1>
          <p className="text-sm text-gray-400 mt-1">@{profile?.username || ''}</p>
          <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <span>🔒</span>
            <span>Private account</span>
          </p>
          {userId && profile?.id !== userId && (
            <div className="mt-4">
              {friendStatus === 'none' && (
                <button onClick={handleAddFriend} disabled={actionLoading} className="bg-white text-gray-900 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-100 disabled:opacity-50 transition-colors">Add Friend</button>
              )}
              {friendStatus === 'pending_out' && (
                <button onClick={handleUnrequest} disabled={actionLoading} className="bg-gray-800 text-gray-400 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-red-900/30 hover:text-red-400 disabled:opacity-50 transition-colors">Requested ✓</button>
              )}
              {friendStatus === 'pending_in' && (
                <button onClick={handleAccept} disabled={actionLoading} className="bg-white text-gray-900 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-100 disabled:opacity-50 transition-colors">Accept Request</button>
              )}
            </div>
          )}
        </div>
        <div className="px-5">
          <div className="bg-gray-900 rounded-2xl p-6 flex flex-col items-center text-center gap-3">
            <p className="text-2xl">📚</p>
            <p className="text-sm font-semibold text-gray-200">This account is private</p>
            <p className="text-xs text-gray-400 leading-relaxed">
              Add {profile?.display_name || profile?.username} as a friend to see their bookshelf and reading activity.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#0f0f10] z-[80] flex flex-col overflow-y-auto pb-12">
      {/* Header */}
      <div className="flex items-center px-5 pt-10 pb-4 flex-shrink-0">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Back
        </button>
      </div>

      {/* Avatar + name + friend action */}
      <div className="px-5 flex flex-col items-center mb-6 flex-shrink-0">
        <Avatar profile={profile} size="lg" className="shadow-md mb-4" />
        <h1 className="text-xl font-bold text-white">{profile?.display_name || profile?.username}</h1>
        <p className="text-sm text-gray-400 mt-1">@{profile?.username || ''}</p>
        {userId && profile?.id !== userId && (
          <div className="mt-4">
            {friendStatus === null && <div className="w-28 h-9 bg-gray-800 rounded-xl animate-pulse" />}
            {friendStatus === 'none' && (
              <button onClick={handleAddFriend} disabled={actionLoading} className="bg-white text-gray-900 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-100 disabled:opacity-50 transition-colors">Add Friend</button>
            )}
            {friendStatus === 'pending_out' && (
              <button onClick={handleUnrequest} disabled={actionLoading} className="bg-gray-800 text-gray-400 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-red-900/30 hover:text-red-400 disabled:opacity-50 transition-colors">Requested ✓</button>
            )}
            {friendStatus === 'pending_in' && (
              <button onClick={handleAccept} disabled={actionLoading} className="bg-white text-gray-900 text-sm font-semibold px-6 py-2.5 rounded-xl hover:bg-gray-100 disabled:opacity-50 transition-colors">Accept Request</button>
            )}
            {friendStatus === 'friends' && (
              <span className="inline-block bg-emerald-50 text-emerald-400 bg-emerald-900/30 text-sm font-semibold px-6 py-2.5 rounded-xl">Friends ✓</span>
            )}
          </div>
        )}
      </div>

      {/* Top Books */}
      <TopBooks userId={profile?.id} isOwnProfile={false} onBookSelect={onBookSelect} />

      {/* Stats: Books Read | Loved | Friends */}
      <div className="px-5 flex gap-2 justify-center mb-6">
        <div
          className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => setViewBooksRead(true)}
        >
          {stats === null
            ? <div className="w-8 h-7 bg-gray-700 rounded animate-pulse mb-1.5" />
            : <span className="text-2xl font-bold text-white">{stats.booksRead}</span>}
          <span className="text-xs text-gray-400 mt-1.5">Books Read</span>
        </div>
        <div
          className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => setViewLovedBooks(true)}
        >
          {stats === null
            ? <div className="w-8 h-7 bg-gray-700 rounded animate-pulse mb-1.5" />
            : <span className="text-2xl font-bold text-rose-500">{stats.loved}</span>}
          <span className="text-xs text-gray-400 mt-1.5">Loved</span>
        </div>
        <div
          className="flex-1 flex flex-col items-center bg-gray-900 rounded-2xl py-4 cursor-pointer hover:bg-gray-800 transition-colors"
          onClick={() => setViewFriends(true)}
        >
          {friendCount === null
            ? <div className="w-8 h-7 bg-gray-700 rounded animate-pulse mb-1.5" />
            : <span className="text-2xl font-bold text-emerald-600">{friendCount}</span>}
          <span className="text-xs text-gray-400 mt-1.5">Friends</span>
        </div>
      </div>

      {/* Favorite Reads */}
      {favouriteReads.length > 0 && (
        <section className="mb-8">
          <h2 className="text-base font-bold text-white mb-3 px-5">Favorite Reads</h2>
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 no-scrollbar">
            {favouriteReads.map(r => (
              <div
                key={r.book_id}
                className="flex-shrink-0 w-32 cursor-pointer group"
                onClick={() => onBookSelect({ id: r.book_id, title: r.book_title, author: r.book_author || '', cover: r.book_cover || null, bg: getBgColor(r.book_id), year: '', description: '' })}
              >
                <div className="w-32 h-48 rounded-xl overflow-hidden mb-2 shadow-sm group-hover:shadow-md transition-shadow">
                  {r.book_cover
                    ? <img src={r.book_cover} alt={r.book_title} className="w-full h-full object-cover" />
                    : <div className={`${getBgColor(r.book_id)} w-full h-full flex items-end p-2.5`}>
                        <p className="text-white text-xs font-semibold line-clamp-3 leading-tight drop-shadow">{r.book_title}</p>
                      </div>
                  }
                </div>
                <p className="text-xs font-semibold text-gray-100 line-clamp-1">{r.book_title}</p>
                <p className="text-xs text-gray-400 line-clamp-1 mt-0.5">{r.book_author}</p>
                <p className="text-xs text-amber-500 mt-0.5">★ {r.rating}/10</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Recent Activity */}
      {recentActivity.length > 0 && (
        <section className="mb-6">
          <h2 className="text-base font-bold text-white mb-4 px-5">Recent Activity</h2>
          <div className="px-5 flex flex-col gap-4">
            {recentActivity.slice(0, recentActivityExpanded ? 10 : 5).map(r => (
              <div
                key={r.id}
                className="flex gap-3 cursor-pointer"
                onClick={async () => {
                  if (loadingActivityId) return;
                  setLoadingActivityId(r.id);
                  try {
                    const full = await fetchBookById(r.book_id);
                    onBookSelect({ ...full, cover: r.book_cover || full.cover });
                  } catch {
                    onBookSelect({ id: r.book_id, title: r.book_title, author: r.book_author || '', cover: r.book_cover || null, bg: getBgColor(r.book_id), year: '', description: '' });
                  } finally {
                    setLoadingActivityId(null);
                  }
                }}
              >
                <div className="flex-shrink-0 w-14 h-20 rounded-xl overflow-hidden shadow-sm relative">
                  {r.book_cover
                    ? <img src={r.book_cover} alt={r.book_title} className="w-full h-full object-cover" />
                    : <div className={`${getBgColor(r.book_id)} w-full h-full`} />
                  }
                  {loadingActivityId === r.id && (
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0 pt-0.5">
                  <p className="text-sm font-semibold text-gray-100 line-clamp-1">{r.book_title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{r.book_author}</p>
                  <div className="mt-1.5 flex items-center gap-2">
                    {r.rating > 0 && <span className="text-xs text-amber-500 font-medium">★ {r.rating}/10</span>}
                    {r.loved && <span className="text-xs text-rose-500 font-medium">♥ Loved</span>}
                  </div>
                  {r.review_text && (
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed line-clamp-2">{r.review_text}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
          {recentActivity.length > 5 && (
            <div className="px-5 mt-4">
              <button
                onClick={() => setRecentActivityExpanded(v => !v)}
                className="w-full text-xs font-semibold text-gray-400 hover:text-white py-2 transition-colors"
              >
                {recentActivityExpanded ? 'Show less' : `Show more (${Math.min(recentActivity.length, 10) - 5} more)`}
              </button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function AvatarZoomModal({ profile, onClose }) {
  const initial = (profile?.display_name || profile?.username || '?')[0].toUpperCase();
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70" onClick={onClose}>
      <div className="flex flex-col items-center gap-4" onClick={e => e.stopPropagation()}>
        {profile?.avatar_url ? (
          <img src={profile.avatar_url} alt={initial} className="w-56 h-56 rounded-full object-cover shadow-2xl" />
        ) : (
          <div className={`w-56 h-56 rounded-full ${profile?.avatar_bg || 'bg-indigo-500'} flex items-center justify-center shadow-2xl`}>
            <span className="text-white font-bold text-7xl">{initial}</span>
          </div>
        )}
        <div className="text-center">
          <p className="text-white font-bold text-lg">{profile?.display_name || profile?.username || ''}</p>
          {profile?.username && <p className="text-white/60 text-sm">@{profile.username}</p>}
        </div>
        <button onClick={onClose} className="text-white/70 text-sm hover:text-white transition-colors">Tap to close</button>
      </div>
    </div>
  );
}

function Avatar({ profile, size = 'md', className = '' }) {
  const [zoomed, setZoomed] = useState(false);
  const sizeClasses = { sm: 'w-7 h-7', md: 'w-9 h-9', lg: 'w-24 h-24' };
  const textClasses  = { sm: 'text-xs',  md: 'text-sm', lg: 'text-3xl' };
  const base = `${sizeClasses[size] || sizeClasses.md} rounded-full flex-shrink-0 ${className}`;
  const initial = (profile?.display_name || profile?.username || '?')[0].toUpperCase();
  const handleZoom = (e) => { e.stopPropagation(); setZoomed(true); };
  return (
    <>
      {zoomed && <AvatarZoomModal profile={profile} onClose={() => setZoomed(false)} />}
      {profile?.avatar_url ? (
        <img src={profile.avatar_url} alt={initial} className={`${base} object-cover cursor-pointer`} onClick={handleZoom} />
      ) : (
        <div className={`${base} ${profile?.avatar_bg || 'bg-indigo-500'} flex items-center justify-center cursor-pointer`} onClick={handleZoom}>
          <span className={`text-white font-bold ${textClasses[size] || textClasses.md}`}>{initial}</span>
        </div>
      )}
    </>
  );
}

// --- Communities ---

function CommunityCard({ community, isMyComm, joinedIds, joiningId, onViewComm, onJoin, friendsIn = 0, onUserSelect, showFeaturedPill = false }) {
  const joined = joinedIds.has(community.id);
  const [coverError, setCoverError] = useState(false);
  const coverUrl = community.cover_book_data?.cover;
  const hasCover = !!coverUrl && !coverError;
  return (
    <div className="bg-gray-900 rounded-2xl overflow-hidden shadow-[0_4px_16px_rgba(0,0,0,0.4)] border border-gray-800 flex min-h-[88px]">
      <div className="w-[72px] flex-shrink-0">
        {hasCover
          ? <img src={coverUrl} alt={community.cover_book_data.title} className="w-full h-full object-cover" onError={() => setCoverError(true)} />
          : <div className={`${getBgColor(community.id)} w-full h-full flex items-center justify-center relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-black/20" />
              <span className="text-white text-3xl font-black relative drop-shadow-sm">{community.name[0]}</span>
            </div>
        }
      </div>
      <div className="flex-1 min-w-0 px-4 py-3 flex flex-col justify-between">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <p className="text-sm font-bold text-white line-clamp-1">{community.name}</p>
            {showFeaturedPill && (
              <span className="text-[10px] font-semibold bg-amber-400/20 text-amber-400 px-2 py-0.5 rounded-full flex-shrink-0">Featured</span>
            )}
          </div>
          {friendsIn > 0 && (
            <span className="text-[10px] font-semibold text-indigo-400">
              {friendsIn} friend{friendsIn > 1 ? 's' : ''} here
            </span>
          )}
          {community.description && (
            <p className="text-xs text-gray-400 mt-0.5 line-clamp-2 leading-relaxed">{community.description}</p>
          )}
        </div>
        <div className="flex items-center justify-between mt-2">
          <div className="flex flex-col gap-0.5 min-w-0 flex-1 mr-3">
            {community.creator_profile && (
              <button
                className="flex items-center gap-1.5 min-w-0 text-left disabled:cursor-default"
                onClick={() => onUserSelect?.(community.creator_profile)}
                disabled={!onUserSelect}
              >
                <Avatar profile={community.creator_profile} size="sm" />
                <span className="text-xs text-gray-400 font-medium truncate">
                  {community.creator_profile.display_name || `@${community.creator_profile.username}`}
                </span>
              </button>
            )}
            <div className="flex items-center gap-1 text-gray-400">
              <UsersIcon />
              <span className="text-xs">{community.member_count} {community.member_count === 1 ? 'member' : 'members'}</span>
            </div>
          </div>
          <button
            onClick={() => onViewComm(community)}
            disabled={joiningId === community.id}
            className={`px-4 py-1.5 rounded-lg text-xs font-semibold transition-colors flex-shrink-0 ${
              joined
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-white text-gray-900 hover:bg-gray-100 disabled:opacity-50'
            }`}
          >
            {joiningId === community.id ? 'Joining...' : joined ? 'Open' : 'View'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CreateCommunityModal({ onClose, onCreated, userId }) {
  const [name,           setName]           = useState('');
  const [description,    setDescription]    = useState('');
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [coverBook,      setCoverBook]      = useState(null);
  const [coverQuery,     setCoverQuery]     = useState('');
  const [coverResults,   setCoverResults]   = useState([]);
  const [coverSearching, setCoverSearching] = useState(false);
  const coverTimer = useRef(null);

  useEffect(() => {
    if (!coverQuery.trim()) { setCoverResults([]); return; }
    clearTimeout(coverTimer.current);
    coverTimer.current = setTimeout(async () => {
      setCoverSearching(true);
      try { setCoverResults(await fetchBooks(coverQuery, 5)); }
      catch { setCoverResults([]); }
      finally { setCoverSearching(false); }
    }, 400);
    return () => clearTimeout(coverTimer.current);
  }, [coverQuery]);

  const handleCreate = async () => {
    if (!name.trim()) { setError('Community name is required.'); return; }
    if (!userId) { setError('You must be signed in to create a community.'); return; }
    setLoading(true);
    setError(null);
    try {
      const { data: community, error: commErr } = await supabase
        .from('book_clubs')
        .insert({
          name: name.trim(),
          description: description.trim() || null,
          is_public: true,
          creator_id: userId,
          cover_book_id:   coverBook ? coverBook.id   : null,
          cover_book_data: coverBook ? { title: coverBook.title, author: coverBook.author, cover: coverBook.cover } : null,
        })
        .select()
        .single();
      if (commErr) throw commErr;
      await supabase.from('club_members').insert({ club_id: community.id, user_id: userId });
      onCreated();
    } catch {
      setError('Failed to create community. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-[#161616] rounded-t-2xl z-50 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-800 sticky top-0 bg-[#161616]">
          <h2 className="text-base font-bold text-white">Create Community</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors"><CloseIcon /></button>
        </div>
        <div className="px-5 py-5 flex flex-col gap-4 pb-10">
          <div>
            <label className="block text-xs font-semibold text-gray-200 mb-1.5">Name *</label>
            <input
              type="text"
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="e.g. Becoming Wiser, How to Get Stronger..."
              maxLength={100}
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-200 mb-1.5">Description</label>
            <textarea
              value={description}
              onChange={e => setDescription(e.target.value)}
              placeholder="What is this community about?"
              rows={3}
              maxLength={500}
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 resize-none transition-colors"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-200 mb-1.5">Cover Book <span className="text-gray-400 font-normal">(optional)</span></label>
            {coverBook ? (
              <div className="flex items-center gap-3 bg-gray-900 rounded-xl p-3 border border-gray-800">
                <div className="flex-shrink-0 w-8 h-12 rounded-lg overflow-hidden shadow-sm">
                  {coverBook.cover
                    ? <img src={coverBook.cover} alt={coverBook.title} className="w-full h-full object-cover" />
                    : <div className={`${coverBook.bg} w-full h-full`} />
                  }
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-semibold text-gray-100 line-clamp-1">{coverBook.title}</p>
                  <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{coverBook.author}</p>
                </div>
                <button onClick={() => { setCoverBook(null); setCoverQuery(''); }} className="text-gray-400 hover:text-gray-300 flex-shrink-0 transition-colors"><CloseIcon /></button>
              </div>
            ) : (
              <div>
                <input
                  type="text"
                  value={coverQuery}
                  onChange={e => setCoverQuery(e.target.value)}
                  placeholder="Search for a book..."
                  className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
                />
                {coverSearching && <p className="text-xs text-gray-400 mt-2 text-center">Searching...</p>}
                {coverResults.length > 0 && (
                  <div className="mt-2 border border-gray-800 rounded-xl overflow-hidden bg-gray-800 shadow-md">
                    {coverResults.map(book => (
                      <button
                        key={book.id}
                        onClick={() => { setCoverBook(book); setCoverQuery(''); setCoverResults([]); }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-900 transition-colors text-left border-b border-gray-700 last:border-b-0"
                      >
                        <div className="flex-shrink-0 w-8 h-12 rounded overflow-hidden">
                          {book.cover
                            ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
                            : <div className={`${book.bg} w-full h-full`} />
                          }
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-semibold text-gray-100 line-clamp-1">{book.title}</p>
                          <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{book.author}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          <button
            onClick={handleCreate}
            disabled={loading}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Creating...' : 'Create Community'}
          </button>
        </div>
      </div>
    </>
  );
}

function BookPickerModal({ title, onClose, onSelect, confirmLabel, suggestedBooks }) {
  const [bookQuery,    setBookQuery]    = useState('');
  const [bookResults,  setBookResults]  = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [loading,      setLoading]      = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!bookQuery.trim()) { setBookResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await fetchBooks(bookQuery, 6);
        setBookResults(results);
      } catch {
        setBookResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 500);
    return () => clearTimeout(timer.current);
  }, [bookQuery]);

  const clearBook = () => { setSelectedBook(null); setBookQuery(''); setBookResults([]); };

  const handleConfirm = async () => {
    if (!selectedBook) return;
    setLoading(true);
    try {
      await onSelect(selectedBook);
      onClose();
    } catch {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40" onClick={onClose} />
      <div className="fixed bottom-0 left-0 right-0 max-w-lg mx-auto bg-[#161616] rounded-t-2xl z-50 shadow-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 pt-5 pb-4 border-b border-gray-800 sticky top-0 bg-[#161616]">
          <h2 className="text-base font-bold text-white">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors">
            <CloseIcon />
          </button>
        </div>
        <div className="px-5 py-5 flex flex-col gap-4 pb-10">
          {selectedBook ? (
            <div className="flex items-center gap-3 bg-gray-900 rounded-xl p-3 border border-gray-800">
              <div className="flex-shrink-0 w-10 h-14 rounded-lg overflow-hidden shadow-sm">
                {selectedBook.cover
                  ? <img src={selectedBook.cover} alt={selectedBook.title} className="w-full h-full object-cover" />
                  : <div className={`${selectedBook.bg} w-full h-full`} />
                }
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-gray-100 line-clamp-1">{selectedBook.title}</p>
                <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{selectedBook.author}</p>
              </div>
              <button onClick={clearBook} className="text-gray-400 hover:text-gray-300 flex-shrink-0 transition-colors">
                <CloseIcon />
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              {suggestedBooks?.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 mb-2">From your community</p>
                  <div className="flex gap-2.5 overflow-x-auto pb-1 no-scrollbar">
                    {suggestedBooks.map(book => (
                      <button
                        key={book.id}
                        onClick={() => setSelectedBook(book)}
                        className="flex-shrink-0 w-14 text-left"
                      >
                        <div className="w-14 h-20 rounded-lg overflow-hidden shadow-sm mb-1">
                          {book.cover
                            ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
                            : <div className={`${book.bg} w-full h-full flex items-end p-1`}>
                                <p className="text-white text-[8px] font-semibold line-clamp-2 leading-tight drop-shadow">{book.title}</p>
                              </div>
                          }
                        </div>
                        <p className="text-[10px] font-semibold text-gray-200 line-clamp-1">{book.title}</p>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              <div>
              <input
                type="text"
                value={bookQuery}
                onChange={e => setBookQuery(e.target.value)}
                placeholder="Search for a book..."
                className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
              />
              {searchLoading && <p className="text-xs text-gray-400 mt-2 text-center">Searching...</p>}
              {bookResults.length > 0 && (
                <div className="mt-2 border border-gray-800 rounded-xl overflow-hidden bg-gray-800 shadow-md">
                  {bookResults.map(book => (
                    <button
                      key={book.id}
                      onClick={() => { setSelectedBook(book); setBookQuery(''); setBookResults([]); }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-900 transition-colors text-left border-b border-gray-700 last:border-b-0"
                    >
                      <div className="flex-shrink-0 w-8 h-12 rounded overflow-hidden">
                        {book.cover
                          ? <img src={book.cover} alt={book.title} className="w-full h-full object-cover" />
                          : <div className={`${book.bg} w-full h-full`} />
                        }
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-gray-100 line-clamp-1">{book.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{book.author}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}
          <button
            onClick={handleConfirm}
            disabled={!selectedBook || loading}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 transition-colors"
          >
            {loading ? 'Saving...' : confirmLabel}
          </button>
        </div>
      </div>
    </>
  );
}

function CommunityDetailScreen({ community: initialComm, isMember, onBack, onJoin, joinLoading, onBookSelect, userId, onUserSelect }) {
  const [community,        setCommunity]        = useState(initialComm);
  const isCreator = userId && community.creator_id === userId;

  const [commBooks,        setCommBooks]        = useState([]);
  const [loadingBooks,     setLoadingBooks]     = useState(true);
  const [showDiscussion,   setShowDiscussion]   = useState(false);
  const [discussions,      setDiscussions]      = useState([]);
  const [loadingDisc,      setLoadingDisc]      = useState(false);
  const [revealedSpoilers, setRevealedSpoilers] = useState(new Set());
  const [newMsg,           setNewMsg]           = useState('');
  const [newMsgSpoiler,    setNewMsgSpoiler]    = useState(false);
  const [sendingMsg,       setSendingMsg]       = useState(false);
  const [showAddBook,      setShowAddBook]      = useState(false);
  const [showBotmPicker,   setShowBotmPicker]   = useState(false);
  const [showCoverPicker,  setShowCoverPicker]  = useState(false);
  const [badgeToast,       setBadgeToast]       = useState(null);
  const [msgError,         setMsgError]         = useState(null);
  const [removingBookId,   setRemovingBookId]   = useState(null);
  const [loadingBookId,    setLoadingBookId]    = useState(null);
  const [editingBooks,     setEditingBooks]     = useState(false);
  const [loadingBotm,      setLoadingBotm]      = useState(false);
  const [editingDesc,      setEditingDesc]      = useState(false);
  const [draftDesc,        setDraftDesc]        = useState(community.description || '');
  const [savingDesc,       setSavingDesc]       = useState(false);
  const [creatorProfile,   setCreatorProfile]   = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deletingComm,      setDeletingComm]      = useState(false);

  useEffect(() => {
    if (community.creator_id) {
      supabase.from('profiles').select('id, username, display_name, avatar_bg, avatar_url').eq('id', community.creator_id).maybeSingle()
        .then(({ data }) => { if (data) setCreatorProfile(data); });
    }
  }, [community.creator_id]);

  useEffect(() => {
    supabase.from('community_books').select('*').eq('community_id', community.id).order('added_at')
      .then(({ data }) => { setCommBooks(data || []); setLoadingBooks(false); });
  }, [community.id]);

  useEffect(() => {
    if (!showDiscussion) return;
    setLoadingDisc(true);
    supabase
      .from('club_discussions')
      .select('*, profiles(id, username, display_name, avatar_bg, avatar_url)')
      .eq('club_id', community.id)
      .order('created_at', { ascending: true })
      .then(async ({ data: rows }) => {
        if (!rows || rows.length === 0) { setDiscussions([]); setLoadingDisc(false); return; }
        const ids = rows.map(m => m.id);
        const [{ data: likesData }, { data: userLikesData }] = await Promise.all([
          supabase.from('discussion_likes').select('discussion_id').in('discussion_id', ids),
          userId
            ? supabase.from('discussion_likes').select('discussion_id').eq('user_id', userId).in('discussion_id', ids)
            : Promise.resolve({ data: [] }),
        ]);
        const countMap = {};
        (likesData || []).forEach(l => { countMap[l.discussion_id] = (countMap[l.discussion_id] || 0) + 1; });
        const userLikedSet = new Set((userLikesData || []).map(l => l.discussion_id));
        setDiscussions(rows.map(m => ({ ...m, like_count: countMap[m.id] || 0, liked_by_user: userLikedSet.has(m.id) })));
        setLoadingDisc(false);
      });
  }, [showDiscussion, community.id, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleAddBook = async (book) => {
    if (!userId) throw new Error('Not authenticated');
    if (commBooks.length >= 20) throw new Error('Community book limit reached');
    const { data, error } = await supabase.from('community_books').insert({
      community_id: community.id,
      book_id:      book.id,
      book_title:   book.title,
      book_author:  book.author || null,
      book_cover:   book.cover  || null,
      added_by:     userId,
    }).select().single();
    if (error) throw error;
    setCommBooks(prev => [...prev, data]);
  };

  const handleRemoveBook = async (commBookId) => {
    setRemovingBookId(commBookId);
    try {
      const { error } = await supabase.from('community_books').delete().eq('id', commBookId);
      if (error) throw error;
      setCommBooks(prev => prev.filter(b => b.id !== commBookId));
    } finally {
      setRemovingBookId(null);
    }
  };

  const handleSetBotm = async (book) => {
    const { error } = await supabase.from('book_clubs').update({
      current_book_title:  book.title,
      current_book_author: book.author || null,
      current_book_cover:  book.cover  || null,
      current_book_id:     book.id,
    }).eq('id', community.id);
    if (error) throw error;
    setCommunity(prev => ({ ...prev, current_book_title: book.title, current_book_author: book.author || null, current_book_cover: book.cover || null, current_book_id: book.id }));
  };

  const handleRemoveBotm = async () => {
    await supabase.from('book_clubs').update({ current_book_title: null, current_book_author: null, current_book_cover: null, current_book_id: null }).eq('id', community.id);
    setCommunity(prev => ({ ...prev, current_book_title: null, current_book_author: null, current_book_cover: null, current_book_id: null }));
  };

  const handleSetCover = async (book) => {
    const cover_book_data = { title: book.title, author: book.author, cover: book.cover };
    const { error } = await supabase.from('book_clubs').update({ cover_book_id: book.id, cover_book_data }).eq('id', community.id);
    if (error) throw error;
    setCommunity(prev => ({ ...prev, cover_book_id: book.id, cover_book_data }));
    if (!commBooks.some(b => b.book_id === book.id)) {
      const { data: newEntry } = await supabase.from('community_books').insert({
        community_id: community.id,
        book_id:      book.id,
        book_title:   book.title,
        book_author:  book.author || null,
        book_cover:   book.cover  || null,
        added_by:     userId,
      }).select().single();
      if (newEntry) setCommBooks(prev => [...prev, newEntry]);
    }
  };

  const handleRemoveCover = async () => {
    await supabase.from('book_clubs').update({ cover_book_id: null, cover_book_data: null }).eq('id', community.id);
    setCommunity(prev => ({ ...prev, cover_book_id: null, cover_book_data: null }));
  };

  const handleCoverClick = async () => {
    if (!community.cover_book_id) return;
    try {
      const full = await fetchBookById(community.cover_book_id);
      onBookSelect(full);
    } catch {
      if (community.cover_book_data) {
        onBookSelect({ id: community.cover_book_id, title: community.cover_book_data.title, author: community.cover_book_data.author || 'Unknown', cover: community.cover_book_data.cover || null, bg: getBgColor(community.cover_book_id), description: '', year: '' });
      }
    }
  };

  const handleDeleteMessage = async (msgId) => {
    await supabase.from('club_discussions').delete().eq('id', msgId).eq('user_id', userId);
    setDiscussions(prev => prev.filter(m => m.id !== msgId));
  };

  const toggleDiscussionLike = async (msgId) => {
    if (!userId) return;
    const msg = discussions.find(m => m.id === msgId);
    if (!msg) return;
    const wasLiked = msg.liked_by_user;
    // Optimistic update
    setDiscussions(prev => prev.map(m =>
      m.id === msgId
        ? { ...m, liked_by_user: !wasLiked, like_count: m.like_count + (wasLiked ? -1 : 1) }
        : m
    ));
    const { error } = wasLiked
      ? await supabase.from('discussion_likes').delete().eq('discussion_id', msgId).eq('user_id', userId)
      : await supabase.from('discussion_likes').insert({ discussion_id: msgId, user_id: userId });
    if (error) {
      setDiscussions(prev => prev.map(m =>
        m.id === msgId
          ? { ...m, liked_by_user: wasLiked, like_count: m.like_count + (wasLiked ? 1 : -1) }
          : m
      ));
    }
  };

  const handleSendMessage = async () => {
    if (!newMsg.trim() || !userId) return;
    const modResult = moderateText(newMsg.trim());
    if (modResult.blocked) {
      setMsgError(modResult.reason);
      setTimeout(() => setMsgError(null), 3000);
      return;
    }
    setSendingMsg(true);
    try {
      const { data, error } = await supabase.from('club_discussions').insert({
        club_id:    community.id,
        user_id:    userId,
        message:    modResult.text,
        is_spoiler: newMsgSpoiler,
      }).select('*, profiles(id, username, display_name, avatar_bg, avatar_url)').single();
      if (error) throw error;
      setDiscussions(prev => [...prev, data]);
      setNewMsg('');
      setNewMsgSpoiler(false);
      const { count } = await supabase.from('club_discussions').select('*', { count: 'exact', head: true }).eq('user_id', userId);
      if (count >= 10) {
        const isNew = await unlockBadge('social_reader', userId).catch(() => false);
        if (isNew) { setBadgeToast('Social Reader'); setTimeout(() => setBadgeToast(null), 3000); }
      }
    } catch {
      setMsgError('Failed to send. Please try again.');
      setTimeout(() => setMsgError(null), 3000);
    } finally {
      setSendingMsg(false);
    }
  };

  if (showDiscussion) {
    return (
      <div className="fixed inset-0 bg-[#0f0f10] z-[60] flex flex-col">
        {badgeToast && (
          <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-[70] pointer-events-none">
            <div className="bg-amber-500 text-white text-sm font-bold text-center py-3 px-5 rounded-2xl shadow-xl">Badge unlocked: {badgeToast}</div>
          </div>
        )}
        <div className="flex items-center justify-between px-5 pt-10 pb-4 border-b border-gray-800 flex-shrink-0">
          <button onClick={() => setShowDiscussion(false)} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
            <BackIcon />
            Back
          </button>
          <div className="text-center flex-1 px-2">
            <p className="text-sm font-bold text-white line-clamp-1">{community.current_book_title}</p>
            {community.current_book_author && <p className="text-xs text-gray-400">{community.current_book_author}</p>}
          </div>
          <div className="w-12" />
        </div>
        <div className="overflow-y-auto flex-1 px-5 pt-4 pb-4">
          {loadingDisc ? (
            <p className="text-sm text-gray-400 text-center py-10">Loading...</p>
          ) : discussions.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-10">No comments yet. Be the first!</p>
          ) : (
            <div className="flex flex-col gap-3">
              {discussions.map(msg => {
                const revealed = revealedSpoilers.has(msg.id);
                return (
                  <div key={msg.id} className="flex gap-2.5">
                    <Avatar
                      profile={msg.user_id === userId ? { ...msg.profiles, display_name: 'You' } : msg.profiles}
                      size="sm"
                    />
                    <div className="flex-1 min-w-0 bg-gray-900 rounded-xl px-3 py-2.5">
                      <div className="flex items-center justify-between mb-1">
                        {msg.user_id !== userId && msg.profiles && onUserSelect ? (
                          <button className="text-[10px] font-semibold text-gray-400 hover:text-gray-200 transition-colors" onClick={() => onUserSelect(msg.profiles)}>
                            {msg.profiles?.display_name || msg.profiles?.username || 'User'}
                          </button>
                        ) : (
                          <p className="text-[10px] font-semibold text-gray-400">{msg.user_id === userId ? 'You' : (msg.profiles?.display_name || msg.profiles?.username || 'User')}</p>
                        )}
                        <div className="flex items-center gap-2">
                          <LikeButton
                            count={msg.like_count || 0}
                            liked={msg.liked_by_user || false}
                            onToggle={() => toggleDiscussionLike(msg.id)}
                            size="sm"
                          />
                          {msg.user_id === userId && (
                            <button onClick={() => handleDeleteMessage(msg.id)} className="text-gray-300 hover:text-red-400 transition-colors text-xs leading-none">✕</button>
                          )}
                        </div>
                      </div>
                      {msg.is_spoiler && !revealed ? (
                        <button onClick={() => setRevealedSpoilers(prev => new Set([...prev, msg.id]))} className="flex items-center gap-1.5 text-xs text-amber-700 font-medium">
                          <span>⚠</span><span>Spoiler, tap to reveal</span>
                        </button>
                      ) : (
                        <>
                          {msg.is_spoiler && <span className="inline-block text-[10px] text-amber-400 font-semibold bg-amber-900/30 px-1.5 py-0.5 rounded-full mr-1.5 mb-1">spoiler</span>}
                          <p className="text-sm text-gray-100">{msg.message}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        {isMember ? (
          <div className="px-5 py-4 border-t border-gray-800 flex-shrink-0 bg-[#0f0f10]">
            <div className="flex gap-2 items-end">
              <textarea
                value={newMsg}
                onChange={e => setNewMsg(e.target.value)}
                placeholder="Add a comment..."
                rows={2}
                maxLength={1000}
                className="flex-1 rounded-xl border border-gray-700 bg-gray-900 px-3 py-2.5 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 resize-none transition-colors"
              />
              <button
                onClick={handleSendMessage}
                disabled={sendingMsg || !newMsg.trim()}
                className="flex-shrink-0 bg-white text-gray-900 text-xs font-semibold px-4 py-2.5 rounded-xl hover:bg-gray-100 disabled:opacity-40 transition-colors"
              >
                {sendingMsg ? '...' : 'Send'}
              </button>
            </div>
            <div className="flex items-center justify-between mt-2">
              <label className="flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer">
                <input type="checkbox" checked={newMsgSpoiler} onChange={e => setNewMsgSpoiler(e.target.checked)} className="accent-amber-500" />
                Spoiler
              </label>
              {msgError && <p className="text-xs text-red-500">{msgError}</p>}
            </div>
          </div>
        ) : (
          <div className="px-5 py-4 border-t border-gray-800 flex-shrink-0 text-center">
            <p className="text-sm text-gray-400">Join this community to leave comments</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="pb-10">
      {showAddBook && (
        <BookPickerModal title="Add Book to Community" confirmLabel="Add to Community" onClose={() => setShowAddBook(false)} onSelect={handleAddBook} />
      )}
      {showBotmPicker && (
        <BookPickerModal title="Set Book of the Month" confirmLabel="Set as Book of the Month" onClose={() => setShowBotmPicker(false)} onSelect={handleSetBotm} suggestedBooks={commBooks.length > 0 ? commBooks.map(b => ({ id: b.book_id, title: b.book_title, author: b.book_author || '', cover: b.book_cover || null, bg: getBgColor(b.book_id) })) : undefined} />
      )}
      {showCoverPicker && (
        <BookPickerModal
          title="Set Cover Book"
          confirmLabel="Set as Cover"
          onClose={() => setShowCoverPicker(false)}
          onSelect={handleSetCover}
          suggestedBooks={commBooks.length > 0 ? commBooks.map(b => ({ id: b.book_id, title: b.book_title, author: b.book_author || '', cover: b.book_cover || null, bg: getBgColor(b.book_id) })) : undefined}
        />
      )}
      {badgeToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-amber-500 text-white text-sm font-bold text-center py-3 px-5 rounded-2xl shadow-xl">Badge unlocked: {badgeToast}</div>
        </div>
      )}

      <div className="flex items-center px-5 pt-10 pb-4">
        <button onClick={onBack} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
          <BackIcon />
          Communities
        </button>
      </div>

      <div className="px-5 mb-5">
        <div className="flex gap-4 items-start">
          {community.cover_book_data?.cover ? (
            <button onClick={handleCoverClick} className="flex-shrink-0 w-20 h-28 rounded-2xl overflow-hidden shadow-md">
              <img src={community.cover_book_data.cover} alt={community.cover_book_data.title} className="w-full h-full object-cover" />
            </button>
          ) : (
            <div className={`flex-shrink-0 w-14 h-14 rounded-2xl ${getBgColor(community.id)} flex items-center justify-center shadow-sm relative overflow-hidden`}>
              <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-black/20" />
              <span className="text-white text-2xl font-black relative">{community.name[0]}</span>
            </div>
          )}
          <div className="flex-1 min-w-0 pt-1">
            <h1 className="text-xl font-bold text-white leading-tight">{community.name}</h1>
            {editingDesc ? (
              <div className="mt-2">
                <textarea
                  value={draftDesc}
                  onChange={e => setDraftDesc(e.target.value)}
                  rows={3}
                  maxLength={300}
                  className="w-full text-sm rounded-xl border border-gray-700 bg-gray-900 px-3 py-2 resize-none outline-none focus:border-gray-400"
                  placeholder="Community description..."
                  autoFocus
                />
                <div className="flex items-center gap-2 mt-1">
                  <button
                    disabled={savingDesc}
                    onClick={async () => {
                      if (draftDesc.trim()) {
                        const modResult = moderateText(draftDesc.trim());
                        if (modResult.blocked) { alert(modResult.reason); return; }
                        setDraftDesc(modResult.text);
                      }
                      setSavingDesc(true);
                      const finalDesc = draftDesc.trim() ? moderateText(draftDesc.trim()).text : null;
                      const { error } = await supabase.from('book_clubs').update({ description: finalDesc }).eq('id', community.id);
                      if (!error) setCommunity(prev => ({ ...prev, description: finalDesc }));
                      setSavingDesc(false);
                      setEditingDesc(false);
                    }}
                    className="text-xs font-semibold text-white hover:text-gray-300 transition-colors disabled:opacity-50"
                  >
                    {savingDesc ? 'Saving...' : 'Save'}
                  </button>
                  <button onClick={() => { setDraftDesc(community.description || ''); setEditingDesc(false); }} className="text-xs font-semibold text-gray-400 hover:text-gray-300 transition-colors">
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="mt-1">
                {community.description
                  ? <p className="text-sm text-gray-400 leading-relaxed">{community.description}</p>
                  : isCreator && <p className="text-sm text-gray-300 italic">No description</p>
                }
                {isCreator && (
                  <button onClick={() => setEditingDesc(true)} className="text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors mt-1">
                    {community.description ? 'Edit description' : 'Add description'}
                  </button>
                )}
              </div>
            )}
            <div className="flex items-center gap-1.5 mt-2 text-gray-400">
              <UsersIcon />
              <span className="text-sm">{community.member_count} {community.member_count === 1 ? 'member' : 'members'}</span>
            </div>
            {creatorProfile && (
              <button
                className="flex items-center gap-2 mt-1 text-left"
                onClick={() => community.creator_id !== userId && onUserSelect?.(creatorProfile)}
                disabled={community.creator_id === userId || !onUserSelect}
              >
                <Avatar profile={creatorProfile} size="sm" />
                <div>
                  <p className="text-xs font-semibold text-gray-300">{creatorProfile.display_name || creatorProfile.username}</p>
                  {creatorProfile.username && <p className="text-xs text-gray-400">@{creatorProfile.username}</p>}
                </div>
              </button>
            )}
            {isCreator && (
              <div className="flex items-center gap-3 mt-2">
                <button onClick={() => setShowCoverPicker(true)} className="text-xs font-semibold text-gray-400 hover:text-gray-200 transition-colors">
                  {community.cover_book_id ? 'Change Cover' : 'Set Cover'}
                </button>
                {community.cover_book_id && (
                  <button onClick={handleRemoveCover} className="text-xs font-semibold text-red-400 hover:text-red-600 transition-colors">Remove</button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {!isMember && (
        <div className="px-5 mb-6">
          <button onClick={onJoin} disabled={joinLoading} className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 disabled:opacity-50 transition-colors">
            {joinLoading ? 'Joining...' : 'Join Community'}
          </button>
        </div>
      )}

      <div className="mb-6">
        <div className="flex items-center justify-between mb-3 px-5">
          <h2 className="text-base font-bold text-white">Books</h2>
          {isCreator && (
            <div className="flex items-center gap-2">
              {commBooks.length > 0 && (
                <button onClick={() => setEditingBooks(v => !v)} className="text-xs font-semibold text-gray-400 hover:text-white transition-colors">
                  {editingBooks ? 'Done' : 'Edit'}
                </button>
              )}
              {commBooks.length < 20 && (
                <button onClick={() => setShowAddBook(true)} className="flex items-center gap-1 bg-gray-800 hover:bg-gray-600 text-gray-200 text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors">
                  <PlusIcon />
                  Add Book
                </button>
              )}
            </div>
          )}
        </div>
        {loadingBooks ? (
          <p className="text-xs text-gray-400 text-center py-4">Loading...</p>
        ) : commBooks.length === 0 && !community.current_book_title ? (
          <p className="text-sm text-gray-400 text-center py-4 px-5">{isCreator ? 'Add books to build your community collection.' : 'No books added yet.'}</p>
        ) : (
          <div className="flex gap-4 overflow-x-auto px-5 pb-2 no-scrollbar">
            {community.current_book_title && !commBooks.some(b => b.book_id === community.current_book_id) && (
              <div className="flex-shrink-0 w-24 relative">
                <button
                  className="w-full text-left"
                  disabled={loadingBotm}
                  onClick={async () => {
                    if (!community.current_book_id) return;
                    setLoadingBotm(true);
                    try {
                      const full = await fetchBookById(community.current_book_id);
                      onBookSelect(full);
                    } catch {
                      onBookSelect({ id: community.current_book_id, title: community.current_book_title, author: community.current_book_author || 'Unknown', year: '', cover: community.current_book_cover || null, bg: getBgColor(community.current_book_id), description: '' });
                    } finally {
                      setLoadingBotm(false);
                    }
                  }}
                >
                  <div className="w-24 h-36 rounded-xl overflow-hidden mb-1.5 shadow-sm relative">
                    {community.current_book_cover
                      ? <img src={community.current_book_cover} alt={community.current_book_title} className="w-full h-full object-cover" />
                      : <div className={`${getBgColor(community.current_book_id)} w-full h-full flex items-end p-2`}>
                          <p className="text-white text-[10px] font-semibold line-clamp-2 leading-tight drop-shadow">{community.current_book_title}</p>
                        </div>
                    }
                    <span className="absolute top-1.5 left-1.5 text-[9px] font-bold bg-amber-400 text-white px-1.5 py-0.5 rounded-full leading-tight">BOTM</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-100 line-clamp-1">{community.current_book_title}</p>
                  {community.current_book_author && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{community.current_book_author}</p>}
                </button>
              </div>
            )}
            {commBooks.map(b => (
              <div key={b.id} className="flex-shrink-0 w-24 relative">
                <button
                  className="w-full text-left"
                  disabled={loadingBookId === b.id}
                  onClick={async () => {
                    setLoadingBookId(b.id);
                    try {
                      const fullBook = await fetchBookById(b.book_id);
                      onBookSelect(fullBook);
                    } catch {
                      onBookSelect({ id: b.book_id, title: b.book_title, author: b.book_author || 'Unknown', year: '', cover: b.book_cover || null, bg: getBgColor(b.book_id), description: '' });
                    } finally {
                      setLoadingBookId(null);
                    }
                  }}
                >
                  <div className="w-24 h-36 rounded-xl overflow-hidden mb-1.5 shadow-sm relative">
                    {b.book_cover
                      ? <img src={b.book_cover} alt={b.book_title} className="w-full h-full object-cover" />
                      : <div className={`${getBgColor(b.id)} w-full h-full flex items-end p-2`}>
                          <p className="text-white text-[10px] font-semibold line-clamp-2 leading-tight drop-shadow">{b.book_title}</p>
                        </div>
                    }
                    {loadingBookId === b.id && (
                      <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-xl">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs font-semibold text-gray-100 line-clamp-1">{b.book_title}</p>
                  {b.book_author && <p className="text-xs text-gray-400 mt-0.5 line-clamp-1">{b.book_author}</p>}
                </button>
                {isCreator && editingBooks && (
                  <button
                    onClick={() => handleRemoveBook(b.id)}
                    disabled={removingBookId === b.id}
                    className="absolute top-1 right-1 w-5 h-5 bg-black/60 hover:bg-black/80 text-white rounded-full flex items-center justify-center transition-colors disabled:opacity-50"
                    aria-label="Remove book"
                  >
                    <span className="text-[10px] leading-none font-bold">✕</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="px-5 mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base font-bold text-white">Book of the Month</h2>
          {isCreator && (
            <div className="flex items-center gap-2">
              <button onClick={() => setShowBotmPicker(true)} className="text-xs font-semibold text-gray-400 bg-gray-800 hover:bg-gray-600 px-3 py-1.5 rounded-lg transition-colors">
                {community.current_book_id ? 'Change' : 'Set'}
              </button>
              {community.current_book_id && (
                <button onClick={handleRemoveBotm} className="text-xs font-semibold text-red-400 hover:text-red-600 transition-colors">Remove</button>
              )}
            </div>
          )}
        </div>
        {community.current_book_title ? (
          <div className="w-full flex items-center gap-4 bg-gray-900 rounded-2xl p-4">
            <button
              className="flex-shrink-0 w-16 h-24 rounded-xl overflow-hidden shadow-md relative"
              disabled={loadingBotm}
              onClick={async () => {
                if (!community.current_book_id) return;
                setLoadingBotm(true);
                try {
                  const full = await fetchBookById(community.current_book_id);
                  onBookSelect(full);
                } catch {
                  onBookSelect({ id: community.current_book_id, title: community.current_book_title, author: community.current_book_author || 'Unknown', cover: community.current_book_cover || null, bg: getBgColor(community.current_book_id), description: '', year: '' });
                } finally {
                  setLoadingBotm(false);
                }
              }}
            >
              {community.current_book_cover
                ? <img src={community.current_book_cover} alt={community.current_book_title} className="w-full h-full object-cover" />
                : <div className={`${getBgColor(community.current_book_id || community.id)} w-full h-full flex items-end p-2`}>
                    <p className="text-white text-[10px] font-semibold line-clamp-2 leading-tight drop-shadow">{community.current_book_title}</p>
                  </div>
              }
              {loadingBotm && (
                <div className="absolute inset-0 bg-black/30 flex items-center justify-center rounded-xl">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
            </button>
            <div className="flex-1 min-w-0">
              <p className="text-xs text-gray-400 font-medium mb-0.5">This month's read</p>
              <p className="text-sm font-bold text-white line-clamp-2">{community.current_book_title}</p>
              {community.current_book_author && <p className="text-xs text-gray-400 mt-1">{community.current_book_author}</p>}
              <button onClick={() => setShowDiscussion(true)} className="mt-2.5 text-xs font-semibold text-white hover:text-gray-400 transition-colors">
                Discuss →
              </button>
            </div>
          </div>
        ) : (
          <div className="bg-gray-900 rounded-2xl p-5 text-center">
            <p className="text-sm text-gray-400">No book of the month set</p>
            {isCreator && <p className="text-xs text-gray-400 mt-1 leading-relaxed">Optional: pick a book for the community to read together this month</p>}
          </div>
        )}
      </div>

      {isCreator && (
        <div className="px-5 pt-2 pb-10">
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="w-full py-3 rounded-xl text-sm font-semibold text-red-500 border border-red-200 hover:bg-red-50 transition-colors"
          >
            Delete Community
          </button>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end justify-center">
          <div className="bg-[#161616] w-full max-w-lg rounded-t-3xl px-5 pt-6 pb-10">
            <h2 className="text-base font-bold text-white mb-1">Delete community?</h2>
            <p className="text-sm text-gray-400 mb-6">
              <span className="font-semibold text-gray-200">"{community.name}"</span> will be permanently deleted along with all its books and discussions. This cannot be undone.
            </p>
            <button
              disabled={deletingComm}
              onClick={async () => {
                setDeletingComm(true);
                await supabase.from('club_discussions').delete().eq('club_id', community.id);
                await supabase.from('community_books').delete().eq('community_id', community.id);
                await supabase.from('club_members').delete().eq('club_id', community.id);
                await supabase.from('book_clubs').delete().eq('id', community.id);
                setDeletingComm(false);
                onBack();
              }}
              className="w-full bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white rounded-xl py-3.5 text-sm font-semibold transition-colors mb-3"
            >
              {deletingComm ? 'Deleting...' : 'Yes, delete it'}
            </button>
            <button
              onClick={() => setShowDeleteConfirm(false)}
              className="w-full bg-gray-800 hover:bg-gray-600 text-gray-200 rounded-xl py-3.5 text-sm font-semibold transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CommunitiesScreen({ onBookSelect, userId, onAuthRequired, onUserSelect }) {
  const [myComms,          setMyComms]          = useState([]);
  const [featuredComms,    setFeaturedComms]    = useState([]);
  const [recommendedComms, setRecommendedComms] = useState([]);
  const [discoverComms,    setDiscoverComms]    = useState([]);
  const [loading,          setLoading]          = useState(true);
  const [fetchError,       setFetchError]       = useState(false);
  const [commSearch,       setCommSearch]       = useState('');
  const [showCreate,    setShowCreate]    = useState(false);
  const [successMsg,    setSuccessMsg]    = useState(null);
  const [errorMsg,      setErrorMsg]      = useState(null);
  const [joinedIds,     setJoinedIds]     = useState(new Set());
  const [joiningId,     setJoiningId]     = useState(null);
  const [selectedComm,  setSelectedComm]  = useState(null);

  const showSuccess = (msg) => { setSuccessMsg(msg); setTimeout(() => setSuccessMsg(null), 3000); };
  const showError   = (msg) => { setErrorMsg(msg);   setTimeout(() => setErrorMsg(null),   3000); };

  const fetchComms = async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const { data: memberRows } = await supabase.from('club_members').select('club_id').eq('user_id', userId || '');
      const myIds = new Set((memberRows || []).map(r => r.club_id));
      setJoinedIds(myIds);
      const { data: allComms } = await supabase.from('book_clubs').select('*, club_members(count)').eq('is_public', true).order('created_at', { ascending: false });
      const creatorIds = [...new Set((allComms || []).map(c => c.creator_id).filter(Boolean))];
      const { data: creatorProfiles } = creatorIds.length > 0
        ? await supabase.from('profiles').select('id, username, display_name, avatar_url, avatar_bg').in('id', creatorIds)
        : { data: [] };
      const profileMap = Object.fromEntries((creatorProfiles || []).map(p => [p.id, p]));
      const enriched = (allComms || []).map(c => ({ ...c, member_count: parseInt(c.club_members?.[0]?.count ?? 0), creator_profile: profileMap[c.creator_id] || null }));
      setMyComms(enriched.filter(c => myIds.has(c.id)));

      // Friends-in-community signal
      let friendCommMap = new Map(); // commId -> count of friends in it
      if (userId) {
        const { data: friendships } = await supabase
          .from('friendships').select('requester_id, addressee_id').eq('status', 'accepted')
          .or(`requester_id.eq.${userId},addressee_id.eq.${userId}`);
        const friendIds = (friendships || []).map(f => f.requester_id === userId ? f.addressee_id : f.requester_id);
        if (friendIds.length > 0) {
          const { data: friendMemberships } = await supabase
            .from('club_members').select('club_id').in('user_id', friendIds);
          (friendMemberships || []).forEach(m => friendCommMap.set(m.club_id, (friendCommMap.get(m.club_id) || 0) + 1));
        }
      }

      const nonJoined = enriched
        .filter(c => !myIds.has(c.id))
        .map(c => ({ ...c, _friendsIn: friendCommMap.get(c.id) || 0 }))
        .sort((a, b) => (b._friendsIn * 10 + b.member_count) - (a._friendsIn * 10 + a.member_count));

      const featured = nonJoined.filter(c => c.is_featured);
      const featuredIds = new Set(featured.map(c => c.id));
      const nonFeatured = nonJoined.filter(c => !featuredIds.has(c.id));
      setFeaturedComms(featured);

      // Top picks with at least some signal (friends or members), else first 3 popular
      const recCandidates = nonFeatured.filter(c => c._friendsIn > 0 || c.member_count > 1);
      const recommended = (recCandidates.length >= 2 ? recCandidates : nonFeatured).slice(0, 4);
      const recIds = new Set(recommended.map(c => c.id));
      setRecommendedComms(recommended);
      setDiscoverComms(nonFeatured.filter(c => !recIds.has(c.id)));
    } catch (err) {
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchComms(); }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleJoin = async (comm) => {
    if (!userId) { onAuthRequired?.(); return; }
    setJoiningId(comm.id);
    try {
      const { error } = await supabase.from('club_members').insert({ club_id: comm.id, user_id: userId });
      if (error) throw error;
      const updated = { ...comm, member_count: (comm.member_count || 0) + 1 };
      setJoinedIds(prev => new Set([...prev, comm.id]));
      setDiscoverComms(prev => prev.filter(c => c.id !== comm.id));
      setRecommendedComms(prev => prev.filter(c => c.id !== comm.id));
      setFeaturedComms(prev => prev.filter(c => c.id !== comm.id));
      setMyComms(prev => [updated, ...prev]);
      const isNewBadge = await unlockBadge('book_club_member', userId).catch(() => false);
      showSuccess(isNewBadge ? 'Joined! Badge unlocked: Community Member' : 'You joined the community!');
    } catch (err) {
      showError('Failed to join. Please try again.');
    } finally {
      setJoiningId(null);
    }
  };

  const handleCreated = () => { setShowCreate(false); fetchComms(); showSuccess('Community created!'); };

  if (selectedComm) {
    return (
      <CommunityDetailScreen
        community={selectedComm}
        isMember={joinedIds.has(selectedComm.id)}
        onBack={() => { setSelectedComm(null); fetchComms(); }}
        onJoin={() => handleJoin(selectedComm)}
        joinLoading={joiningId === selectedComm.id}
        onBookSelect={onBookSelect}
        userId={userId}
        onUserSelect={onUserSelect}
      />
    );
  }

  return (
    <div className="pt-4">
      {showCreate && <CreateCommunityModal onClose={() => setShowCreate(false)} onCreated={handleCreated} userId={userId} />}
      {successMsg && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-gray-700 text-white text-sm font-semibold text-center py-3 px-5 rounded-2xl shadow-xl">{successMsg}</div>
        </div>
      )}
      {errorMsg && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-red-500 text-white text-sm font-semibold text-center py-3 px-5 rounded-2xl shadow-xl">{errorMsg}</div>
        </div>
      )}
      <div className="px-5 mb-5">
        <input
          type="text"
          value={commSearch}
          onChange={e => setCommSearch(e.target.value)}
          placeholder="Search communities..."
          className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
        />
      </div>
      {loading ? (
        <div className="px-5 pt-4">
          <div className="h-5 bg-gray-700 rounded-full w-36 mb-3 animate-pulse" />
          <div className="flex flex-col gap-3">
            {Array.from({ length: 4 }).map((_, i) => <CommunityCardSkeleton key={i} />)}
          </div>
        </div>
      ) : fetchError ? (
        <div className="px-5 pt-10 text-center">
          <p className="text-sm text-gray-400 mb-4">Couldn't load communities. Check your connection.</p>
          <button onClick={fetchComms} className="text-sm text-white bg-gray-700 hover:bg-gray-600 rounded-xl px-5 py-2.5">Try again</button>
        </div>
      ) : commSearch.trim() ? (
        <section className="mb-10 px-5">
          {(() => {
            const norm = s => s.toLowerCase().replace(/[^a-z0-9]/g, '');
            const q = norm(commSearch.trim());
            const seen = new Set();
            const results = [...myComms, ...featuredComms, ...recommendedComms, ...discoverComms].filter(c => {
              if (seen.has(c.id)) return false;
              seen.add(c.id);
              return norm(c.name).includes(q) || norm(c.description || '').includes(q);
            });
            return results.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-10">No communities match "{commSearch.trim()}"</p>
            ) : (
              <div className="flex flex-col gap-3">
                {results.map(comm => (
                  <CommunityCard key={comm.id} community={comm} isMyComm={joinedIds.has(comm.id)} joinedIds={joinedIds} joiningId={joiningId} onViewComm={setSelectedComm} onJoin={handleJoin} friendsIn={comm._friendsIn || 0} onUserSelect={onUserSelect} />
                ))}
              </div>
            );
          })()}
        </section>
      ) : (
        <>
          <section className="mb-7 px-5">
            <h2 className="text-base font-bold text-white mb-3">My Communities</h2>
            {myComms.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">You haven't joined any communities yet</p>
            ) : (
              <div className="flex flex-col gap-3">
                {myComms.map(comm => (
                  <CommunityCard key={comm.id} community={comm} isMyComm joinedIds={joinedIds} joiningId={joiningId} onViewComm={setSelectedComm} onJoin={handleJoin} onUserSelect={onUserSelect} />
                ))}
              </div>
            )}
          </section>
          {featuredComms.length > 0 && !commSearch.trim() && (
            <section className="mb-7 px-5">
              <h2 className="text-base font-bold text-white mb-3">Featured</h2>
              <div className="flex flex-col gap-3">
                {featuredComms.map(comm => (
                  <CommunityCard key={comm.id} community={comm} isMyComm={false} joinedIds={joinedIds} joiningId={joiningId} onViewComm={setSelectedComm} onJoin={handleJoin} friendsIn={comm._friendsIn || 0} onUserSelect={onUserSelect} showFeaturedPill />
                ))}
              </div>
            </section>
          )}
          {recommendedComms.length > 0 && (
            <section className="mb-7 px-5">
              <h2 className="text-base font-bold text-white mb-3">Recommended for You</h2>
              <div className="flex flex-col gap-3">
                {recommendedComms.map(comm => (
                  <CommunityCard key={comm.id} community={comm} isMyComm={false} joinedIds={joinedIds} joiningId={joiningId} onViewComm={setSelectedComm} onJoin={handleJoin} friendsIn={comm._friendsIn || 0} onUserSelect={onUserSelect} />
                ))}
              </div>
            </section>
          )}
          <section className="mb-10 px-5">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-white">Discover</h2>
              <button onClick={() => setShowCreate(true)} className="flex items-center gap-1.5 bg-white text-gray-900 text-xs font-semibold px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                <PlusIcon />
                New Community
              </button>
            </div>
            {discoverComms.length === 0 ? (
              <div className="text-center py-6">
                <p className="text-sm text-gray-400">
                  {myComms.length > 0 || recommendedComms.length > 0
                    ? "You're all caught up, no more communities to discover"
                    : "No communities yet. Be the first to create one!"}
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">
                {discoverComms.map(comm => (
                  <CommunityCard key={comm.id} community={comm} isMyComm={false} joinedIds={joinedIds} joiningId={joiningId} onViewComm={setSelectedComm} onJoin={handleJoin} friendsIn={comm._friendsIn || 0} onUserSelect={onUserSelect} />
                ))}
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
}

// --- Auth Modal ---

function AuthModal({ mode: initialMode, onClose, prompt }) {
  const [mode,        setMode]        = useState(initialMode);
  const [email,       setEmail]       = useState('');
  const [password,    setPassword]    = useState('');
  const [displayName, setDisplayName] = useState('');
  const [username,    setUsername]    = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [confirmed,   setConfirmed]   = useState(false);
  const [resetSent,   setResetSent]   = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === 'signup') {
        if (!username.trim()) { setError('Username is required.'); setLoading(false); return; }
        if (!/^[a-zA-Z0-9_]{3,20}$/.test(username.trim())) { setError('Username must be 3–20 characters using only letters, numbers, and underscores.'); setLoading(false); return; }
        const avatarBg = BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)];
        const { error: signUpErr } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              username:     username.trim().toLowerCase(),
              display_name: displayName.trim() || username.trim(),
              avatar_bg:    avatarBg,
            },
          },
        });
        if (signUpErr) throw signUpErr;
        setConfirmed(true);
      } else if (mode === 'forgot') {
        if (!email.trim()) { setError('Please enter your email address.'); setLoading(false); return; }
        const { error: resetErr } = await supabase.auth.resetPasswordForEmail(email.trim(), {
          redirectTo: 'https://readshelf.app',
        });
        if (resetErr) throw resetErr;
        setResetSent(true);
      } else {
        const { error: signInErr } = await supabase.auth.signInWithPassword({ email, password });
        if (signInErr) throw signInErr;
        onClose();
      }
    } catch (err) {
      const msg = err.message || '';
      if (msg.includes('Database error saving new user') || msg.includes('unique') || msg.includes('duplicate')) {
        setError('That username is already taken. Please choose another.');
      } else {
        setError(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => { setMode(m => m === 'login' ? 'signup' : 'login'); setError(null); };

  if (confirmed) {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-end">
        <div className="w-full max-w-lg mx-auto bg-[#161616] rounded-t-2xl px-5 pt-6 pb-10 shadow-2xl text-center">
          <div className="text-4xl mb-4">📬</div>
          <h2 className="text-lg font-bold text-white mb-2">Check your email</h2>
          <p className="text-sm text-gray-400 mb-1">We sent a confirmation link to</p>
          <p className="text-sm font-semibold text-gray-100 mb-4">{email}</p>
          <p className="text-sm text-gray-400 mb-6">Click the link in that email to activate your account, then come back and sign in.</p>
          <button
            onClick={onClose}
            className="w-full bg-white text-gray-900 font-semibold py-3.5 rounded-xl hover:bg-gray-100 active:scale-95 transition-all"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'forgot') {
    return (
      <div className="fixed inset-0 bg-black/60 z-50 flex items-end">
        <div className="w-full max-w-lg mx-auto bg-[#161616] rounded-t-2xl px-5 pt-6 pb-10 shadow-2xl">
          <div className="flex items-center justify-between mb-6">
            <button onClick={() => { setMode('login'); setError(null); setResetSent(false); }} className="flex items-center gap-1 text-sm font-medium text-gray-400 hover:text-white transition-colors">
              <BackIcon /> Back
            </button>
            <h2 className="text-lg font-bold text-white">Reset Password</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors"><CloseIcon /></button>
          </div>
          {resetSent ? (
            <div className="text-center">
              <div className="text-4xl mb-4">📬</div>
              <p className="text-sm font-semibold text-white mb-2">Check your email</p>
              <p className="text-sm text-gray-400 mb-6">We sent a password reset link to <span className="text-gray-100">{email}</span>. Click it to choose a new password.</p>
              <button onClick={onClose} className="w-full bg-white text-gray-900 font-semibold py-3.5 rounded-xl hover:bg-gray-100 active:scale-95 transition-all">
                Got it
              </button>
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm text-gray-400">Enter your email and we'll send you a link to reset your password.</p>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSubmit()}
                placeholder="Email"
                className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
              />
              {error && <p className="text-xs text-red-500 text-center">{error}</p>}
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 active:scale-95 disabled:opacity-50 transition-all"
              >
                {loading ? 'Sending…' : 'Send Reset Link'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end">
      <div className="w-full max-w-lg mx-auto bg-[#161616] rounded-t-2xl px-5 pt-6 pb-10 shadow-2xl">
        {prompt && (
          <div className="bg-gray-900 rounded-xl px-4 py-3 mb-5 text-center">
            <p className="text-sm text-gray-300">{prompt}</p>
          </div>
        )}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-bold text-white">{mode === 'login' ? 'Sign In' : 'Create Account'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200 transition-colors"><CloseIcon /></button>
        </div>
        <div className="flex flex-col gap-4">
          {mode === 'signup' && (
            <>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="Display Name"
                maxLength={50}
                className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
              />
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                placeholder="Username (letters, numbers, _)"
                maxLength={30}
                className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
              />
            </>
          )}
          <input
            type="email"
            value={email}
            onChange={e => setEmail(e.target.value)}
            placeholder="Email"
            className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
          />
          <input
            type="password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()}
            placeholder="Password"
            className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
          />
          {error && <p className="text-xs text-red-500 text-center">{error}</p>}
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 active:scale-95 disabled:opacity-50 transition-all"
          >
            {loading
              ? (mode === 'login' ? 'Signing in…' : 'Creating account…')
              : (mode === 'login' ? 'Sign In' : 'Sign Up')}
          </button>
          {mode === 'login' && (
            <button
              onClick={() => { setMode('forgot'); setError(null); }}
              className="text-xs text-gray-500 text-center hover:text-gray-300 transition-colors -mt-1"
            >
              Forgot password?
            </button>
          )}
          <button
            onClick={switchMode}
            className="text-sm text-gray-400 text-center hover:text-white transition-colors"
          >
            {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

function SetPasswordModal({ onDone }) {
  const [password,  setPassword]  = useState('');
  const [password2, setPassword2] = useState('');
  const [loading,   setLoading]   = useState(false);
  const [error,     setError]     = useState(null);
  const [done,      setDone]      = useState(false);

  const handleSubmit = async () => {
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== password2) { setError('Passwords do not match.'); return; }
    setLoading(true);
    setError(null);
    const { error: updateErr } = await supabase.auth.updateUser({ password });
    setLoading(false);
    if (updateErr) { setError(updateErr.message); return; }
    setDone(true);
    setTimeout(onDone, 2000);
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end">
      <div className="w-full max-w-lg mx-auto bg-[#161616] rounded-t-2xl px-5 pt-6 pb-10 shadow-2xl">
        <h2 className="text-lg font-bold text-white mb-2">Set New Password</h2>
        {done ? (
          <div className="text-center py-6">
            <div className="text-4xl mb-3">✅</div>
            <p className="text-sm text-gray-300">Password updated! Taking you back…</p>
          </div>
        ) : (
          <div className="flex flex-col gap-4 mt-4">
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="New password"
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
            />
            <input
              type="password"
              value={password2}
              onChange={e => setPassword2(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()}
              placeholder="Confirm new password"
              className="w-full rounded-xl border border-gray-700 bg-gray-900 px-4 py-3 text-sm text-gray-100 placeholder-gray-400 outline-none focus:border-gray-500 focus:bg-gray-800 transition-colors"
            />
            {error && <p className="text-xs text-red-500 text-center">{error}</p>}
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="w-full bg-white text-gray-900 rounded-xl py-3.5 text-sm font-semibold hover:bg-gray-100 active:scale-95 disabled:opacity-50 transition-all"
            >
              {loading ? 'Updating…' : 'Update Password'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// --- App ---

const TABS = ['Home', 'Social', 'Communities', 'Profile'];

async function fetchOrCreateProfile(user) {
  const { data } = await supabase.from('profiles').select('*').eq('id', user.id).single();
  if (data) {
    // Sign out banned users immediately on session load
    if (data.is_banned) {
      await supabase.auth.signOut();
      return null;
    }
    return data;
  }
  // Profile row missing (trigger may not be set up) — create it from auth metadata
  const meta = user.user_metadata || {};
  const avatarBg = meta.avatar_bg || BG_COLORS[Math.floor(Math.random() * BG_COLORS.length)];
  const { data: newProfile } = await supabase.from('profiles').insert({
    id: user.id,
    username: meta.username || `user_${user.id.slice(0, 8)}`,
    display_name: meta.display_name || meta.username || 'Reader',
    avatar_bg: avatarBg,
  }).select().single();
  return newProfile ?? null;
}

export default function App() {
  const [activeTab,        setActiveTab]        = useState('Home');
  const [searchOpen,       setSearchOpen]       = useState(false);
  const [selectedBook,     setSelectedBook]     = useState(null);
  const [bookData,         setBookData]         = useState({});
  const [achievementToast,  setAchievementToast]  = useState(null);
  const [saveErrorToast,    setSaveErrorToast]    = useState(null);
  const [goalCompleteToast, setGoalCompleteToast] = useState(false);
  const [hasReadingGoal,   setHasReadingGoal]   = useState(false);
  const [readingGoalCount, setReadingGoalCount] = useState(0);
  const prevGoalProgress = useRef(null);
  const [viewedUser,       setViewedUser]       = useState(null);
  const [pendingReqCount,  setPendingReqCount]  = useState(0);

  // Auth state: undefined = loading, null = logged out, object = logged in
  const [authUser,          setAuthUser]          = useState(undefined);
  const [userProfile,       setUserProfile]       = useState(null);
  const [showAuthModal,     setShowAuthModal]     = useState(false);
  const [authMode,          setAuthMode]          = useState('login');
  const [authPrompt,        setAuthPrompt]        = useState(null);
  const [showSetPassword,   setShowSetPassword]   = useState(false);
  const pendingAction = useRef(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const user = session?.user ?? null;
      setAuthUser(user);
      if (user) {
        posthog.identify(user.id, { email: user.email });
        fetchOrCreateProfile(user).then(profile => setUserProfile(profile));
      }
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowSetPassword(true);
        return;
      }
      const user = session?.user ?? null;
      setAuthUser(user);
      if (user) {
        posthog.identify(user.id, { email: user.email });
        fetchOrCreateProfile(user).then(profile => setUserProfile(profile));
      } else {
        posthog.reset();
        setUserProfile(null);
        setActiveTab('Home');
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const userId = authUser?.id ?? null;

  // Fetch incoming friend request count for badge
  useEffect(() => {
    if (!userId) { setPendingReqCount(0); return; }
    supabase.from('friendships')
      .select('id', { count: 'exact', head: true })
      .eq('addressee_id', userId)
      .eq('status', 'pending')
      .then(({ count }) => setPendingReqCount(count || 0));
  }, [userId]);

  // Load persisted book data when user logs in
  useEffect(() => {
    if (!userId) { setBookData({}); setHasReadingGoal(false); return; }
    supabase.from('reading_goals').select('goal_count').eq('user_id', userId).eq('year', new Date().getFullYear()).maybeSingle()
      .then(({ data: goal }) => {
        setHasReadingGoal(goal?.goal_count > 0);
        setReadingGoalCount(goal?.goal_count ?? 0);
      });
    // Clean up any rows where review_text was accidentally stored as empty string
    supabase.from('reviews').update({ review_text: null }).eq('user_id', userId).eq('review_text', '');
    supabase.from('reviews').select('*').eq('user_id', userId).then(({ data }) => {
      const loaded = {};
      (data || []).forEach(r => {
        loaded[r.book_id] = {
          rating: r.rating || 0,
          loved: r.loved || false,
          booklisted: r.booklisted || false,
          review: r.review_text || '',
          hasSpoiler: r.has_spoiler || false,
          finishedThisYear: r.finished_this_year ?? true,
          _meta: { title: r.book_title, author: r.book_author || '', year: '', cover: r.book_cover || null, bg: getBgColor(r.book_id) },
        };
      });

      // Flush any action the user took before signing in
      const pending = pendingAction.current;
      if (pending) {
        pendingAction.current = null;
        const { id, merged } = pending;
        loaded[id] = { ...(loaded[id] || {}), ...merged };
        const meta = merged._meta || {};
        if (meta.title) {
          supabase.from('reviews').upsert({
            user_id: userId,
            book_id: id,
            book_title: meta.title,
            book_author: meta.author || null,
            book_cover: meta.cover || null,
            rating: merged.rating || null,
            review_text: merged.review || null,
            has_spoiler: merged.hasSpoiler,
            finished_this_year: merged.finishedThisYear,
            loved: merged.loved,
            booklisted: merged.booklisted,
          }, { onConflict: 'user_id,book_id' });
        }
      }

      setBookData(loaded);
    });
  }, [userId]);

  const showAchievementToast = (label) => {
    setAchievementToast(label);
    setTimeout(() => setAchievementToast(null), 3000);
  };

  // Called from ProfileScreen when a book is removed from the reading goal.
  // Updates bookData in the App so the change persists across tab navigation.
  const handleGoalBookRemove = (bookId) => {
    setBookData(prev => ({
      ...prev,
      [bookId]: { ...prev[bookId], finishedThisYear: false },
    }));
  };

  // Detect when the user just completed their reading goal
  useEffect(() => {
    const goalProgress = Object.values(bookData).filter(d => d.finishedThisYear === true && d.rating > 0).length;
    const year = new Date().getFullYear();
    // Key includes goal count so it resets if the user changes their goal target
    const storageKey = `goalComplete_${userId}_${year}_${readingGoalCount}`;
    if (
      readingGoalCount > 0 &&
      prevGoalProgress.current !== null &&
      prevGoalProgress.current < readingGoalCount &&
      goalProgress >= readingGoalCount &&
      !localStorage.getItem(storageKey)
    ) {
      setGoalCompleteToast(true);
      localStorage.setItem(storageKey, '1');
      setTimeout(() => setGoalCompleteToast(false), 5000);
    }
    prevGoalProgress.current = goalProgress;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookData, readingGoalCount]);

  const handleTabClick = (tab) => {
    if ((tab === 'Social' || tab === 'Profile') && !authUser) {
      setAuthMode('login');
      setShowAuthModal(true);
      return;
    }
    setActiveTab(tab);
  };

  const openAuthModal = (prompt = null) => { setAuthMode('login'); setAuthPrompt(prompt); setShowAuthModal(true); };

  const updateBookData = (id, updates) => {
    const prevEntry = bookData[id] || {};
    const merged = { rating: 0, loved: false, booklisted: false, review: '', hasSpoiler: false, finishedThisYear: true, ...prevEntry, ...updates };
    setBookData(prev => ({
      ...prev,
      [id]: merged,
    }));
    if (!userId) {
      pendingAction.current = { id, merged };
      setAuthMode('signup');
      setAuthPrompt('Create a free account to save your rating.');
      setShowAuthModal(true);
      return;
    }

    if (userId) {
      const meta = merged._meta || {};
      const hasMeaningfulData = merged.rating > 0 || merged.loved || merged.booklisted || !!merged.review?.trim();
      if (meta.title && hasMeaningfulData) {
        supabase.from('reviews').upsert({
          user_id: userId,
          book_id: id,
          book_title: meta.title,
          book_author: meta.author || null,
          book_cover: meta.cover || null,
          rating: merged.rating ? Math.round(merged.rating) : null,
          review_text: merged.review || null,
          has_spoiler: merged.hasSpoiler,
          finished_this_year: merged.finishedThisYear,
          loved: merged.loved,
          booklisted: merged.booklisted,
        }, { onConflict: 'user_id,book_id' }).then(({ error }) => {
          if (error) {
            Sentry.captureException(error);
            console.error('Failed to save book data:', error);
            setSaveErrorToast('Failed to save. Please try again.');
            setTimeout(() => setSaveErrorToast(null), 4000);
            setBookData(prev => ({ ...prev, [id]: prevEntry }));
          }
        });
      } else if (meta.title) {
        // All meaningful data cleared — delete the row so it doesn't resurface on reload
        supabase.from('reviews').delete().eq('user_id', userId).eq('book_id', id).then(({ error }) => {
          if (error) { Sentry.captureException(error); console.error('Failed to delete book data:', error); }
        });
      }
    }
    if (updates.rating > 0 && !prevEntry.rating && userId) {
      supabase.from('reading_activity').upsert(
        { user_id: userId, activity_date: new Date().toISOString().slice(0, 10) },
        { onConflict: 'user_id,activity_date' }
      );
      const newCount = Object.values(bookData).filter(d => d.rating > 0).length + 1;
      if (newCount === 1)  unlockBadge('first_book', userId).then(n => { if (n) showAchievementToast('First Book'); });
      if (newCount === 10) unlockBadge('bookworm', userId).then(n => { if (n) showAchievementToast('Bookworm'); });
    }
  };

  const handleSelectBook = (book) => {
    // If the user already has data for this title+author under a different book_id
    // (different edition from Google Books), redirect to that canonical ID so we never
    // create duplicate review rows for the same book.
    const normKey = (t, a) => `${(t||'').toLowerCase().trim()}|${(a||'').toLowerCase().trim()}`;
    const targetKey = normKey(book.title, book.author);
    const existing = Object.entries(bookData).find(
      ([existingId, d]) => existingId !== book.id && d._meta && normKey(d._meta.title, d._meta.author) === targetKey
    );
    const canonicalId = existing ? existing[0] : book.id;
    const canonicalBook = canonicalId !== book.id ? { ...book, id: canonicalId } : book;

    setSelectedBook(canonicalBook);
    setBookData(prev => ({
      ...prev,
      [canonicalBook.id]: {
        rating: 0, loved: false, booklisted: false, review: '', hasSpoiler: false, finishedThisYear: true,
        ...prev[canonicalBook.id],
        _meta: { title: canonicalBook.title, author: canonicalBook.author, year: canonicalBook.year, bg: canonicalBook.bg, cover: canonicalBook.cover || null },
      },
    }));
  };

  const handleSearchBookSelect = (book) => {
    handleSelectBook(book);
  };

  return (
    <div className="min-h-screen bg-[#0f0f10] max-w-lg mx-auto relative pb-10">
      {searchOpen && <SearchOverlay onClose={() => setSearchOpen(false)} onBookSelect={handleSearchBookSelect} />}

      {showSetPassword && (
        <SetPasswordModal onDone={() => setShowSetPassword(false)} />
      )}

      {showAuthModal && !showSetPassword && (
        <AuthModal
          mode={authMode}
          prompt={authPrompt}
          onClose={() => { setShowAuthModal(false); setAuthPrompt(null); }}
        />
      )}

      {selectedBook && (
        <BookDetailOverlay
          book={selectedBook}
          data={bookData[selectedBook.id] || {}}
          onUpdate={updates => updateBookData(selectedBook.id, updates)}
          onClose={() => setSelectedBook(null)}
          userId={userId}
          userProfile={userProfile}
          hasReadingGoal={hasReadingGoal}
          onUserSelect={setViewedUser}
        />
      )}

      {viewedUser && (
        <UserProfileScreen
          profile={viewedUser}
          onBack={() => setViewedUser(null)}
          onBookSelect={(book) => { setViewedUser(null); handleSelectBook(book); }}
          userId={userId}
          onUserSelect={setViewedUser}
        />
      )}

      {goalCompleteToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-emerald-500 text-white text-sm font-bold text-center py-3.5 px-5 rounded-2xl shadow-xl">
            🎉 Reading goal complete! You did it!
          </div>
        </div>
      )}

      {achievementToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-amber-500 text-white text-sm font-bold text-center py-3 px-5 rounded-2xl shadow-xl">
            Badge unlocked: {achievementToast}
          </div>
        </div>
      )}

      {saveErrorToast && (
        <div className="fixed top-20 left-0 right-0 max-w-lg mx-auto px-5 z-30 pointer-events-none">
          <div className="bg-red-500 text-white text-sm font-semibold text-center py-3 px-5 rounded-2xl shadow-xl">
            {saveErrorToast}
          </div>
        </div>
      )}

      <div className="flex items-center justify-between px-5 pt-10 pb-2">
        <button onClick={() => setSearchOpen(true)} className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-800 text-gray-300 transition-colors" aria-label="Search">
          <SearchIcon />
        </button>
        <h1 className="text-lg font-bold text-white tracking-tight">ReadShelf</h1>
        <div className="w-9" />
      </div>

      <div className="flex border-b border-gray-800 px-5 mt-1">
        {TABS.map(tab => (
          <button
            key={tab}
            onClick={() => handleTabClick(tab)}
            className={`flex-1 py-3 text-sm font-semibold transition-colors relative ${activeTab === tab ? 'text-white border-b-2 border-white' : 'text-gray-400 hover:text-gray-300'}`}
          >
            {tab}
            {tab === 'Social' && pendingReqCount > 0 && (
              <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-[9px] font-bold bg-red-500 text-white rounded-full align-middle">
                {pendingReqCount > 9 ? '9+' : pendingReqCount}
              </span>
            )}
          </button>
        ))}
      </div>

      {authUser === undefined ? (
        <div className="flex items-center justify-center py-32">
          <div className="w-6 h-6 rounded-full border-2 border-gray-600 border-t-gray-900 animate-spin" />
        </div>
      ) : (
        <>
          {activeTab === 'Home'        && <HomeScreen onBookSelect={handleSelectBook} bookData={bookData} userId={userId} />}
          {activeTab === 'Social'      && <FriendsScreen onBookSelect={handleSelectBook} userId={userId} onAuthRequired={openAuthModal} onUserSelect={setViewedUser} onRequestHandled={() => setPendingReqCount(prev => Math.max(0, prev - 1))} />}
          {activeTab === 'Communities' && <CommunitiesScreen onBookSelect={handleSelectBook} userId={userId} onAuthRequired={openAuthModal} onUserSelect={setViewedUser} />}
          {activeTab === 'Profile'     && <ProfileScreen bookData={bookData} onBookSelect={handleSelectBook} userId={userId} userProfile={userProfile} onProfileUpdate={updates => setUserProfile(prev => ({ ...prev, ...updates }))} onUserSelect={setViewedUser} isAdmin={userProfile?.is_admin === true} onGoalBookRemove={handleGoalBookRemove} onGoalCountChange={n => { setHasReadingGoal(n > 0); setReadingGoalCount(n); }} />}
        </>
      )}
    </div>
  );
}